/**
 * Pure edit operations on a sequence document (`ops`: every edit returns a new document) and the checks
 * an export runs first (`checkSequence`). The document model itself is in sequence.ts.
 */
import { z } from 'zod';
import { CueSchema, applyCueStyle, nudgeCue, tidyTrackStyle, type Cue, type CueStylePatch, type TrackStyle } from './subtitles.ts';
import { TICKS_PER_SECOND, ticksPerFrame, type Rational } from './time.ts';
import { tidyColor, type ColorCorrection } from './color.ts';
import { KEY_EASES, MAX_KEYS, clampReframe, clampZoom, defaultFit, type FitMode } from './framing.ts';
import {
  A2_TRACK, AudioClipSchema, MAX_DB, MAX_VOLUME_KEYS, MIN_DB, GraphicInstanceSchema, LayerClipSchema, MAX_AUDIO_TRACKS, MAX_GRAPHIC_TRACKS, MAX_LAYERS, MAX_MARKERS, MarkerSchema, PRIMARY_FORMAT, VideoClipSchema,
  allAudioTracks, allClips, angleCovers, audioTrackOf, clipDuration, gapBefore, graphicTrackCount, graphicTrackOf, layoutVideo, maxTransitionTicks, multicamDuration, resolveAt, sequenceDuration, snap,
  type AudioClip, type FormatFraming, type FormatRef, type FramingKey, type GraphicInstance, type GraphicTrack, type LayerClip, type LayerPlace, type Marker, type MotionKey, type MulticamCut,
  type MulticamGroup, type SequenceDocument, type SequenceFormat, type SequenceMarks, type TrackAudio, type Transition, type VideoClip, type VideoLayer,
  type CrossfadeAlign, type VolumeKey,
} from './sequence.ts';
import { fromTimeline, gapsOf, toTimeline, type TimelineItem } from './storyline.ts';
import { formatProblem, formatsOf, framingFor, motionFor, newFormatId } from './sequence-format.ts';
import { bestCrossfadeAlign, crossfadeAlignOf, crossfadeLimits, tailHandle, type SourceDurations } from './audio-levels.ts';
import { allTrackRefs, trackKey, type EditTargets, type TrackRef } from './three-point.ts';


/* ---------------------------------------------------------------- pure edit operations */

let seq = 0;
export function newId(prefix: string): string {
  seq = (seq + 1) % 1e6;
  return `${prefix}${Date.now().toString(36)}${seq.toString(36)}${Math.floor(Math.random() * 1296).toString(36)}`;
}

export interface SourceRange {
  assetId: string;
  versionId: string;
  label: string;
  sourceInTicks: number;
  sourceOutTicks: number;
  /** Source media duration, used to bound trims. */
  sourceDurationTicks: number;
  /**
   * The source's displayed picture size (after rotation), when known (1.0.0-rc.11): a clip whose orientation differs
   * from the sequence's is placed with Fill (framing.ts defaultFit).
   */
  width?: number | null;
  height?: number | null;
  /**
   * 1.8.0: whether the source has sound (a probe with an audio stream); left out when not known. A source known to be
   * silent placed on a video track gets no audio clip (three-point edits, addLayerClip with soundTo 'track').
   */
  hasAudio?: boolean;
}

/**
 * Where the sound of a clip placed on a video track goes (1.8.0, addLayerClip): 'track' onto an audio track, linked
 * (soundToAudioTrack); 'clip' stays in the clip (before 1.8.0, the default); 'none' muted, with no audio clip.
 */
export type LayerSoundTo = 'track' | 'clip' | 'none';

/** A clip's framing (1.0.0-rc.11): Fit or Fill and, for Fill, where the crop sits; 1.7.0 Blur (storyline only) and the zoom. */
export interface FramingPatch { fit?: FitMode; reframeX?: number; reframeY?: number; zoom?: number }

/**
 * The framing fields as they are kept: Fit and a 0 offset are left out, so documents without framing keep their bytes.
 * The pan and zoom are kept for Fill only (1.7.0: and keys and the zoom stay as they are; Blur only on the storyline,
 * a layer asked for Blur keeps its fit).
 */
function withFraming<T extends VideoClip | LayerClip>(c: T, patch: FramingPatch, video = true): T {
  const next = { ...c };
  let fit: FitMode = patch.fit ?? next.fit ?? 'fit';
  if (fit === 'blur' && !video) fit = next.fit ?? 'fit';
  const rx = clampReframe(patch.reframeX ?? next.reframeX);
  const ry = clampReframe(patch.reframeY ?? next.reframeY);
  const zoom = 'zoom' in patch ? clampZoom(patch.zoom) : next.zoom;
  delete next.fit; delete next.reframeX; delete next.reframeY;
  if (fit === 'fill') {
    next.fit = 'fill';
    if (rx) next.reframeX = Number(rx.toFixed(4));
    if (ry) next.reframeY = Number(ry.toFixed(4));
  } else if (fit === 'blur') (next as VideoClip).fit = 'blur';
  if ('zoom' in patch) {
    delete next.zoom;
    if (zoom !== undefined && zoom !== 1) next.zoom = Number(zoom.toFixed(4));
  }
  return next;
}

/* ---------------------------------------------------------------- 1.7.0: formats, framing keys, motion keys */

type PictureClip = VideoClip | LayerClip;
type FramingGroup = Pick<FormatFraming, 'fit' | 'reframeX' | 'reframeY' | 'zoom' | 'keys'>;
const FRAMING_FIELDS = ['fit', 'reframeX', 'reframeY', 'zoom', 'keys'] as const;
const round4 = (v: number) => Number(v.toFixed(4));
const isEase = (e: unknown): e is FramingKey['ease'] => (KEY_EASES as readonly unknown[]).includes(e);

/** Framing keys as they are kept: clamped, rounded, at whole ticks, in time order, one per time (the later wins), at most MAX_KEYS. */
export function tidyFramingKeys(keys: readonly FramingKey[] | undefined): FramingKey[] {
  const out: FramingKey[] = [];
  for (const k of [...(keys ?? [])].sort((a, b) => a.atTicks - b.atTicks)) {
    const key: FramingKey = { atTicks: Math.max(0, Math.round(k.atTicks)), x: round4(clampReframe(k.x)), y: round4(clampReframe(k.y)), zoom: round4(clampZoom(k.zoom)), ease: isEase(k.ease) ? k.ease : 'smooth' };
    if (out.at(-1)?.atTicks === key.atTicks) out[out.length - 1] = key; else out.push(key);
  }
  return out.slice(0, MAX_KEYS);
}

/**
 * Motion keys as they are kept: values clamped and rounded, keys without any value dropped, in time order, keys at
 * one time merged (the later key's values win), at most MAX_KEYS. `positionOnly` drops opacity (a format's own motion).
 */
export function tidyMotionKeys(keys: readonly MotionKey[] | undefined, positionOnly = false): MotionKey[] {
  const out: MotionKey[] = [];
  const clampN = (v: number | undefined, lo: number, hi: number) => (v === undefined || !Number.isFinite(v) ? undefined : round4(Math.min(hi, Math.max(lo, v))));
  for (const k of [...(keys ?? [])].sort((a, b) => a.atTicks - b.atTicks)) {
    const key: MotionKey = { atTicks: Math.max(0, Math.round(k.atTicks)), ease: isEase(k.ease) ? k.ease : 'smooth' };
    const x = clampN(k.x, -1, 1), y = clampN(k.y, -1, 1), scale = clampN(k.scale, 0.05, 4), opacity = positionOnly ? undefined : clampN(k.opacity, 0, 1);
    if (x !== undefined) key.x = x;
    if (y !== undefined) key.y = y;
    if (scale !== undefined) key.scale = scale;
    if (opacity !== undefined) key.opacity = opacity;
    if (key.x === undefined && key.y === undefined && key.scale === undefined && key.opacity === undefined) continue;
    const last = out.at(-1);
    if (last?.atTicks === key.atTicks) out[out.length - 1] = { ...last, ...key }; else out.push(key);
  }
  return out.slice(0, MAX_KEYS);
}

/** A framing group as kept: `fit` written when `explicitFit` (a format's own framing) or not Fit; 0, zoom 1 and no keys left out. */
function tidyFramingGroup(g: FramingGroup, explicitFit: boolean): FramingGroup {
  const out: FramingGroup = {};
  const fit = g.fit ?? 'fit';
  if (explicitFit || fit !== 'fit') out.fit = fit;
  const rx = round4(clampReframe(g.reframeX));
  const ry = round4(clampReframe(g.reframeY));
  const zoom = round4(clampZoom(g.zoom));
  if (rx) out.reframeX = rx;
  if (ry) out.reframeY = ry;
  if (zoom !== 1) out.zoom = zoom;
  const keys = tidyFramingKeys(g.keys);
  if (keys.length) out.keys = keys;
  return out;
}

/** Replace a clip's own (primary) framing fields. */
function setPrimaryFraming<T extends PictureClip>(c: T, g: FramingGroup): T {
  const next = { ...c };
  for (const k of FRAMING_FIELDS) delete next[k];
  return Object.assign(next, tidyFramingGroup(g, false));
}

/** Set (or, with null, remove) a clip's formatFraming entry; an empty entry and an empty map are left out. */
function withFormatEntry<T extends PictureClip>(c: T, formatId: string, entry: FormatFraming | null): T {
  const next = { ...c };
  const map: Record<string, FormatFraming> = { ...(c.formatFraming ?? {}) };
  if (entry && Object.keys(entry).length) map[formatId] = entry; else delete map[formatId];
  if (Object.keys(map).length) next.formatFraming = map as T['formatFraming']; else delete next.formatFraming;
  return next;
}

const isLayerClip = (doc: SequenceDocument, id: string) => (doc.layers ?? []).some((l) => l.clips.some((c) => c.id === id));

/** Apply `fn` to the storyline or layer clip `id` (doc unchanged when there is none, or when fn returns the clip itself). */
function mapPicture(doc: SequenceDocument, id: string, fn: (c: PictureClip, layer: boolean) => PictureClip): SequenceDocument {
  if (doc.video.some((c) => c.id === id)) {
    let changed = false;
    const video = doc.video.map((c) => { if (c.id !== id) return c; const n = fn(c, false) as VideoClip; changed = n !== c; return n; });
    return changed ? { ...doc, video } : doc;
  }
  if (!isLayerClip(doc, id)) return doc;
  let changed = false;
  const layers = doc.layers.map((l) => (l.clips.some((c) => c.id === id) ? { ...l, clips: l.clips.map((c) => { if (c.id !== id) return c; const n = fn(c, true) as LayerClip; changed = n !== c; return n; }) } : l));
  return changed ? { ...doc, layers } : doc;
}

const findPicture = (doc: SequenceDocument, id: string): { clip: PictureClip; layer: boolean } | null => {
  const v = doc.video.find((c) => c.id === id);
  if (v) return { clip: v, layer: false };
  for (const l of doc.layers ?? []) { const c = l.clips.find((x) => x.id === id); if (c) return { clip: c, layer: true }; }
  return null;
};

const hasFormat = (doc: SequenceDocument, formatId: FormatRef) => formatId === PRIMARY_FORMAT || (doc.formats ?? []).some((f) => f.id === formatId);

/**
 * A change to a clip's framing in one format (1.7.0, setFormatFraming). Framing fields as FramingPatch plus `keys`;
 * layers only: `place` (static scale, x, y) and `motion` (the format's position and size keys; for 'primary' the
 * clip's motion keys, opacity included). `reset`: back to the same as the primary for those groups (formats only).
 */
export interface FormatFramingPatch extends FramingPatch {
  keys?: FramingKey[];
  place?: LayerPlace;
  motion?: MotionKey[];
  reset?: Array<'framing' | 'place' | 'motion'>;
}

/** Which keys a key op edits: a clip's framing keys or (layers) its motion keys, in a format. */
export interface KeyTarget { clipId: string; formatId: FormatRef; kind: 'framing' | 'motion' }

/**
 * The undo coalescing key of an edit to a clip's framing in a format (docs/dev/formats-1.7.0-build.md): one drag of a
 * key, or of the static framing (`index` 'static'), is one undo step. Framing: frame:<clipId>:<formatId>:<index>;
 * motion: motion:<clipId>:<formatId>:<index>.
 */
export const keyCoalesce = (t: KeyTarget, index: number | 'static'): string => `${t.kind === 'motion' ? 'motion' : 'frame'}:${t.clipId}:${t.formatId}:${index}`;

/** The keys a target has now (a format with the same framing as the primary: the primary's; motion: its position and size keys). */
export function keysOf(doc: SequenceDocument, t: KeyTarget): Array<FramingKey | MotionKey> {
  const hit = findPicture(doc, t.clipId);
  if (!hit) return [];
  if (t.kind === 'framing') return framingFor(hit.clip, t.formatId).keys ?? [];
  if (!hit.layer) return [];
  const clip = hit.clip as LayerClip;
  if (t.formatId === PRIMARY_FORMAT) return clip.motion?.keys ?? [];
  const own = clip.formatFraming?.[t.formatId]?.motion;
  return own ? own.keys : tidyMotionKeys(clip.motion?.keys, true);
}

function setKeys(doc: SequenceDocument, t: KeyTarget, keys: Array<FramingKey | MotionKey>): SequenceDocument {
  return t.kind === 'framing'
    ? ops.setFormatFraming(doc, t.clipId, t.formatId, { keys: keys as FramingKey[] })
    : ops.setFormatFraming(doc, t.clipId, t.formatId, { motion: keys as MotionKey[] });
}

/** Keys moved by `delta` ticks (switchAngle), kept at 0 or later and one per time. */
function shiftFramingKeys(keys: FramingKey[] | undefined, delta: number): FramingKey[] | undefined {
  if (!keys?.length || !delta) return keys;
  return tidyFramingKeys(keys.map((k) => ({ ...k, atTicks: k.atTicks + delta })));
}

/** The fields of a clip's fades an edit may set (sound: fadeIn/OutTicks; picture: videoFadeIn/OutTicks). */
export type ClipFadePatch = Partial<Pick<VideoClip, 'fadeInTicks' | 'fadeOutTicks' | 'videoFadeInTicks' | 'videoFadeOutTicks'>>;

/**
 * A clip's fades kept valid after an edit (0.96.0): on the frame grid, the fade in no longer than the clip and the
 * fade out no longer than what the fade in leaves, for sound and picture alike. Picture fades of 0 are left out, so
 * a document that never had one keeps its bytes.
 */
export function tidyFades<T extends VideoClip | AudioClip | LayerClip>(c: T, rate: Rational): T {
  const next = { ...c };
  const max = clipDuration(next);
  next.fadeInTicks = Math.max(0, Math.min(snap(next.fadeInTicks, rate), max));
  next.fadeOutTicks = Math.max(0, Math.min(snap(next.fadeOutTicks, rate), max - next.fadeInTicks));
  if ('videoFadeInTicks' in next || 'videoFadeOutTicks' in next) {
    const vin = Math.max(0, Math.min(snap(next.videoFadeInTicks ?? 0, rate), max));
    const vout = Math.max(0, Math.min(snap(next.videoFadeOutTicks ?? 0, rate), max - vin));
    if (vin > 0) next.videoFadeInTicks = vin; else delete next.videoFadeInTicks;
    if (vout > 0) next.videoFadeOutTicks = vout; else delete next.videoFadeOutTicks;
  }
  return next;
}

/** A time span on a track, [start, end). */
export interface Span { start: number; end: number }

/**
 * Where a clip of `len` ticks can go on a track that already holds `taken` (0.96.0: no overlaps on A2 or a layer):
 * at `want` when that is free, otherwise at the nearest free position (right after or before a neighbour), never
 * before 0. Null when there is no room anywhere (never: after the last clip there always is).
 */
export function freeStart(taken: Span[], want: number, len: number): number | null {
  const fits = (at: number) => at >= 0 && taken.every((t) => at >= t.end || at + len <= t.start);
  const start = Math.max(0, want);
  if (fits(start)) return start;
  const candidates = taken.flatMap((t) => [t.end, t.start - len]).filter(fits);
  if (!candidates.length) return null;
  return candidates.sort((a, b) => Math.abs(a - start) - Math.abs(b - start) || a - b)[0]!;
}

const spans = (clips: Array<{ id: string; recordInTicks: number; sourceInTicks: number; sourceOutTicks: number }>, except?: string): Span[] =>
  clips.filter((c) => c.id !== except).map((c) => ({ start: c.recordInTicks, end: c.recordInTicks + clipDuration(c) }));

/** Clips on one track that overlap each other, as pairs of ids (for checkSequence). */
export function overlappingClips(clips: Array<{ id: string; recordInTicks: number; sourceInTicks: number; sourceOutTicks: number }>): Array<[string, string]> {
  const sorted = [...clips].sort((a, b) => a.recordInTicks - b.recordInTicks);
  const out: Array<[string, string]> = [];
  for (let i = 0; i < sorted.length; i++) {
    for (let j = i + 1; j < sorted.length && sorted[j]!.recordInTicks < sorted[i]!.recordInTicks + clipDuration(sorted[i]!); j++) {
      if (clipDuration(sorted[j]!) > 0 && clipDuration(sorted[i]!) > 0) out.push([sorted[i]!.id, sorted[j]!.id]);
    }
  }
  return out;
}

/**
 * How far a clip's media reaches (1.7.2, slip and slide): the source's length, or for a multicam clip its angle's
 * length when that is shorter. A length of 0 or less (a still, or a source whose length is not known) has no end.
 */
function mediaEnd(doc: SequenceDocument, c: { versionId: string; multicam?: VideoClip['multicam'] }, durationTicks: number): number {
  let end = durationTicks > 0 && Number.isFinite(durationTicks) ? durationTicks : Number.MAX_SAFE_INTEGER;
  const g = c.multicam && (doc.multicam ?? []).find((x) => x.id === c.multicam!.groupId);
  const a = g?.angles[c.multicam!.angle];
  if (a) end = Math.min(end, a.durationTicks);
  return end;
}

/** Whole frames of `ticks` (towards 0), so a clamped slip or slide still moves by whole frames (1.7.2). */
const wholeFrames = (ticks: number, tpf: number) => Math.sign(ticks) * Math.round(Math.floor(Math.abs(ticks) / tpf + 1e-9) * tpf);

/** Markers by time; the list is left out when empty (so documents without markers keep their bytes). */
function withMarkers(doc: SequenceDocument, markers: Marker[]): SequenceDocument {
  const next = { ...doc };
  const sorted = [...markers].sort((a, b) => a.atTicks - b.atTicks).slice(0, MAX_MARKERS);
  if (sorted.length) next.markers = sorted; else delete next.markers;
  return next;
}

/* ---------------------------------------------------------------- 1.7.3: items, links, the clipboard (docs/dev/editor-1.7.3-build.md) */

/**
 * One selectable thing on the timeline (1.7.3), as the ops and the editor's selection name it. `id` is the clip's,
 * graphic's, cue's or marker's id; 'audio' is a clip on any audio track (A2–A8); a 'gap' is the black before the V1
 * clip `id` (storyline.ts gapsOf). The same id can name a clip and the gap before it, so compare with itemKey.
 */
export interface ItemRef {
  kind: 'video' | 'audio' | 'layer' | 'graphic' | 'cue' | 'marker' | 'gap';
  id: string;
}
export type ItemRefKind = ItemRef['kind'];
/** The kinds that can carry a linkId. */
export const LINKABLE_KINDS = ['video', 'audio', 'layer', 'graphic'] as const satisfies readonly ItemRefKind[];
/** A stable key for an item (Sets, React keys): "<kind>:<id>". */
export const itemKey = (r: ItemRef): string => `${r.kind}:${r.id}`;
const isLinkable = (r: ItemRef) => (LINKABLE_KINDS as readonly string[]).includes(r.kind);

/** How a ripple reaches the other tracks (1.7.3): 'v1' moves the storyline only; 'sync' also shifts the rest (the Sync toggle). */
export type RippleMode = 'v1' | 'sync';
/** How clips land on V1 (1.7.3): 'overwrite' replaces what is under them; 'insert' pushes it later. */
export type PlaceMode = 'overwrite' | 'insert';

/** The kind of the item with this id (never 'gap'), or null. Clips first, then graphics, cues and markers. */
export function itemRefOf(doc: SequenceDocument, id: string): ItemRef | null {
  if (doc.video.some((c) => c.id === id)) return { kind: 'video', id };
  if (audioTrackOf(doc, id)) return { kind: 'audio', id };
  if ((doc.layers ?? []).some((l) => l.clips.some((c) => c.id === id))) return { kind: 'layer', id };
  if (doc.graphics.some((g) => g.id === id)) return { kind: 'graphic', id };
  if (doc.subtitles?.cues.some((c) => c.id === id)) return { kind: 'cue', id };
  if ((doc.markers ?? []).some((m) => m.id === id)) return { kind: 'marker', id };
  return null;
}

/** Every item in the document but gaps (⌘A): V1, the layers, the audio tracks, graphics, cues, markers. */
export function allItemRefs(doc: SequenceDocument): ItemRef[] {
  return [
    ...doc.video.map((c) => ({ kind: 'video' as const, id: c.id })),
    ...(doc.layers ?? []).flatMap((l) => l.clips.map((c) => ({ kind: 'layer' as const, id: c.id }))),
    ...allAudioTracks(doc).flatMap((t) => t.clips.map((c) => ({ kind: 'audio' as const, id: c.id }))),
    ...doc.graphics.map((g) => ({ kind: 'graphic' as const, id: g.id })),
    ...(doc.subtitles?.cues ?? []).map((c) => ({ kind: 'cue' as const, id: c.id })),
    ...(doc.markers ?? []).map((m) => ({ kind: 'marker' as const, id: m.id })),
  ];
}

/** Where an item sits in the sequence, [start, end) in record ticks (a marker: start = end), or null when it is not there. */
export function itemSpan(doc: SequenceDocument, r: ItemRef): Span | null {
  switch (r.kind) {
    case 'video': { const p = layoutVideo(doc).find((x) => x.clip.id === r.id); return p ? { start: p.recordIn, end: p.recordOut } : null; }
    case 'gap': { const g = gapsOf(doc).find((x) => x.id === r.id); return g ? { start: g.start, end: g.end } : null; }
    case 'audio': { const c = allAudioTracks(doc).flatMap((t) => t.clips).find((x) => x.id === r.id); return c ? { start: c.recordInTicks, end: c.recordInTicks + clipDuration(c) } : null; }
    case 'layer': { const c = (doc.layers ?? []).flatMap((l) => l.clips).find((x) => x.id === r.id); return c ? { start: c.recordInTicks, end: c.recordInTicks + clipDuration(c) } : null; }
    case 'graphic': { const g = doc.graphics.find((x) => x.id === r.id); return g ? { start: g.recordInTicks, end: g.recordInTicks + g.durationTicks } : null; }
    case 'cue': { const c = doc.subtitles?.cues.find((x) => x.id === r.id); return c ? { start: c.startTicks, end: c.endTicks } : null; }
    case 'marker': { const m = (doc.markers ?? []).find((x) => x.id === r.id); return m ? { start: m.atTicks, end: m.atTicks } : null; }
  }
}

/** The linkId of every linkable item that has one, by item id. */
function linkIds(doc: SequenceDocument): Map<string, { ref: ItemRef; linkId: string }> {
  const out = new Map<string, { ref: ItemRef; linkId: string }>();
  const add = (kind: ItemRefKind, x: { id: string; linkId?: string }) => { if (x.linkId) out.set(itemKey({ kind, id: x.id }), { ref: { kind, id: x.id }, linkId: x.linkId }); };
  doc.video.forEach((c) => add('video', c));
  allAudioTracks(doc).forEach((t) => t.clips.forEach((c) => add('audio', c)));
  (doc.layers ?? []).forEach((l) => l.clips.forEach((c) => add('layer', c)));
  doc.graphics.forEach((g) => add('graphic', g));
  return out;
}

/**
 * `items` with the rest of their link groups (1.7.3: clicking one linked item selects the group; Option-click picks
 * one, so the editor skips this). The given items first, in their order, then the others; no item twice.
 */
export function linkedItems(doc: SequenceDocument, items: readonly ItemRef[]): ItemRef[] {
  const links = linkIds(doc);
  const groups = new Set(items.map((r) => links.get(itemKey(r))?.linkId).filter((x): x is string => Boolean(x)));
  const seen = new Set<string>();
  const out: ItemRef[] = [];
  const push = (r: ItemRef) => { const k = itemKey(r); if (!seen.has(k)) { seen.add(k); out.push(r); } };
  items.forEach(push);
  if (groups.size) for (const { ref, linkId } of links.values()) if (groups.has(linkId)) push(ref);
  return out;
}

/** Set (or, with null, remove) the linkId of the linkable items with these ids; arrays without them stay the same. */
function setLinkId(doc: SequenceDocument, ids: ReadonlySet<string>, linkId: string | null): SequenceDocument {
  const fix = <T extends { id: string; linkId?: string }>(x: T): T => {
    if (!ids.has(x.id) || (x.linkId ?? null) === linkId) return x;
    const next = { ...x };
    if (linkId) next.linkId = linkId; else delete next.linkId;
    return next;
  };
  const list = <T extends { id: string; linkId?: string }>(xs: T[]): T[] => (xs.some((x) => ids.has(x.id)) ? xs.map(fix) : xs);
  const next: SequenceDocument = { ...doc, video: list(doc.video), audio: list(doc.audio), graphics: list(doc.graphics) };
  if (doc.layers?.some((l) => l.clips.some((c) => ids.has(c.id)))) next.layers = doc.layers.map((l) => ({ ...l, clips: list(l.clips) }));
  if (doc.audioTracks?.some((t) => t.clips.some((c) => ids.has(c.id)))) next.audioTracks = doc.audioTracks.map((t) => ({ ...t, clips: list(t.clips) }));
  return next;
}

/** Link groups of one item are dissolved (after an unlink, a delete or a track removed); the same document when none. */
function tidyLinks(doc: SequenceDocument): SequenceDocument {
  const counts = new Map<string, string[]>();
  for (const [, { ref, linkId }] of linkIds(doc)) counts.set(linkId, [...(counts.get(linkId) ?? []), ref.id]);
  const lone = new Set([...counts.values()].filter((ids) => ids.length === 1).flat());
  return lone.size ? setLinkId(doc, lone, null) : doc;
}

/** The clips of an audio track ('a2' or an audioTracks id), or null when there is no such track. */
function audioClipsOn(doc: SequenceDocument, trackId: string): AudioClip[] | null {
  if (trackId === A2_TRACK) return doc.audio;
  return (doc.audioTracks ?? []).find((t) => t.id === trackId)?.clips ?? null;
}

/** The document with an audio track's clips replaced. */
function withAudioClips(doc: SequenceDocument, trackId: string, clips: AudioClip[]): SequenceDocument {
  if (trackId === A2_TRACK) return { ...doc, audio: clips };
  return { ...doc, audioTracks: (doc.audioTracks ?? []).map((t) => (t.id === trackId ? { ...t, clips } : t)) };
}

/** The spans of the graphics on a track (but `except`). */
const graphicSpans = (doc: SequenceDocument, track: GraphicTrack, except?: string): Span[] =>
  doc.graphics.filter((x) => x.id !== except && graphicTrackOf(x) === track).map((x) => ({ start: x.recordInTicks, end: x.recordInTicks + x.durationTicks }));

/** A graphic on a track (0 = GFX 1: `track` left out). */
function onGraphicTrack(g: GraphicInstance, track: GraphicTrack): GraphicInstance {
  if (graphicTrackOf(g) === track) return g;
  const next = { ...g };
  if (track) next.track = track; else delete next.track;
  return next;
}

/**
 * Trim the in or out point of a clip on a freely placed track (a layer, an audio track): trimming the in point keeps
 * the out point where it is in the sequence, and neither edge goes into a neighbour on the track (0.96.0).
 */
function trimOnTrack<T extends AudioClip | LayerClip>(clips: T[], id: string, edge: 'in' | 'out', deltaTicks: number, sourceDurationTicks: number, rate: Rational): T[] {
  const tpf = ticksPerFrame(rate);
  const d = snap(deltaTicks, rate);
  return clips.map((c) => {
    if (c.id !== id) return c;
    const others = spans(clips, id);
    const end = c.recordInTicks + clipDuration(c);
    if (edge === 'in') {
      const floor = Math.max(0, ...others.filter((o) => o.end <= c.recordInTicks).map((o) => o.end));
      const sourceInTicks = Math.min(Math.max(0, c.sourceInTicks + d, c.sourceInTicks - (c.recordInTicks - floor)), c.sourceOutTicks - tpf);
      return tidyFades({ ...c, sourceInTicks, recordInTicks: c.recordInTicks + (sourceInTicks - c.sourceInTicks) }, rate);
    }
    const ceiling = Math.min(Number.MAX_SAFE_INTEGER, ...others.filter((o) => o.start >= end).map((o) => o.start));
    const sourceOutTicks = Math.max(Math.min(sourceDurationTicks, c.sourceOutTicks + d, c.sourceOutTicks + (ceiling - end)), c.sourceInTicks + tpf);
    return tidyFades({ ...c, sourceOutTicks }, rate);
  });
}

/**
 * What ⌘C puts on the clipboard (1.7.3, copyItems; the editor keeps it in a module store and localStorage under
 * `prism.clipboard`). Versioned: a reader that does not know `v` ignores it (parseClipboard).
 *  - `offsetTicks`: each item's start, relative to the earliest item's start (that item has 0);
 *  - the items as they are in the document (ids, linkIds and all); pasteItems gives them new ids and new link ids
 *    (the copies are linked to each other as the originals were, not to the originals);
 *  - a V1 clip without gapBeforeTicks (its place is its offset); its transitionIn is kept, and applies only when it
 *    lands overlapping the same neighbour (storyline.ts);
 *  - its sound goes with it unless soundDetached; the detached sound is an 'audio' item only when it was copied;
 *  - `track` / `layer`: the position counted from 0 (audio 0 = A2, 1 = A3…; layer 0 = V2); a graphic's track is its own `track`.
 */
export type ClipboardItem =
  | { kind: 'video'; offsetTicks: number; clip: VideoClip }
  | { kind: 'audio'; offsetTicks: number; track: number; clip: AudioClip }
  | { kind: 'layer'; offsetTicks: number; layer: number; clip: LayerClip }
  | { kind: 'graphic'; offsetTicks: number; graphic: GraphicInstance }
  | { kind: 'cue'; offsetTicks: number; cue: Cue }
  | { kind: 'marker'; offsetTicks: number; marker: Marker };

export interface ClipboardPayload {
  v: 1;
  /** The copied sequence's frame rate (pasting into another rate snaps every time to the new grid). */
  frameRate: Rational;
  /** From the earliest item's start to the latest item's end. */
  durationTicks: number;
  items: ClipboardItem[];
}

const Offset = z.number().int().nonnegative();
const ClipboardItemSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('video'), offsetTicks: Offset, clip: VideoClipSchema }),
  z.object({ kind: z.literal('audio'), offsetTicks: Offset, track: z.number().int().min(0).max(MAX_AUDIO_TRACKS), clip: AudioClipSchema }),
  z.object({ kind: z.literal('layer'), offsetTicks: Offset, layer: z.number().int().min(0).max(MAX_LAYERS - 1), clip: LayerClipSchema }),
  z.object({ kind: z.literal('graphic'), offsetTicks: Offset, graphic: GraphicInstanceSchema }),
  z.object({ kind: z.literal('cue'), offsetTicks: Offset, cue: CueSchema }),
  z.object({ kind: z.literal('marker'), offsetTicks: Offset, marker: MarkerSchema }),
]);
export const ClipboardPayloadSchema = z.object({
  v: z.literal(1),
  frameRate: z.object({ num: z.number().int().positive(), den: z.number().int().positive() }),
  durationTicks: Offset,
  items: z.array(ClipboardItemSchema).min(1).max(5000),
});

/** A clipboard payload read back (localStorage, another tab): the payload, or null when it is not one this version knows. */
export function parseClipboard(raw: unknown): ClipboardPayload | null {
  const r = ClipboardPayloadSchema.safeParse(typeof raw === 'string' ? safeJson(raw) : raw);
  return r.success ? (r.data as ClipboardPayload) : null;
}
const safeJson = (s: string): unknown => { try { return JSON.parse(s); } catch { return null; } };

/** What pasteItems returns: the document and the pasted items (the editor selects them). */
export interface PasteResult { doc: SequenceDocument; pasted: ItemRef[] }

/** Why a V1 clip and an audio clip cannot be linked back into one (ops.relink), or null when they can. */
export function relinkProblem(doc: SequenceDocument, clipId: string, audioId: string): string | null {
  const p = layoutVideo(doc).find((x) => x.clip.id === clipId);
  if (!p) return 'Pick a clip on V1';
  if (!p.clip.soundDetached) return 'That clip’s sound is already linked';
  const trackId = audioTrackOf(doc, audioId);
  const a = trackId ? audioClipsOn(doc, trackId)!.find((c) => c.id === audioId) : undefined;
  if (!a) return 'Pick the sound on an audio track';
  if (a.versionId !== p.clip.versionId || a.recordInTicks !== p.recordIn || a.sourceInTicks !== p.clip.sourceInTicks || a.sourceOutTicks !== p.clip.sourceOutTicks) return 'Line the sound up with its picture to link it';
  return null;
}

/* ---------------------------------------------------------------- 1.7.3: storyline editing internals (storyline.ts) */

/** A storyline clip at `start`, its end from its own length (the clip may have been trimmed). */
const itemAt = (clip: VideoClip, start: number): TimelineItem => ({ clip, start, end: start + clipDuration(clip) });

/** Items in fromTimeline's order: by start, items starting together keep their order. */
const sortItems = (items: readonly TimelineItem[]): TimelineItem[] =>
  items.map((it, i) => ({ it, i })).sort((a, b) => a.it.start - b.it.start || a.i - b.i).map((x) => x.it);

/** The clip before each V1 clip, by id (null for the first). */
function predecessors(items: readonly TimelineItem[]): Map<string, string | null> {
  const out = new Map<string, string | null>();
  sortItems(items).forEach((it, i, all) => out.set(it.clip.id, all[i - 1]?.clip.id ?? null));
  return out;
}

/** The transition into `clip` from `prev` as layoutVideo applies it (0 for none). */
function applied(prev: VideoClip, clip: VideoClip, rate: Rational): number {
  const want = clip.transitionIn?.durationTicks ?? 0;
  return want > 0 ? Math.min(snap(want, rate), maxTransitionTicks(prev, clip, rate)) : 0;
}

/**
 * Write V1 back from items at absolute times (1.7.3): a clip whose neighbour before it changed (oldPred) loses its
 * transitionIn; a clip that overlaps the one before it keeps its place only through a transition of exactly that
 * overlap (its duration is set to the overlap when the clip still has one that fits), otherwise its head is trimmed
 * off the overlap (ops never ask for this; it keeps every clip where it was put). The same document when nothing changed.
 */
function withItems(doc: SequenceDocument, items: readonly TimelineItem[], oldPred: ReadonlyMap<string, string | null>): SequenceDocument {
  const rate = doc.frameRate;
  const sorted = sortItems(items.filter((it) => clipDuration(it.clip) > 0));
  const out: TimelineItem[] = [];
  for (const raw of sorted) {
    let it = raw;
    const prev = out.at(-1);
    const before = prev?.clip.id ?? null;
    if (it.clip.transitionIn && oldPred.has(it.clip.id) && oldPred.get(it.clip.id) !== before) it = { ...it, clip: { ...it.clip, transitionIn: null } };
    // 1.7.4: a crossfade from a clip before it that is now another clip goes too, so a dormant one never wakes against
    // a different clip (with nothing before it, it stays dormant).
    if (it.clip.crossfadeInTicks && before !== null && oldPred.has(it.clip.id) && oldPred.get(it.clip.id) !== before) it = { ...it, clip: withoutCrossfade(it.clip) };
    if (prev && prev.end > it.start) {
      const overlap = prev.end - it.start;
      const c = it.clip;
      if (c.transitionIn && applied(prev.clip, c, rate) === overlap) { /* as it is */ } else if (c.transitionIn && overlap <= maxTransitionTicks(prev.clip, c, rate)) {
        it = { ...it, clip: { ...c, transitionIn: { ...c.transitionIn, durationTicks: overlap } } };
      } else {
        if (clipDuration(c) <= overlap) continue;
        it = itemAt(tidyFades({ ...c, transitionIn: null, sourceInTicks: c.sourceInTicks + overlap }, rate), prev.end);
      }
    }
    out.push(it);
  }
  const video = fromTimeline(out, rate);
  return video.length === doc.video.length && video.every((c, i) => c === doc.video[i]) ? doc : { ...doc, video };
}

/**
 * The parts of a storyline item outside [s, e) (an overwrite landing there, or with s = e a split point): the item
 * itself, its head (the same clip, trimmed at the out point), its tail (trimmed at the in point; a new clip,
 * newId('v'), when the head is kept too) or both. Split halves lose the fades at the cut, as splitAt's.
 */
function cutOut(it: TimelineItem, s: number, e: number, rate: Rational): TimelineItem[] {
  if (e <= it.start || s >= it.end || (s === e && s === it.start)) return [it];
  const c = it.clip;
  const head = s > it.start;
  const tail = e < it.end;
  const out: TimelineItem[] = [];
  if (head) {
    const left: VideoClip = { ...c, sourceOutTicks: c.sourceOutTicks - (it.end - s) };
    if (tail) { left.fadeOutTicks = 0; delete left.videoFadeOutTicks; }
    out.push(itemAt(tidyFades(left, rate), it.start));
  }
  if (tail) {
    const right: VideoClip = head ? { ...structuredClone(c), id: newId('v'), fadeInTicks: 0, transitionIn: null } : { ...c };
    if (head) { delete right.videoFadeInTicks; delete right.crossfadeInTicks; delete right.crossfadeAlign; }
    delete right.gapBeforeTicks;
    right.sourceInTicks = c.sourceInTicks + (e - it.start);
    out.push(itemAt(tidyFades(right, rate), e));
  }
  return out;
}

/** `xs` mapped, or `xs` itself when `f` returns every element unchanged (so a no-op keeps the document's arrays). */
function mapSame<T>(xs: T[], f: (x: T) => T): T[] {
  const out = xs.map(f);
  return out.every((x, i) => x === xs[i]) ? xs : out;
}

/** Items on one of the other tracks, for the sync rule: [start, end) and its item key. */
interface TrackSpan { key: string; start: number; end: number }

/** Every track but V1, each as its spans: A2–A8, the layers, GFX 1–3, the cues, the markers. */
function otherTracks(doc: SequenceDocument): TrackSpan[][] {
  const clipSpan = (kind: ItemRefKind) => (c: { id: string; recordInTicks: number; sourceInTicks: number; sourceOutTicks: number }): TrackSpan => ({ key: itemKey({ kind, id: c.id }), start: c.recordInTicks, end: c.recordInTicks + clipDuration(c) });
  const out: TrackSpan[][] = [];
  for (const t of allAudioTracks(doc)) out.push(t.clips.map(clipSpan('audio')));
  for (const l of doc.layers ?? []) out.push(l.clips.map(clipSpan('layer')));
  for (let k = 0; k < MAX_GRAPHIC_TRACKS; k++) out.push(doc.graphics.filter((g) => graphicTrackOf(g) === k).map((g) => ({ key: itemKey({ kind: 'graphic', id: g.id }), start: g.recordInTicks, end: g.recordInTicks + g.durationTicks })));
  out.push((doc.subtitles?.cues ?? []).map((c) => ({ key: itemKey({ kind: 'cue', id: c.id }), start: c.startTicks, end: c.endTicks })));
  // Markers are points: they only stop at 0.
  out.push((doc.markers ?? []).map((m) => ({ key: itemKey({ kind: 'marker', id: m.id }), start: m.atTicks, end: m.atTicks })));
  return out;
}

/**
 * How far the other tracks can move earlier from P together (the sync rule): on each track the items starting at or
 * after P move as one, never into the item before them nor before 0. Items in `skip` (moved by the caller) are left
 * out. Infinity when nothing moves.
 */
function syncRoom(doc: SequenceDocument, P: number, skip: ReadonlySet<string>): number {
  let room = Number.POSITIVE_INFINITY;
  for (const track of otherTracks(doc)) {
    const spansOn = track.filter((x) => !skip.has(x.key));
    const moving = spansOn.filter((x) => x.start >= P);
    if (!moving.length) continue;
    const first = Math.min(...moving.map((x) => x.start));
    const before = spansOn.filter((x) => x.start < P && x.end <= first);
    room = Math.min(room, first - Math.max(0, ...before.map((x) => x.end)));
  }
  return room;
}

/** The other tracks' items (not V1) starting at or after P, but those in `skip`, moved by `delta`. */
function shiftOthers(doc: SequenceDocument, P: number, delta: number, skip: ReadonlySet<string>): SequenceDocument {
  if (!delta) return doc;
  const moves = (kind: ItemRefKind, id: string, start: number) => start >= P && !skip.has(itemKey({ kind, id }));
  const shift = <T extends { id: string; recordInTicks: number }>(kind: ItemRefKind, xs: T[]): T[] =>
    (xs.some((x) => moves(kind, x.id, x.recordInTicks)) ? xs.map((x) => (moves(kind, x.id, x.recordInTicks) ? { ...x, recordInTicks: Math.max(0, x.recordInTicks + delta) } : x)) : xs);
  let next: SequenceDocument = { ...doc, audio: shift('audio', doc.audio), graphics: shift('graphic', doc.graphics) };
  if (doc.layers) next.layers = mapSame(doc.layers, (l) => { const clips = shift('layer', l.clips); return clips === l.clips ? l : { ...l, clips }; });
  if (doc.audioTracks) next.audioTracks = mapSame(doc.audioTracks, (t) => { const clips = shift('audio', t.clips); return clips === t.clips ? t : { ...t, clips }; });
  const cues = doc.subtitles?.cues;
  if (cues?.some((c) => moves('cue', c.id, c.startTicks))) {
    next.subtitles = { ...doc.subtitles!, cues: cues.map((c) => (moves('cue', c.id, c.startTicks) ? { ...c, startTicks: Math.max(0, c.startTicks + delta), endTicks: Math.max(0, c.endTicks + delta) } : c)) };
  }
  if (doc.markers?.some((m) => moves('marker', m.id, m.atTicks))) next = withMarkers(next, doc.markers.map((m) => (moves('marker', m.id, m.atTicks) ? { ...m, atTicks: Math.max(0, m.atTicks + delta) } : m)));
  return next;
}

/**
 * Remove V1 clips and close up (rippleDelete, placeVideo's insert lift): runs of neighbouring clips are done from
 * the last to the first. Each run's length (with the gaps before its clips; the gap before the next clip stays) is
 * closed; with 'sync' the other tracks from the run's start move as much earlier, limited by syncRoom (V1 then keeps
 * the rest as a gap). Returns each run's start and end (before the edit) and how far it closed.
 */
function rippleV1(doc: SequenceDocument, ids: ReadonlySet<string>, ripple: RippleMode, skip: ReadonlySet<string>): { doc: SequenceDocument; runs: Array<{ start: number; end: number; by: number }> } {
  const groups: number[][] = [];
  toTimeline(doc).forEach((it, i) => {
    if (!ids.has(it.clip.id)) return;
    const g = groups.at(-1);
    if (g && g.at(-1) === i - 1) g.push(i); else groups.push([i]);
  });
  let d = doc;
  const runs: Array<{ start: number; end: number; by: number }> = [];
  const ids0 = toTimeline(doc).map((it) => it.clip.id);
  for (const g of [...groups].reverse()) {
    const items = toTimeline(d);
    const i = items.findIndex((it) => it.clip.id === ids0[g[0]!]);
    const j = items.findIndex((it) => it.clip.id === ids0[g.at(-1)!]);
    if (i < 0 || j < i) continue;
    const prevEnd = i > 0 ? items[i - 1]!.end : 0;
    const P = items[i]!.start;
    const runEnd = items[j]!.end;
    const next = items[j + 1];
    const full = Math.max(0, (next ? next.start - gapBefore(next.clip, d.frameRate) : runEnd) - prevEnd);
    const by = ripple === 'sync' ? Math.max(0, Math.min(full, syncRoom(d, P, skip))) : full;
    const rest = items.flatMap((it, k) => (k >= i && k <= j ? [] : k > j ? [itemAt(it.clip, it.start - by)] : [it]));
    d = withItems(d, rest, predecessors(items));
    if (ripple === 'sync') d = shiftOthers(d, P, -by, skip);
    runs.unshift({ start: P, end: runEnd, by });
  }
  return { doc: d, runs };
}

/**
 * Put clips into V1 at T, pushing what is there later (placeVideo's insert, pasteItems' ⌘⇧V): `clips` at T plus their
 * offsets, `length` long together. A T inside a transition's overlap is the cut there: the outgoing clip ends where it
 * did, the incoming one (whole, its transition gone with its neighbour) follows the inserted clips. A clip T falls
 * inside is split there. With 'sync' the other tracks from T (but `skip`) move `length` later. `pred` names the clip
 * before each inserted clip as it was (moved clips keep a transition between them).
 */
function insertV1(doc: SequenceDocument, clips: Array<{ clip: VideoClip; offset: number }>, at: number, length: number, ripple: RippleMode, skip: ReadonlySet<string>, pred: ReadonlyMap<string, string | null>): SequenceDocument {
  const rate = doc.frameRate;
  const items = toTimeline(doc);
  let T = Math.max(0, snap(at, rate));
  let whole: string | null = null;
  const sorted = sortItems(items);
  for (let k = 1; k < sorted.length; k++) {
    const it = sorted[k]!, prev = sorted[k - 1]!;
    if (T >= it.start && T < prev.end) { T = prev.end; whole = it.clip.id; }
  }
  const rest = items.flatMap((it) => (it.clip.id === whole ? [itemAt(it.clip, T + length)] : cutOut(it, T, T, rate).map((x) => (x.start >= T ? itemAt(x.clip, x.start + length) : x))));
  const landed = clips.map(({ clip, offset }) => itemAt(clip, T + offset));
  const oldPred = new Map([...predecessors(items), ...pred]);
  let out = withItems(doc, [...rest, ...landed], oldPred);
  if (ripple === 'sync') out = shiftOthers(out, T, length, skip);
  return out;
}

/** Overwrite onto V1 (placeVideo, pasteItems' ⌘V): `landed` at their times over `rest`, which is cut away under them. */
function overwriteV1(doc: SequenceDocument, rest: TimelineItem[], landed: TimelineItem[], oldPred: ReadonlyMap<string, string | null>): SequenceDocument {
  let under = rest;
  for (const l of landed) under = under.flatMap((it) => cutOut(it, l.start, l.end, doc.frameRate));
  return withItems(doc, [...under, ...landed], oldPred);
}

/** placeVideo's body, with the items a moveItems call moves itself (`skip`), which the sync ripple leaves alone. */
function placeV1(doc: SequenceDocument, ids: readonly string[], deltaTicks: number, mode: PlaceMode, ripple: RippleMode, skip: ReadonlySet<string>): SequenceDocument {
  const items = toTimeline(doc);
  const set = new Set(ids.filter((id) => doc.video.some((c) => c.id === id)));
  if (!set.size) return doc;
  const moved = items.filter((it) => set.has(it.clip.id));
  const earliest = Math.min(...moved.map((m) => m.start));
  const d = Math.max(snap(deltaTicks, doc.frameRate), -earliest);
  if (!d) return doc;
  const oldPred = predecessors(items);
  if (mode === 'overwrite') {
    return overwriteV1(doc, items.filter((it) => !set.has(it.clip.id)), moved.map((m) => itemAt(m.clip, m.start + d)), oldPred);
  }
  // Insert: the drop time against the storyline before the edit; inside the moved clips is where they are.
  const T0 = earliest + d;
  if (moved.some((m) => T0 >= m.start && T0 < m.end)) return doc;
  const lifted = rippleV1(doc, set, ripple, skip);
  const T = Math.max(0, T0 - lifted.runs.filter((r) => r.end <= T0).reduce((n, r) => n + r.by, 0));
  const length = Math.max(...moved.map((m) => m.end)) - earliest;
  const pred = new Map([...set].map((id) => [id, oldPred.get(id) ?? null] as const));
  return insertV1(lifted.doc, moved.map((m) => ({ clip: m.clip, offset: m.start - earliest })), T, length, ripple, skip, pred);
}

/** The layer, audio and graphic clips, cues and markers among `items` removed (V1 and gaps are the caller's). */
function removeOthers(doc: SequenceDocument, items: readonly ItemRef[]): SequenceDocument {
  const of = (kind: ItemRefKind) => new Set(items.filter((r) => r.kind === kind).map((r) => r.id));
  const audio = of('audio'), layer = of('layer'), graphic = of('graphic'), cue = of('cue'), marker = of('marker');
  const drop = <T extends { id: string }>(xs: T[], ids: Set<string>): T[] => (xs.some((x) => ids.has(x.id)) ? xs.filter((x) => !ids.has(x.id)) : xs);
  let next: SequenceDocument = { ...doc, audio: drop(doc.audio, audio), graphics: drop(doc.graphics, graphic) };
  if (doc.layers) next.layers = mapSame(doc.layers, (l) => { const clips = drop(l.clips, layer); return clips === l.clips ? l : { ...l, clips }; });
  if (doc.audioTracks) next.audioTracks = mapSame(doc.audioTracks, (t) => { const clips = drop(t.clips, audio); return clips === t.clips ? t : { ...t, clips }; });
  if (doc.subtitles?.cues.some((c) => cue.has(c.id))) next.subtitles = { ...doc.subtitles, cues: drop(doc.subtitles.cues, cue) };
  if (doc.markers?.some((m) => marker.has(m.id))) next = withMarkers(next, drop(doc.markers, marker));
  return next;
}

/**
 * Remove items from one freely placed track and close up (rippleDelete): from the last removed item to the first,
 * the items after it move earlier by its length, never into the item before them nor before 0.
 */
function closeUpTrack<T>(xs: T[], removed: ReadonlySet<string>, span: (x: T) => { id: string; start: number; end: number }, moveTo: (x: T, start: number) => T): T[] {
  if (!xs.some((x) => removed.has(span(x).id))) return xs;
  let list = [...xs];
  const gone = xs.filter((x) => removed.has(span(x).id)).sort((a, b) => span(b).start - span(a).start);
  for (const g of gone) {
    const { start: s, end: e } = span(g);
    list = list.filter((x) => x !== g);
    const later = list.filter((x) => span(x).start >= s);
    if (!later.length) continue;
    const first = Math.min(...later.map((x) => span(x).start));
    const floor = Math.max(0, ...list.filter((x) => span(x).start < s && span(x).end <= first).map((x) => span(x).end));
    const by = Math.max(0, Math.min(e - s, first - floor));
    if (by) list = list.map((x) => (later.includes(x) ? moveTo(x, span(x).start - by) : x));
  }
  return list;
}

/** rippleDelete's other tracks: layer, audio and graphic clips and cues closed up on their own track; markers removed. */
function rippleOthers(doc: SequenceDocument, items: readonly ItemRef[]): SequenceDocument {
  const of = (kind: ItemRefKind) => new Set(items.filter((r) => r.kind === kind).map((r) => r.id));
  const audio = of('audio'), layer = of('layer'), graphic = of('graphic'), cue = of('cue');
  const clipSpan = (c: AudioClip | LayerClip) => ({ id: c.id, start: c.recordInTicks, end: c.recordInTicks + clipDuration(c) });
  const moveClip = <T extends AudioClip | LayerClip>(c: T, start: number): T => ({ ...c, recordInTicks: start });
  let next: SequenceDocument = { ...doc, audio: closeUpTrack(doc.audio, audio, clipSpan, moveClip) };
  if (doc.layers) next.layers = mapSame(doc.layers, (l) => { const clips = closeUpTrack(l.clips, layer, clipSpan, moveClip); return clips === l.clips ? l : { ...l, clips }; });
  if (doc.audioTracks) next.audioTracks = mapSame(doc.audioTracks, (t) => { const clips = closeUpTrack(t.clips, audio, clipSpan, moveClip); return clips === t.clips ? t : { ...t, clips }; });
  if (doc.graphics.some((g) => graphic.has(g.id))) {
    let graphics = doc.graphics;
    for (let k = 0; k < MAX_GRAPHIC_TRACKS; k++) {
      const on = graphics.filter((g) => graphicTrackOf(g) === k);
      const kept = closeUpTrack(on, graphic, (g) => ({ id: g.id, start: g.recordInTicks, end: g.recordInTicks + g.durationTicks }), (g, start) => ({ ...g, recordInTicks: start }));
      if (kept === on) continue;
      const byId = new Map(kept.map((g) => [g.id, g]));
      graphics = graphics.flatMap((g) => (graphicTrackOf(g) !== k ? [g] : byId.has(g.id) ? [byId.get(g.id)!] : []));
    }
    next.graphics = graphics;
  }
  if (doc.subtitles?.cues.some((c) => cue.has(c.id))) {
    next.subtitles = { ...doc.subtitles, cues: closeUpTrack(doc.subtitles.cues, cue, (c) => ({ id: c.id, start: c.startTicks, end: c.endTicks }), (c, start) => ({ ...c, startTicks: start, endTicks: start + (c.endTicks - c.startTicks) })) };
  }
  return removeOthers(next, items.filter((r) => r.kind === 'marker'));
}

/** The document unchanged when an op's result has the same tracks (no new object for a no-op). */
function sameTracks(a: SequenceDocument, b: SequenceDocument): boolean {
  return a.video === b.video && a.audio === b.audio && a.graphics === b.graphics && a.layers === b.layers && a.audioTracks === b.audioTracks && a.subtitles === b.subtitles && a.markers === b.markers;
}
const orSame = (doc: SequenceDocument, next: SequenceDocument) => (sameTracks(doc, next) ? doc : next);

/** Where each moved item on one freely placed track lands: at its wanted start, else the nearest free one (freeStart). */
function landOnTrack(taken: Span[], moves: Array<{ id: string; want: number; len: number }>): Map<string, number> {
  const out = new Map<string, number>();
  const busy = [...taken];
  for (const m of [...moves].sort((a, b) => a.want - b.want)) {
    const at = freeStart(busy, m.want, m.len) ?? Math.max(0, m.want);
    out.set(m.id, at);
    busy.push({ start: at, end: at + m.len });
  }
  return out;
}

/** Whether a track holding `taken` is free over every span of `want`. */
const freeOver = (taken: Span[], want: Span[]) => want.every((w) => taken.every((t) => w.end <= t.start || w.start >= t.end || t.end <= t.start || w.end <= w.start));


export const ops = {
  insertClip(doc: SequenceDocument, src: SourceRange, index = doc.video.length): SequenceDocument {
    const clip: VideoClip = {
      id: newId('v'), assetId: src.assetId, versionId: src.versionId, label: src.label,
      sourceInTicks: snap(src.sourceInTicks, doc.frameRate), sourceOutTicks: snap(src.sourceOutTicks, doc.frameRate),
      gainDb: 0, muted: false, fadeInTicks: 0, fadeOutTicks: 0, transitionIn: null, multicam: null,
    };
    if (defaultFit(src, doc) === 'fill') clip.fit = 'fill';
    if (clipDuration(clip) <= 0) return doc;
    const video = [...doc.video];
    video.splice(Math.max(0, Math.min(index, video.length)), 0, clip);
    return { ...doc, video };
  },

  moveClip(doc: SequenceDocument, from: number, to: number): SequenceDocument {
    if (from === to || !doc.video[from]) return doc;
    const video = [...doc.video];
    const [c] = video.splice(from, 1);
    video.splice(Math.max(0, Math.min(to, video.length)), 0, c!);
    return { ...doc, video };
  },

  /** Trim a storyline clip's in or out point by a frame-snapped delta, within source bounds. */
  trimClip(doc: SequenceDocument, id: string, edge: 'in' | 'out', deltaTicks: number, sourceDurationTicks: number): SequenceDocument {
    const tpf = ticksPerFrame(doc.frameRate);
    return {
      ...doc,
      video: doc.video.map((c) => {
        if (c.id !== id) return c;
        const d = snap(deltaTicks, doc.frameRate);
        if (edge === 'in') {
          const sourceInTicks = Math.min(Math.max(0, c.sourceInTicks + d), c.sourceOutTicks - tpf);
          return tidyFades({ ...c, sourceInTicks }, doc.frameRate);
        }
        const sourceOutTicks = Math.max(Math.min(sourceDurationTicks, c.sourceOutTicks + d), c.sourceInTicks + tpf);
        return tidyFades({ ...c, sourceOutTicks }, doc.frameRate);
      }),
    };
  },

  /**
   * Split the storyline clip under `atTicks` into two clips at the nearest frame. 1.7.4: `scope` says what to split
   * (SplitScope; 'v1', the default, is this behaviour, unchanged):
   *  - `{ items }`: those clips and graphics (any track) that are under the point; the others in the list (not under
   *    it, cues, markers, gaps) are ignored. S splits the selection this way (or 'v1' with nothing selected).
   *  - `{ tracks }`: every clip or graphic under the point on those tracks (TrackRef). ⌘K and Blade ⇧-click use
   *    splitAll (every track).
   * On every track: an item is split only where start < at < end (snapped; at an edge nothing happens). The left part
   * keeps its id; the right part gets a new id (newId 'v' V1, 'p' layer, 'a' audio, 'g' graphic). Both keep every
   * framing, motion and volume key (keys are in source time, so level and picture are continuous across the cut).
   * Sound and picture fades at the cut are cleared (left: fade out, right: fade in), as V1 has always done; the right
   * part has no transitionIn, crossfadeInTicks or gapBeforeTicks. A graphic splits into two instances with the same
   * template, version and data: the left keeps durationTicks = at − start and the step marks before it; the right
   * starts at `at` with the rest, its step marks and updates shifted by the left's length (updates before the cut are
   * merged into its data, so it shows what was showing). Link groups: left parts keep their linkId; when the split made
   * two or more right parts of one group, they share a new linkId (newId('lk')); a lone right part has none (tidyLinks).
   * Unchanged (the same document) when nothing is under the point.
   */
  splitAt(doc: SequenceDocument, atTicks: number, scope: SplitScope = 'v1'): SequenceDocument {
    const rate = doc.frameRate;
    const at = snap(atTicks, rate);
    /** The storyline clip under the point split in two (the 1.7.3 behaviour), with the right half's id; null when none. */
    const splitV1 = (d: SequenceDocument, only?: ReadonlySet<string>): { doc: SequenceDocument; right: VideoClip } | null => {
      const hit = resolveAt(d, at);
      if (!hit || (only && !only.has(hit.placed.clip.id))) return null;
      const { placed, sourceTicks } = hit;
      if (sourceTicks <= placed.clip.sourceInTicks || sourceTicks >= placed.clip.sourceOutTicks) return null;
      // Both halves keep every framing key (1.7.0): keys are in source time, so the picture is continuous across the cut.
      const left: VideoClip = { ...placed.clip, sourceOutTicks: sourceTicks, fadeOutTicks: 0 };
      const right: VideoClip = { ...structuredClone(placed.clip), id: newId('v'), sourceInTicks: sourceTicks, fadeInTicks: 0, transitionIn: null };
      // The fade out stays at the end and the fade in at the start (picture as sound), each within its half.
      delete left.videoFadeOutTicks;
      delete right.videoFadeInTicks;
      delete right.gapBeforeTicks; // the gap stays before the left half
      delete right.crossfadeInTicks; delete right.crossfadeAlign; // 1.7.4: the crossfade stays at the left half's start

      const tidy = (c: VideoClip) => ('videoFadeInTicks' in c || 'videoFadeOutTicks' in c ? tidyFades(c, rate) : c);
      const video = [...d.video];
      video.splice(placed.index, 1, tidy(left), tidy(right));
      return { doc: { ...d, video }, right };
    };
    if (scope === 'v1') return splitV1(doc)?.doc ?? doc;

    // 1.7.4 scopes: what is in scope, by item key or by track key.
    const items = 'items' in scope ? new Set(scope.items.map(itemKey)) : null;
    const tracks = 'tracks' in scope ? new Set(scope.tracks.map(trackKey)) : null;
    const inScope = (r: ItemRef, track: string) => (items ? items.has(itemKey(r)) : tracks!.has(track));
    const rights: Array<{ ref: ItemRef; linkId?: string }> = [];
    let d = doc;
    // V1: the clip under the point (inside a transition, the incoming one).
    const v1Hit = resolveAt(d, at)?.placed.clip.id;
    if (v1Hit && inScope({ kind: 'video', id: v1Hit }, 'v1')) {
      const r = splitV1(d);
      if (r) { d = r.doc; rights.push({ ref: { kind: 'video', id: r.right.id }, linkId: r.right.linkId }); }
    }
    // Free tracks: a clip with start < at < end.
    const splitClips = <T extends LayerClip | AudioClip>(clips: T[], kind: 'layer' | 'audio', track: string, prefix: string): T[] => {
      const hit = clips.filter((c) => c.recordInTicks < at && at < c.recordInTicks + clipDuration(c) && inScope({ kind, id: c.id }, track));
      if (!hit.length) return clips;
      return clips.flatMap((c) => {
        if (!hit.includes(c)) return [c];
        const parts = cutFree(c, at, at, prefix, rate);
        if (parts[1]) rights.push({ ref: { kind, id: parts[1].id }, linkId: parts[1].linkId });
        return parts;
      });
    };
    if (d.layers?.length) {
      const layers = mapSame(d.layers, (l) => { const clips = splitClips(l.clips, 'layer', `layer:${l.id}`, 'p'); return clips === l.clips ? l : { ...l, clips }; });
      if (layers !== d.layers) d = { ...d, layers };
    }
    for (const t of allAudioTracks(d)) {
      const clips = splitClips(t.clips, 'audio', `audio:${t.id}`, 'a');
      if (clips !== t.clips) d = withAudioClips(d, t.id, clips);
    }
    // Graphics: two instances with the same data.
    const gHit = d.graphics.filter((g) => g.recordInTicks < at && at < g.recordInTicks + g.durationTicks && inScope({ kind: 'graphic', id: g.id }, `graphic:${graphicTrackOf(g)}`));
    if (gHit.length) {
      d = {
        ...d,
        graphics: d.graphics.flatMap((g) => {
          if (!gHit.includes(g)) return [g];
          const cut = at - g.recordInTicks;
          const left = tidyGraphic({ ...g, durationTicks: cut }, rate);
          const right = { ...graphicTail(structuredClone(g), cut, rate), id: newId('g') };
          rights.push({ ref: { kind: 'graphic', id: right.id }, linkId: right.linkId });
          return [left, right];
        }),
      };
    }
    if (!rights.length) return doc;
    // Link groups: the right parts of one group share a new link; a lone right part has none.
    const byGroup = new Map<string, string[]>();
    for (const r of rights) if (r.linkId) byGroup.set(r.linkId, [...(byGroup.get(r.linkId) ?? []), r.ref.id]);
    for (const ids of byGroup.values()) d = setLinkId(d, new Set(ids), ids.length >= 2 ? newId('lk') : null);
    return tidyLinks(d);
  },

  /** Close the gap before a storyline clip (1.0.0-rc.7): it and the clips after it move up to the clip before. */
  closeGap(doc: SequenceDocument, id: string): SequenceDocument {
    if (!doc.video.some((c) => c.id === id && c.gapBeforeTicks)) return doc;
    return { ...doc, video: doc.video.map((c) => { if (c.id !== id) return c; const next = { ...c, transitionIn: null }; delete next.gapBeforeTicks; return next; }) };
  },

  removeClip(doc: SequenceDocument, id: string): SequenceDocument {
    const next: SequenceDocument = {
      ...doc,
      video: doc.video.filter((c) => c.id !== id),
      audio: doc.audio.filter((c) => c.id !== id),
      graphics: doc.graphics.filter((g) => g.id !== id),
      layers: (doc.layers ?? []).map((l) => (l.clips.some((c) => c.id === id) ? { ...l, clips: l.clips.filter((c) => c.id !== id) } : l)),
    };
    // 1.7.3: A3–A8 too.
    if (doc.audioTracks?.some((t) => t.clips.some((c) => c.id === id))) next.audioTracks = doc.audioTracks.map((t) => ({ ...t, clips: t.clips.filter((c) => c.id !== id) }));
    return next;
  },

  updateClip(doc: SequenceDocument, id: string, patch: Partial<Pick<VideoClip, 'gainDb' | 'muted' | 'label'>> & ClipFadePatch): SequenceDocument {
    const fix = <T extends VideoClip | AudioClip | LayerClip>(c: T): T => {
      if (c.id !== id) return c;
      const next = { ...c, ...patch };
      // Audio clips (A2, 1.7.3 A3–A8) have no picture: no picture fades on them.
      if (audioTrackOf(doc, id)) { delete next.videoFadeInTicks; delete next.videoFadeOutTicks; }
      return tidyFades(next, doc.frameRate);
    };
    const out: SequenceDocument = { ...doc, video: doc.video.map(fix), audio: doc.audio.map(fix), layers: (doc.layers ?? []).map((l) => ({ ...l, clips: l.clips.map(fix) })) };
    if (doc.audioTracks?.some((t) => t.clips.some((c) => c.id === id))) out.audioTracks = doc.audioTracks.map((t) => ({ ...t, clips: t.clips.map(fix) }));
    return out;
  },

  /**
   * Set, change or remove (null) the transition into a storyline clip from the one before it.
   *
   * 1.8.0 (transitionBorrow): a transition of length d uses the clips' extra media, so the cut and the sequence length
   * stay. The outgoing clip's Out moves b later (its tail handle: `durations`, by version id, for the source's length)
   * and the incoming clip's In a earlier (its head handle), a = b = d/2 where the media allows; where one side has less
   * the other gives more, so the transition sits off centre, toward the media. Only d − a − b (what neither side has)
   * still overlaps and shortens the sequence. A still always stretches; a multicam clip within its angle; a source of
   * unknown length (not in `durations`) gives only its head side. The borrow is kept as headTicks (a) and tailTicks (b):
   * removing or shortening the transition gives it back, lengthening borrows more by the same rule. A transition with
   * neither field (every one made before 1.8.0, or one that borrowed nothing) keeps its overlap exactly when changed,
   * and a new one set without `durations` at all overlaps as before 1.8.0 (API callers; the editor always passes them).
   * The layout (layoutVideo) is unchanged: the overlap of the two longer clips is what keeps the cut in place.
   */
  setTransition(doc: SequenceDocument, id: string, transition: Transition | null, durations?: SourceDurations): SequenceDocument {
    const i = doc.video.findIndex((c) => c.id === id);
    if (i <= 0) return doc;
    const rate = doc.frameRate;
    const tpf = ticksPerFrame(rate);
    const old = doc.video[i]!.transitionIn;
    // Give back what the transition borrowed, so the limits and the borrow below start from the clips as edited.
    let prev = doc.video[i - 1]!;
    let clip = doc.video[i]!;
    if (old?.tailTicks) prev = tidyFades({ ...prev, sourceOutTicks: prev.sourceOutTicks - old.tailTicks }, rate);
    if (old?.headTicks) clip = tidyFades({ ...clip, sourceInTicks: clip.sourceInTicks + old.headTicks }, rate);
    const max = maxTransitionTicks(prev, clip, rate);
    const d = transition && transition.durationTicks > 0 && max > 0 ? Math.min(max, Math.max(tpf, snap(transition.durationTicks, rate))) : 0;
    const write = (p: VideoClip, c: VideoClip) => ({ ...doc, video: doc.video.map((x, j) => (j === i - 1 ? p : j === i ? c : x)) });
    if (!d) return write(prev, { ...clip, transitionIn: null });
    // A transition from before 1.8.0 (no borrow record) keeps its overlap, and so does a new one set without source
    // lengths (callers as before 1.8.0: the API, the layout's own tests).
    if (old ? !old.headTicks && !old.tailTicks : !durations) return write(prev, { ...clip, transitionIn: { kind: transition!.kind, durationTicks: d } });
    const still = durations?.get(clip.versionId) === Infinity;
    const head = still ? Infinity : wholeFrames(Math.max(0, clip.sourceInTicks), tpf);
    // The tail handle: from the source's length; of unknown length, only what this transition had borrowed (given back above).
    const known = durations && (durations.has(prev.versionId) || prev.multicam);
    const tail = known ? wholeFrames(Math.min(Number.MAX_SAFE_INTEGER, tailHandle(doc, prev, durations)), tpf) : old?.tailTicks ?? 0;
    const half = Math.floor(d / 2 / tpf + 1e-9) * tpf;
    let a = Math.min(head, half);
    const b = Math.min(tail, d - a);
    a = Math.min(head, d - b);
    // A still with too little before its In: its source window moves later first (the picture is the same throughout).
    if (still && clip.sourceInTicks < a) clip = shiftSource(clip, a - clip.sourceInTicks);
    const t: Transition = { kind: transition!.kind, durationTicks: d, ...(a > 0 ? { headTicks: a } : {}), ...(b > 0 ? { tailTicks: b } : {}) };
    if (b > 0) prev = tidyFades({ ...prev, sourceOutTicks: prev.sourceOutTicks + b }, rate);
    return write(prev, { ...tidyFades({ ...clip, sourceInTicks: clip.sourceInTicks - a }, rate), transitionIn: t });
  },

  addLayer(doc: SequenceDocument, label?: string): SequenceDocument {
    const layers = doc.layers ?? [];
    if (layers.length >= MAX_LAYERS) return doc;
    return { ...doc, layers: [...layers, { id: newId('l'), label: label ?? `V${layers.length + 2}`, hidden: false, clips: [] }] };
  },

  updateLayer(doc: SequenceDocument, layerId: string, patch: Partial<Pick<VideoLayer, 'label' | 'hidden'>>): SequenceDocument {
    return { ...doc, layers: (doc.layers ?? []).map((l) => (l.id === layerId ? { ...l, ...patch } : l)) };
  },

  removeLayer(doc: SequenceDocument, layerId: string): SequenceDocument {
    const next = { ...doc, layers: (doc.layers ?? []).filter((l) => l.id !== layerId) };
    return doc.trackAudio?.[layerId] ? ops.setTrackAudio(next, layerId, { mute: false, solo: false }) : next;
  },

  /** Put a clip on a video layer at a sequence position (a new top layer when none is given). */
  /**
   * A clip on a video track above V1: on `layerId`, or a new layer; at the nearest free time there. 1.8.0: `opts.soundTo`
   * (LayerSoundTo) says where its sound goes: 'clip' (the default, as before), 'none' (muted, as the rough cut's
   * cutaways), or 'track' (the editor): onto an audio track, linked, the clip muted (moveSoundToTrack; a source known
   * to be silent, src.hasAudio false, or no room on any audio track keeps it in the clip).
   */
  addLayerClip(doc: SequenceDocument, src: SourceRange, recordInTicks: number, layerId?: string, opts: { soundTo?: LayerSoundTo } = {}): SequenceDocument {
    const d = ops.placeLayerClip(doc, src, recordInTicks, layerId);
    if (d === doc || !opts.soundTo || opts.soundTo === 'clip') return d;
    const placed = d.layers.flatMap((l) => l.clips).find((c) => !(doc.layers ?? []).some((l) => l.clips.some((x) => x.id === c.id)));
    if (!placed) return d;
    if (opts.soundTo === 'none') return ops.updateClip(d, placed.id, { muted: true });
    return src.hasAudio === false ? d : ops.moveSoundToTrack(d, placed.id).doc;
  },

  /**
   * "Move sound to an audio track" (1.8.0): a video-track clip's own sound (not muted) becomes a linked audio clip on
   * an audio track (soundToAudioTrack: the matching track, the next free one below, a new one up to A8) and the clip is
   * muted. `moved` is the new audio clip; with no room the document is unchanged and `notes` says so (NO_AUDIO_TRACK).
   */
  moveSoundToTrack(doc: SequenceDocument, clipId: string): TrackMoveResult {
    const layer = (doc.layers ?? []).find((l) => l.clips.some((c) => c.id === clipId));
    const lc = layer?.clips.find((c) => c.id === clipId);
    if (!layer || !lc || lc.muted) return { doc, moved: [], notes: [] };
    const r = soundToAudioTrack(doc, layer.id, clipId);
    return r.sound ? { doc: r.doc, moved: [r.sound], notes: [] } : { doc, moved: [], notes: [NO_AUDIO_TRACK] };
  },

  /** addLayerClip's placement (before 1.8.0 all of it): the clip with its own sound, on the layer, at the nearest free time. */
  placeLayerClip(doc: SequenceDocument, src: SourceRange, recordInTicks: number, layerId?: string): SequenceDocument {
    let d = doc;
    let target = layerId ? (d.layers ?? []).find((l) => l.id === layerId) : undefined;
    if (!target) {
      d = ops.addLayer(d);
      target = d.layers.at(-1);
      if (!target || target.clips.length) return doc;
    }
    const clip: LayerClip = {
      id: newId('p'), assetId: src.assetId, versionId: src.versionId, label: src.label,
      sourceInTicks: snap(src.sourceInTicks, d.frameRate), sourceOutTicks: snap(src.sourceOutTicks, d.frameRate),
      recordInTicks: snap(Math.max(0, recordInTicks), d.frameRate), gainDb: 0, muted: false, fadeInTicks: 0, fadeOutTicks: 0,
      opacity: 1, scale: 1, x: 0, y: 0,
    };
    if (defaultFit(src, d) === 'fill') clip.fit = 'fill';
    if (clipDuration(clip) <= 0) return doc;
    // No overlaps on a layer (0.96.0): the nearest free position.
    const at = freeStart(spans(target.clips), clip.recordInTicks, clipDuration(clip));
    if (at === null) return doc;
    clip.recordInTicks = at;
    const id = target.id;
    return { ...d, layers: d.layers.map((l) => (l.id === id ? { ...l, clips: [...l.clips, clip] } : l)) };
  },

  /** Move a layer clip in time and, optionally, to another layer. */
  moveLayerClip(doc: SequenceDocument, id: string, recordInTicks: number, toLayerId?: string): SequenceDocument {
    const from = (doc.layers ?? []).find((l) => l.clips.some((c) => c.id === id));
    if (!from) return doc;
    const clip = { ...from.clips.find((c) => c.id === id)!, recordInTicks: snap(Math.max(0, recordInTicks), doc.frameRate) };
    const dest = toLayerId && doc.layers.some((l) => l.id === toLayerId) ? toLayerId : from.id;
    // No overlaps on a layer (0.96.0): the nearest free position on the destination layer.
    const at = freeStart(spans(doc.layers.find((l) => l.id === dest)!.clips, id), clip.recordInTicks, clipDuration(clip));
    if (at === null) return doc;
    clip.recordInTicks = at;
    return {
      ...doc,
      layers: doc.layers.map((l) => {
        const clips = l.clips.filter((c) => c.id !== id);
        return l.id === dest ? { ...l, clips: [...clips, clip] } : l.id === from.id ? { ...l, clips } : l;
      }),
    };
  },

  /** Trim a layer clip's in or out point; trimming the in point keeps the out point where it is in the sequence. */
  trimLayerClip(doc: SequenceDocument, id: string, edge: 'in' | 'out', deltaTicks: number, sourceDurationTicks: number): SequenceDocument {
    // Not into a neighbour on the layer (0.96.0); 1.7.3: the rules are trimOnTrack's, shared with trimAudio.
    return { ...doc, layers: (doc.layers ?? []).map((l) => ({ ...l, clips: trimOnTrack(l.clips, id, edge, deltaTicks, sourceDurationTicks, doc.frameRate) })) };
  },

  addMulticam(doc: SequenceDocument, group: Omit<MulticamGroup, 'id'>): SequenceDocument {
    return { ...doc, multicam: [...(doc.multicam ?? []), { ...group, id: newId('m') }] };
  },

  /**
   * Cut a multicam group into the storyline: one clip per cut, on the angle
   * chosen from that moment, between `fromTicks` and `toTicks` on the group's
   * timeline, inserted at `index`. The storyline clips are muted and one
   * angle's sound is laid on A2 for the whole edit (or, with audio 'follow',
   * each clip keeps its own camera's sound).
   */
  cutMulticam(doc: SequenceDocument, groupId: string, cuts: MulticamCut[], fromTicks: number, toTicks: number, opts: { index?: number; audio?: number | 'follow' } = {}): SequenceDocument {
    const g = (doc.multicam ?? []).find((x) => x.id === groupId);
    if (!g) return doc;
    const from = snap(Math.max(0, fromTicks), doc.frameRate);
    const to = snap(Math.min(toTicks, multicamDuration(g)), doc.frameRate);
    if (to <= from) return doc;
    const sorted = [...cuts].map((c) => ({ ...c, atTicks: snap(c.atTicks, doc.frameRate) })).sort((a, b) => a.atTicks - b.atTicks);
    // The angle at `from` is the last cut at or before it (or the first cut's angle).
    const points = [{ atTicks: from, angle: [...sorted].reverse().find((c) => c.atTicks <= from)?.angle ?? sorted[0]?.angle ?? 0 }, ...sorted.filter((c) => c.atTicks > from && c.atTicks < to)];
    const clips: VideoClip[] = [];
    points.forEach((pt, k) => {
      const end = points[k + 1]?.atTicks ?? to;
      const a = g.angles[pt.angle];
      if (!a || end <= pt.atTicks) return;
      // Where the chosen angle has no picture, keep the section on the edit's previous angle, or drop it.
      const inTicks = Math.max(pt.atTicks, a.startTicks);
      const outTicks = Math.min(end, a.startTicks + a.durationTicks);
      if (outTicks <= inTicks) return;
      const prev = clips.at(-1);
      if (prev && prev.multicam?.angle === pt.angle && prev.sourceOutTicks + a.startTicks === inTicks) { prev.sourceOutTicks += outTicks - inTicks; return; }
      clips.push({
        id: newId('v'), assetId: a.assetId, versionId: a.versionId, label: a.label,
        sourceInTicks: inTicks - a.startTicks, sourceOutTicks: outTicks - a.startTicks,
        gainDb: 0, muted: opts.audio !== 'follow', fadeInTicks: 0, fadeOutTicks: 0, transitionIn: null, multicam: { groupId, angle: pt.angle },
      });
    });
    if (!clips.length) return doc;
    const index = Math.max(0, Math.min(opts.index ?? doc.video.length, doc.video.length));
    const video = [...doc.video];
    video.splice(index, 0, ...clips);
    let next: SequenceDocument = { ...doc, video };
    const audioAngle = opts.audio === 'follow' ? null : g.angles[opts.audio ?? 0];
    if (audioAngle) {
      const recordIn = layoutVideo(next)[index]!.recordIn;
      const start = Math.max(from, audioAngle.startTicks);
      const end = Math.min(to, audioAngle.startTicks + audioAngle.durationTicks);
      if (end > start) {
        const before = next;
        const src = {
          assetId: audioAngle.assetId, versionId: audioAngle.versionId, label: `${g.label} sound (${audioAngle.label})`,
          sourceInTicks: start - audioAngle.startTicks, sourceOutTicks: end - audioAngle.startTicks, sourceDurationTicks: audioAngle.durationTicks,
        };
        // On A2, else (1.7.3) the first of A3–A8 that is free there (no new track is made).
        let trackId: string | null = null;
        for (const t of allAudioTracks(before)) {
          next = ops.addAudio(before, src, recordIn + (start - from), { exact: true, trackId: t.id });
          if (next !== before) { trackId = t.id; break; }
        }
        if (!trackId) {
          // Every audio track is in use there (no overlaps, 0.96.0): each clip keeps its own camera's sound instead.
          const ids = new Set(clips.map((c) => c.id));
          next = { ...before, video: before.video.map((c) => (ids.has(c.id) ? { ...c, muted: false } : c)) };
        } else audioClipsOn(next, trackId)!.at(-1)!.gainDb = 0;
      }
    }
    return next;
  },

  /** Show another angle of a multicam group for a storyline clip, at the same moment and length. */
  switchAngle(doc: SequenceDocument, clipId: string, angle: number): SequenceDocument {
    const clip = doc.video.find((c) => c.id === clipId);
    const g = clip?.multicam && (doc.multicam ?? []).find((x) => x.id === clip.multicam!.groupId);
    if (!clip || !g || !clip.multicam || clip.multicam.angle === angle) return doc;
    const from = g.angles[clip.multicam.angle]!.startTicks + clip.sourceInTicks;
    const to = from + clipDuration(clip);
    if (!angleCovers(g, angle, from, to)) return doc;
    const a = g.angles[angle]!;
    // Framing keys are in source time (1.7.0): they move with the change of the source in point, so they stay on the
    // same moment of the edit.
    const delta = from - a.startTicks - clip.sourceInTicks;
    const shift = <T extends { keys?: FramingKey[] }>(f: T): T => {
      if (!f.keys?.length || !delta) return f;
      const n = { ...f, keys: shiftFramingKeys(f.keys, delta) };
      if (!n.keys?.length) delete n.keys;
      return n;
    };
    return {
      ...doc,
      video: doc.video.map((c) => {
        if (c.id !== clipId) return c;
        const next: VideoClip = shift({
          ...c, assetId: a.assetId, versionId: a.versionId, label: a.label,
          sourceInTicks: from - a.startTicks, sourceOutTicks: to - a.startTicks, multicam: { groupId: g.id, angle },
        });
        if (next.formatFraming && delta) next.formatFraming = Object.fromEntries(Object.entries(next.formatFraming).map(([k, v]) => [k, shift(v)]));
        return next;
      }),
    };
  },

  removeMulticam(doc: SequenceDocument, groupId: string): SequenceDocument {
    return {
      ...doc,
      multicam: (doc.multicam ?? []).filter((g) => g.id !== groupId),
      video: doc.video.map((c) => (c.multicam?.groupId === groupId ? { ...c, multicam: null } : c)),
    };
  },

  updateLayerClip(doc: SequenceDocument, id: string, patch: Partial<Pick<LayerClip, 'opacity' | 'scale' | 'x' | 'y'>>): SequenceDocument {
    const clampN = (v: number | undefined, lo: number, hi: number, fallback: number) => (v === undefined || !Number.isFinite(v) ? fallback : Math.min(hi, Math.max(lo, v)));
    return {
      ...doc,
      layers: (doc.layers ?? []).map((l) => ({
        ...l,
        clips: l.clips.map((c) => (c.id === id ? {
          ...c,
          opacity: clampN(patch.opacity, 0, 1, c.opacity), scale: clampN(patch.scale, 0.05, 4, c.scale),
          x: clampN(patch.x, -1, 1, c.x), y: clampN(patch.y, -1, 1, c.y),
        } : c)),
      })),
    };
  },

  /**
   * Put a clip on A2 (1.7.3: or on the audio track `opts.trackId`, 'a2' or an audioTracks id; an unknown track:
   * unchanged); where it would overlap another, at the nearest free position (with `exact`, not at all).
   */
  addAudio(doc: SequenceDocument, src: SourceRange, recordInTicks: number, opts: { exact?: boolean; trackId?: string } = {}): SequenceDocument {
    const clip: AudioClip = {
      id: newId('a'), assetId: src.assetId, versionId: src.versionId, label: src.label,
      sourceInTicks: snap(src.sourceInTicks, doc.frameRate), sourceOutTicks: snap(src.sourceOutTicks, doc.frameRate),
      recordInTicks: snap(Math.max(0, recordInTicks), doc.frameRate), gainDb: -6, muted: false, fadeInTicks: 0, fadeOutTicks: 0,
    };
    if (clipDuration(clip) <= 0) return doc;
    const trackId = opts.trackId ?? A2_TRACK;
    const clips = audioClipsOn(doc, trackId);
    if (!clips) return doc;
    const at = freeStart(spans(clips), clip.recordInTicks, clipDuration(clip));
    if (at === null || (opts.exact && at !== clip.recordInTicks)) return doc;
    clip.recordInTicks = at;
    return withAudioClips(doc, trackId, [...clips, clip]);
  },

  /**
   * Move an audio clip (A2; 1.7.3 any audio track) and, with `toTrackId`, to another audio track ('a2' or an
   * audioTracks id; unknown: its own); where it would overlap another, to the nearest free position.
   */
  moveAudio(doc: SequenceDocument, id: string, recordInTicks: number, toTrackId?: string): SequenceDocument {
    const from = audioTrackOf(doc, id);
    if (!from) return doc;
    const c = audioClipsOn(doc, from)!.find((a) => a.id === id)!;
    const dest = toTrackId && audioClipsOn(doc, toTrackId) ? toTrackId : from;
    const at = freeStart(spans(audioClipsOn(doc, dest)!, id), snap(Math.max(0, recordInTicks), doc.frameRate), clipDuration(c));
    if (at === null || (at === c.recordInTicks && dest === from)) return doc;
    if (dest === from) return tidyAudioCrossfades(doc, withAudioClips(doc, from, audioClipsOn(doc, from)!.map((x) => (x.id === id ? { ...x, recordInTicks: at } : x))));
    const d = withAudioClips(doc, from, audioClipsOn(doc, from)!.filter((x) => x.id !== id));
    return tidyAudioCrossfades(doc, withAudioClips(d, dest, [...audioClipsOn(d, dest)!, { ...c, recordInTicks: at }]));
  },

  /** Colour-correct a storyline or layer clip (0.96.0); a neutral correction (or null) removes it. */
  setColor(doc: SequenceDocument, id: string, color: Partial<ColorCorrection> | null): SequenceDocument {
    const c = tidyColor(color);
    const fix = <T extends VideoClip | LayerClip>(clip: T): T => {
      if (clip.id !== id) return clip;
      const next = { ...clip };
      if (c) next.color = c; else delete next.color;
      return next;
    };
    if (!doc.video.some((v) => v.id === id) && !(doc.layers ?? []).some((l) => l.clips.some((x) => x.id === id))) return doc;
    return { ...doc, video: doc.video.map(fix), layers: (doc.layers ?? []).map((l) => (l.clips.some((x) => x.id === id) ? { ...l, clips: l.clips.map(fix) } : l)) };
  },

  /** Fit or Fill a storyline or layer clip, and where a Fill crop sits (1.0.0-rc.11); 1.7.0 Blur (storyline) and the zoom. */
  setFraming(doc: SequenceDocument, id: string, patch: FramingPatch): SequenceDocument {
    const layer = isLayerClip(doc, id);
    const fix = <T extends VideoClip | LayerClip>(clip: T): T => (clip.id === id ? withFraming(clip, patch, !layer) : clip);
    if (!doc.video.some((v) => v.id === id) && !(doc.layers ?? []).some((l) => l.clips.some((x) => x.id === id))) return doc;
    return { ...doc, video: doc.video.map(fix), layers: (doc.layers ?? []).map((l) => (l.clips.some((x) => x.id === id) ? { ...l, clips: l.clips.map(fix) } : l)) };
  },

  /* ------------------------------------------------ markers (0.96.0) */

  addMarker(doc: SequenceDocument, atTicks: number, label?: string, id = newId('k')): SequenceDocument {
    const markers = doc.markers ?? [];
    if (markers.length >= MAX_MARKERS) return doc;
    const at = snap(Math.max(0, atTicks), doc.frameRate);
    return withMarkers(doc, [...markers, { id, atTicks: at, label: (label ?? `Marker ${markers.length + 1}`).slice(0, 200) }]);
  },

  updateMarker(doc: SequenceDocument, id: string, patch: Partial<Pick<Marker, 'label' | 'atTicks' | 'color'>>): SequenceDocument {
    if (!(doc.markers ?? []).some((m) => m.id === id)) return doc;
    return withMarkers(doc, doc.markers!.map((m) => {
      if (m.id !== id) return m;
      const next: Marker = { ...m, ...patch, label: (patch.label ?? m.label).slice(0, 200), atTicks: snap(Math.max(0, patch.atTicks ?? m.atTicks), doc.frameRate) };
      if (!next.color) delete next.color;
      return next;
    }));
  },

  removeMarker(doc: SequenceDocument, id: string): SequenceDocument {
    if (!(doc.markers ?? []).some((m) => m.id === id)) return doc;
    return withMarkers(doc, doc.markers!.filter((m) => m.id !== id));
  },

  /* ------------------------------------------------ track sound (0.96.0) */

  /**
   * Mute or solo a sound track ('a1', 'a2' or a layer id). Unset flags are left out, and so is an empty map. 1.7.4: a
   * track's gainDb is kept (and may be patched too: 0 leaves it out; setTrackGain is the op for it).
   */
  setTrackAudio(doc: SequenceDocument, track: string, patch: TrackAudio): SequenceDocument {
    const all = { ...(doc.trackAudio ?? {}) };
    const cur: TrackAudio = { ...all[track], ...patch };
    const tidy: TrackAudio = {};
    if (cur.mute) tidy.mute = true;
    if (cur.solo) tidy.solo = true;
    const gain = cur.gainDb === undefined || !Number.isFinite(cur.gainDb) ? 0 : Math.round(Math.min(MAX_DB, Math.max(MIN_DB, cur.gainDb)) * 10) / 10;
    if (gain) tidy.gainDb = gain;
    if (Object.keys(tidy).length) all[track] = tidy; else delete all[track];
    const next = { ...doc };
    if (Object.keys(all).length) next.trackAudio = all; else delete next.trackAudio;
    return next;
  },

  /**
   * Add a graphic on its track (`track`, GFX 1 when left out). 1.7.3: graphics on one track never overlap: where it
   * would, it goes to the nearest free position on that track (freeStart).
   */
  addGraphic(doc: SequenceDocument, g: Omit<GraphicInstance, 'id'>): SequenceDocument {
    const recordInTicks = snap(g.recordInTicks, doc.frameRate);
    const durationTicks = snap(g.durationTicks, doc.frameRate);
    const at = freeStart(graphicSpans(doc, graphicTrackOf(g)), recordInTicks, durationTicks) ?? recordInTicks;
    return { ...doc, graphics: [...doc.graphics, { ...g, id: newId('g'), recordInTicks: at, durationTicks }] };
  },

  /**
   * Change a graphic (its data merged). 1.7.3, per graphics track: a longer graphic stops at the next graphic on its
   * track (one it already overlapped is left as it is); a graphic moved in time or to another track (`track`) goes to
   * the nearest free position there (freeStart). A change that leaves the timing alone does not move it.
   */
  updateGraphic(doc: SequenceDocument, id: string, patch: Partial<Omit<GraphicInstance, 'id'>>): SequenceDocument {
    return {
      ...doc,
      graphics: doc.graphics.map((g) => {
        if (g.id !== id) return g;
        const next: GraphicInstance = {
          ...g, ...patch,
          recordInTicks: snap(Math.max(0, patch.recordInTicks ?? g.recordInTicks), doc.frameRate),
          durationTicks: Math.max(ticksPerFrame(doc.frameRate), snap(patch.durationTicks ?? g.durationTicks, doc.frameRate)),
          data: { ...g.data, ...patch.data },
        };
        if ('track' in patch && !next.track) delete next.track;
        const track = graphicTrackOf(next);
        if (next.recordInTicks !== g.recordInTicks || track !== graphicTrackOf(g)) {
          next.recordInTicks = freeStart(graphicSpans(doc, track, id), next.recordInTicks, next.durationTicks) ?? next.recordInTicks;
        } else if (next.durationTicks > g.durationTicks) {
          const ceiling = Math.min(Number.MAX_SAFE_INTEGER, ...graphicSpans(doc, track, id).filter((s) => s.start >= g.recordInTicks + g.durationTicks).map((s) => s.start));
          next.durationTicks = Math.max(g.durationTicks, Math.min(next.durationTicks, ceiling - next.recordInTicks));
        }
        return tidyGraphic(next, doc.frameRate);
      }),
    };
  },

  /**
   * Point a graphic at another version of its template with new data (the OGraf "Update to vN", 0.93.0): unlike
   * updateGraphic, `data` (and `updates`, when given) replace what the clip had instead of being merged.
   */
  retargetGraphic(doc: SequenceDocument, id: string, next: Pick<GraphicInstance, 'templateVersion' | 'data'> & Partial<Pick<GraphicInstance, 'updates'>>): SequenceDocument {
    return {
      ...doc,
      graphics: doc.graphics.map((g) => (g.id === id ? tidyGraphic({ ...g, templateVersion: next.templateVersion, data: { ...next.data }, ...(next.updates ? { updates: next.updates } : {}) }, doc.frameRate) : g)),
    };
  },

  /** OGraf step marks (0.93.0): ticks from the clip start; snapped, inside the clip, ascending, left out when empty. */
  setStepMarks(doc: SequenceDocument, id: string, marks: number[]): SequenceDocument {
    return { ...doc, graphics: doc.graphics.map((g) => (g.id === id ? tidyGraphic({ ...g, stepMarks: marks }, doc.frameRate) : g)) };
  },

  /** OGraf updates (0.93.0): partial data at moments of the clip; sorted, inside the clip, left out when empty. */
  setGraphicUpdates(doc: SequenceDocument, id: string, updates: NonNullable<GraphicInstance['updates']>): SequenceDocument {
    return { ...doc, graphics: doc.graphics.map((g) => (g.id === id ? tidyGraphic({ ...g, updates }, doc.frameRate) : g)) };
  },

  /* ------------------------------------------------ formats (1.7.0, docs/dev/formats-1.7.0-build.md) */

  /** Add a format (unchanged when formatProblem objects: a bad size, a shape the sequence has, more than MAX_FORMATS). */
  addFormat(doc: SequenceDocument, f: { width: number; height: number; label?: string; subtitleBottom?: number; fit?: FitMode }, id?: string): SequenceDocument {
    if (formatProblem(doc, f)) return doc;
    const fid = id && id !== PRIMARY_FORMAT && !(doc.formats ?? []).some((x) => x.id === id) ? id : newFormatId(doc, f);
    const format: SequenceFormat = { id: fid, width: f.width, height: f.height };
    if (f.label?.trim()) format.label = f.label.trim().slice(0, 60);
    if (f.subtitleBottom !== undefined) format.subtitleBottom = Math.min(0.5, Math.max(0.02, f.subtitleBottom));
    if (f.fit) format.fit = f.fit;
    return { ...doc, formats: [...(doc.formats ?? []), format] };
  },

  /**
   * Rename, resize or set the subtitle height or (1.7.1) the framing for the whole storyline of a format ('primary': its
   * size, as setPrimarySize, its subtitle height and its fit). null clears the label, the subtitle height or the fit
   * (per clip). Unchanged when the new size is refused (formatProblem), and when nothing changes.
   */
  updateFormat(doc: SequenceDocument, id: FormatRef, patch: { width?: number; height?: number; label?: string | null; subtitleBottom?: number | null; fit?: FitMode | null }): SequenceDocument {
    const sb = (v: number) => Math.min(0.5, Math.max(0.02, v));
    if (id === PRIMARY_FORMAT) {
      let next = doc;
      const w = patch.width ?? doc.width, h = patch.height ?? doc.height;
      if (w !== doc.width || h !== doc.height) {
        next = ops.setPrimarySize(doc, w, h);
        if (next === doc) return doc;
      }
      if (patch.subtitleBottom !== undefined && (patch.subtitleBottom === null ? next.subtitleBottom !== undefined : next.subtitleBottom !== sb(patch.subtitleBottom))) {
        next = { ...next };
        if (patch.subtitleBottom === null) delete next.subtitleBottom; else next.subtitleBottom = sb(patch.subtitleBottom);
      }
      if (patch.fit !== undefined && (patch.fit ?? undefined) !== next.fit) {
        next = { ...next };
        if (patch.fit === null) delete next.fit; else next.fit = patch.fit;
      }
      return next;
    }
    const cur = (doc.formats ?? []).find((f) => f.id === id);
    if (!cur) return doc;
    const size = { width: patch.width ?? cur.width, height: patch.height ?? cur.height };
    if ((size.width !== cur.width || size.height !== cur.height) && formatProblem(doc, size, id)) return doc;
    const next: SequenceFormat = { ...cur, ...size };
    if (patch.label !== undefined) { if (patch.label?.trim()) next.label = patch.label.trim().slice(0, 60); else delete next.label; }
    if (patch.subtitleBottom !== undefined) { if (patch.subtitleBottom === null) delete next.subtitleBottom; else next.subtitleBottom = sb(patch.subtitleBottom); }
    if (patch.fit !== undefined) { if (patch.fit === null) delete next.fit; else next.fit = patch.fit; }
    if (JSON.stringify(next) === JSON.stringify(cur)) return doc;
    return { ...doc, formats: doc.formats!.map((f) => (f.id === id ? next : f)) };
  },

  /** Remove a format, and every clip's framing for it and every mention of it in hideIn. */
  removeFormat(doc: SequenceDocument, id: string): SequenceDocument {
    if (id === PRIMARY_FORMAT || !(doc.formats ?? []).some((f) => f.id === id)) return doc;
    const next: SequenceDocument = { ...doc };
    const formats = doc.formats!.filter((f) => f.id !== id);
    if (formats.length) next.formats = formats; else delete next.formats;
    const strip = <T extends PictureClip>(c: T): T => (c.formatFraming?.[id] ? withFormatEntry(c, id, null) : c);
    const unhide = <T extends { hideIn?: string[] }>(x: T): T => {
      if (!x.hideIn?.includes(id)) return x;
      const n: T = { ...x };
      const rest = x.hideIn.filter((h) => h !== id);
      if (rest.length) n.hideIn = rest; else delete n.hideIn;
      return n;
    };
    next.video = doc.video.map(strip);
    if (doc.layers) next.layers = doc.layers.map((l) => ({ ...l, clips: l.clips.map((c) => unhide(strip(c))) }));
    next.graphics = doc.graphics.map(unhide);
    return next;
  },

  /** Change the sequence's own (primary) size; unchanged when refused (a bad size, or the shape of one of its formats). */
  setPrimarySize(doc: SequenceDocument, width: number, height: number): SequenceDocument {
    if (width === doc.width && height === doc.height) return doc;
    if (formatProblem(doc, { width, height }, PRIMARY_FORMAT)) return doc;
    return { ...doc, width, height };
  },

  /**
   * Make a format the primary: the sequence takes its size and subtitle height, every clip's framing (placement,
   * motion) for it becomes the clip's own and the clip's own goes to the old primary, which becomes a format with a
   * fresh id (`oldPrimaryId`, else from its shape) where the promoted one was in the list. hideIn follows.
   */
  promoteFormat(doc: SequenceDocument, id: string, oldPrimaryId?: string): SequenceDocument {
    const f = (doc.formats ?? []).find((x) => x.id === id);
    if (!f) return doc;
    const nid = oldPrimaryId && oldPrimaryId !== PRIMARY_FORMAT && !(doc.formats ?? []).some((x) => x.id === oldPrimaryId && x.id !== id)
      ? oldPrimaryId : newFormatId({ formats: (doc.formats ?? []).filter((x) => x.id !== id) }, doc, [id]);
    const old: SequenceFormat = { id: nid, width: doc.width, height: doc.height };
    if (doc.subtitleBottom !== undefined) old.subtitleBottom = doc.subtitleBottom;
    if (doc.fit) old.fit = doc.fit;
    const next: SequenceDocument = { ...doc, width: f.width, height: f.height, formats: doc.formats!.map((x) => (x.id === id ? old : x)) };
    if (f.subtitleBottom !== undefined) next.subtitleBottom = f.subtitleBottom; else delete next.subtitleBottom;
    if (f.fit) next.fit = f.fit; else delete next.fit;
    const swap = <T extends PictureClip>(c: T, layer: boolean): T => {
      const ff = c.formatFraming?.[id] as FormatFraming | undefined;
      if (!ff) return c;
      let n = { ...c };
      const back: FormatFraming = {};
      if (ff.fit) {
        const prim = framingFor(c, PRIMARY_FORMAT);
        Object.assign(back, tidyFramingGroup(prim, true));
        n = setPrimaryFraming(n, ff);
      }
      if (layer) {
        const lc = c as LayerClip;
        const ln = n as LayerClip;
        if (ff.place) {
          const place: LayerPlace = {};
          for (const k of ['scale', 'x', 'y'] as const) if (ff.place[k] !== undefined) { place[k] = lc[k]; ln[k] = ff.place[k]!; }
          if (Object.keys(place).length) back.place = place;
        }
        if (ff.motion) {
          const position = tidyMotionKeys(lc.motion?.keys, true);
          if (position.length) back.motion = { keys: position };
          const keys = motionFor(lc, id);
          if (keys.length) ln.motion = { keys }; else delete ln.motion;
        }
      }
      return withFormatEntry(withFormatEntry(n, id, null), nid, Object.keys(back).length ? back : null);
    };
    const rehide = <T extends { hideIn?: string[] }>(x: T): T => (x.hideIn ? { ...x, hideIn: x.hideIn.map((h) => (h === PRIMARY_FORMAT ? nid : h === id ? PRIMARY_FORMAT : h)) } : x);
    next.video = doc.video.map((c) => swap(c, false));
    if (doc.layers) next.layers = doc.layers.map((l) => ({ ...l, clips: l.clips.map((c) => rehide(swap(c, true))) }));
    next.graphics = doc.graphics.map(rehide);
    return next;
  },

  /**
   * Change a clip's framing in one format (1.7.0). For 'primary' the clip's own fields (as setFraming, plus keys; layers:
   * place sets scale, x, y and motion the motion keys). For a format, its formatFraming entry: framing fields start
   * from the primary's when the format had none of its own (`fit` is then written, marking its own framing); `reset`
   * returns groups to the same as the primary; an entry left empty is removed. Unchanged for an unknown clip or format.
   */
  setFormatFraming(doc: SequenceDocument, clipId: string, formatId: FormatRef, patch: FormatFramingPatch): SequenceDocument {
    if (!hasFormat(doc, formatId)) return doc;
    const framingKeys = ['fit', 'reframeX', 'reframeY', 'zoom', 'keys'] as const;
    const touchesFraming = framingKeys.some((k) => k in patch);
    return mapPicture(doc, clipId, (c, layer) => {
      const fitOk = (fit: FitMode | undefined) => !(fit === 'blur' && layer);
      if (formatId === PRIMARY_FORMAT) {
        let n = c;
        if (touchesFraming) {
          const cur = framingFor(c, PRIMARY_FORMAT);
          const g: FramingGroup = { ...cur };
          for (const k of framingKeys) if (k in patch && (k !== 'fit' || fitOk(patch.fit))) (g as Record<string, unknown>)[k] = patch[k];
          n = setPrimaryFraming(n, g);
        }
        if (layer && patch.place) {
          const lc = { ...(n as LayerClip) };
          const clampN = (v: number | undefined, lo: number, hi: number, fallback: number) => (v === undefined || !Number.isFinite(v) ? fallback : Math.min(hi, Math.max(lo, v)));
          lc.scale = clampN(patch.place.scale, 0.05, 4, lc.scale);
          lc.x = clampN(patch.place.x, -1, 1, lc.x);
          lc.y = clampN(patch.place.y, -1, 1, lc.y);
          n = lc;
        }
        if (layer && patch.motion) {
          const keys = tidyMotionKeys(patch.motion);
          n = { ...n } as LayerClip;
          if (keys.length) (n as LayerClip).motion = { keys }; else delete (n as LayerClip).motion;
        }
        return n;
      }
      const entry: FormatFraming = { ...(c.formatFraming?.[formatId] ?? {}) };
      for (const r of patch.reset ?? []) {
        if (r === 'framing') for (const k of framingKeys) delete entry[k];
        else if (r === 'place') delete entry.place;
        else if (r === 'motion') delete entry.motion;
      }
      if (touchesFraming) {
        const g: FramingGroup = entry.fit ? { ...entry } : { ...framingFor(c, PRIMARY_FORMAT) };
        for (const k of framingKeys) if (k in patch && (k !== 'fit' || fitOk(patch.fit))) (g as Record<string, unknown>)[k] = patch[k];
        for (const k of framingKeys) delete entry[k];
        Object.assign(entry, tidyFramingGroup(g, true));
      }
      if (layer && patch.place) {
        const p: LayerPlace = { ...(entry.place ?? {}) };
        const clampN = (v: number | undefined, lo: number, hi: number) => (v === undefined || !Number.isFinite(v) ? undefined : Math.min(hi, Math.max(lo, v)));
        for (const [k, lo, hi] of [['scale', 0.05, 4], ['x', -1, 1], ['y', -1, 1]] as const) {
          if (!(k in patch.place)) continue;
          const v = clampN(patch.place[k], lo, hi);
          if (v === undefined) delete p[k]; else p[k] = v;
        }
        if (Object.keys(p).length) entry.place = p; else delete entry.place;
      }
      if (layer && patch.motion) {
        const keys = tidyMotionKeys(patch.motion, true);
        if (keys.length) entry.motion = { keys }; else delete entry.motion;
      }
      // Storyline clips have no placement or motion.
      if (!layer) { delete entry.place; delete entry.motion; }
      return withFormatEntry(c, formatId, Object.keys(entry).length ? entry : null);
    });
  },

  /** Add a key at `key.atTicks` (source time, on the frame grid); a key at that time is replaced (framing) or merged into (motion). */
  addKey(doc: SequenceDocument, t: KeyTarget, key: FramingKey | MotionKey): SequenceDocument {
    if (!findPicture(doc, t.clipId) || !hasFormat(doc, t.formatId)) return doc;
    const at = snap(Math.max(0, key.atTicks), doc.frameRate);
    const keys = [...keysOf(doc, t)];
    const i = keys.findIndex((k) => k.atTicks === at);
    if (i >= 0) keys[i] = t.kind === 'motion' ? { ...keys[i], ...key, atTicks: at } : { ...key, atTicks: at };
    else { if (keys.length >= MAX_KEYS) return doc; keys.push({ ...key, atTicks: at }); }
    return setKeys(doc, t, keys);
  },

  /** Change a key's values or ease (motion: a value set to undefined is removed; a key left without values goes). */
  updateKey(doc: SequenceDocument, t: KeyTarget, index: number, patch: Partial<Omit<FramingKey, 'atTicks'>> | Partial<Omit<MotionKey, 'atTicks'>>): SequenceDocument {
    const keys = [...keysOf(doc, t)];
    if (!keys[index]) return doc;
    const next: Record<string, unknown> = { ...keys[index] };
    for (const [k, v] of Object.entries(patch)) { if (v === undefined) delete next[k]; else next[k] = v; }
    keys[index] = next as unknown as FramingKey | MotionKey;
    return setKeys(doc, t, keys);
  },

  /** Retime a key: on the frame grid, clamped strictly between its neighbours (at least a frame from each). */
  moveKey(doc: SequenceDocument, t: KeyTarget, index: number, atTicks: number): SequenceDocument {
    const keys = [...keysOf(doc, t)];
    const k = keys[index];
    if (!k) return doc;
    const tpf = ticksPerFrame(doc.frameRate);
    const lo = index > 0 ? keys[index - 1]!.atTicks + tpf : 0;
    const hi = index < keys.length - 1 ? keys[index + 1]!.atTicks - tpf : Number.MAX_SAFE_INTEGER;
    if (lo > hi) return doc;
    const at = Math.round(Math.min(hi, Math.max(lo, snap(Math.max(0, atTicks), doc.frameRate))));
    if (at === k.atTicks) return doc;
    keys[index] = { ...k, atTicks: at };
    return setKeys(doc, t, keys);
  },

  deleteKey(doc: SequenceDocument, t: KeyTarget, index: number): SequenceDocument {
    const keys = keysOf(doc, t);
    if (!keys[index]) return doc;
    return setKeys(doc, t, keys.filter((_, i) => i !== index));
  },

  /** Where a layer clip or a graphic is hidden ('primary' and format ids; unknown ids dropped); empty: shown everywhere. */
  setHideIn(doc: SequenceDocument, itemId: string, hideIn: string[]): SequenceDocument {
    const order = formatsOf(doc).map((f) => f.id);
    const list = order.filter((id) => hideIn.includes(id));
    const fix = <T extends { id: string; hideIn?: string[] }>(x: T): T => {
      if (x.id !== itemId) return x;
      const n = { ...x };
      if (list.length) n.hideIn = list; else delete n.hideIn;
      return n;
    };
    if (doc.graphics.some((g) => g.id === itemId)) return { ...doc, graphics: doc.graphics.map(fix) };
    if (isLayerClip(doc, itemId)) return { ...doc, layers: doc.layers.map((l) => ({ ...l, clips: l.clips.map(fix) })) };
    return doc;
  },

  /** Replace the sequence's subtitles (import, clear). 1.9.0: `style` is the track's default style, kept when given. */
  setSubtitles(doc: SequenceDocument, subtitles: { language: string; label: string; cues: Cue[]; style?: TrackStyle } | null): SequenceDocument {
    if (!subtitles) return { ...doc, subtitles: null };
    const cues = subtitles.cues.map((c) => ({ ...c, startTicks: Math.max(0, Math.round(c.startTicks)), endTicks: Math.max(0, Math.round(c.endTicks)) }));
    const { style, ...rest } = subtitles;
    const tidy = tidyTrackStyle(style);
    return { ...doc, subtitles: { ...rest, cues, ...(tidy ? { style: tidy } : {}) } };
  },

  /**
   * Set or clear the style of the cues `ids` (1.9.0; subtitles.ts applyCueStyle): a field's value sets it, null
   * removes it, a null patch removes the cue's own style. Unchanged without subtitles or when nothing changes.
   */
  setCueStyle(doc: SequenceDocument, ids: readonly string[], patch: CueStylePatch | null): SequenceDocument {
    const cues = doc.subtitles?.cues;
    if (!cues) return doc;
    const next = applyCueStyle(cues, ids, patch);
    return next === cues ? doc : { ...doc, subtitles: { ...doc.subtitles!, cues: next } };
  },

  /** Set the sequence track's default style (1.9.0); null or an empty style removes it. Unchanged without subtitles. */
  setSubtitleStyle(doc: SequenceDocument, style: TrackStyle | null): SequenceDocument {
    if (!doc.subtitles) return doc;
    const tidy = tidyTrackStyle(style);
    if (JSON.stringify(tidy ?? undefined) === JSON.stringify(doc.subtitles.style)) return doc;
    const { style: _old, ...rest } = doc.subtitles;
    return { ...doc, subtitles: { ...rest, ...(tidy ? { style: tidy } : {}) } };
  },

  /**
   * Trim a cue's start ('in') or end ('out') by `deltaTicks`, in whole frames (1.7.1, the timeline's cue handles): the
   * start stays at 0 or later, the cue at least a frame long, and the edge stops MIN_CUE_GAP_FRAMES (2) before the
   * neighbouring cue of its row unless the two are closer or overlap already (1.9.0: subtitles.ts nudgeCue, shared
   * with the Subtitles tab and the strip). Unchanged for an unknown cue or when nothing moves.
   */
  trimCue(doc: SequenceDocument, id: string, edge: 'in' | 'out', deltaTicks: number, frameTicks = ticksPerFrame(doc.frameRate)): SequenceDocument {
    const cues = doc.subtitles?.cues;
    if (!cues || !cues.some((c) => c.id === id)) return doc;
    const tpf = Math.max(1, frameTicks);
    const frames = Math.round(deltaTicks / tpf);
    if (!frames) return doc;
    const next = nudgeCue(cues, id, edge === 'in' ? 'start' : 'end', frames, { frameTicks: tpf, trackStyle: doc.subtitles!.style });
    return next === cues ? doc : { ...doc, subtitles: { ...doc.subtitles!, cues: next } };
  },

  /**
   * Slip a clip (1.7.2): its source in and out points move together by `deltaTicks`, in whole frames, so the same
   * place on the timeline shows other footage. Works on V1, layer and audio clips (A2; 1.7.3 A3–A8 too). Clamped so that the in point stays at
   * 0 or later and the out point at `sourceDurationTicks` (what is recorded so far, for a recording) or earlier; a
   * multicam clip stays within its angle. Its place, length, fades and transitions stay; framing keys are in source
   * time, so they stay on the footage. A still (or a source of unknown length: `sourceDurationTicks` 0 or less, or
   * MAX_SAFE_INTEGER) does not slip. Unchanged when nothing moves.
   */
  slipClip(doc: SequenceDocument, id: string, deltaTicks: number, sourceDurationTicks: number): SequenceDocument {
    const tpf = ticksPerFrame(doc.frameRate);
    if (!(sourceDurationTicks > 0) || sourceDurationTicks >= Number.MAX_SAFE_INTEGER) return doc;
    const slip = <T extends VideoClip | LayerClip | AudioClip>(c: T): T => {
      const end = mediaEnd(doc, c as { versionId: string; multicam?: VideoClip['multicam'] }, sourceDurationTicks);
      const d = Math.max(-wholeFrames(Math.max(0, c.sourceInTicks), tpf), Math.min(snap(deltaTicks, doc.frameRate), wholeFrames(Math.max(0, end - c.sourceOutTicks), tpf)));
      return d ? { ...c, sourceInTicks: c.sourceInTicks + d, sourceOutTicks: c.sourceOutTicks + d } : c;
    };
    const v = doc.video.find((c) => c.id === id);
    if (v) {
      const next = slip(v);
      if (next === v) return doc;
      // A multicam clip keeps to its angle's picture (it would not export otherwise).
      const g = v.multicam && (doc.multicam ?? []).find((x) => x.id === v.multicam!.groupId);
      const a = g?.angles[v.multicam!.angle];
      if (g && a && !angleCovers(g, v.multicam!.angle, a.startTicks + next.sourceInTicks, a.startTicks + next.sourceOutTicks)) return doc;
      return { ...doc, video: doc.video.map((c) => (c.id === id ? next : c)) };
    }
    // 1.7.3: any audio track (A2–A8).
    const trackId = audioTrackOf(doc, id);
    if (trackId) {
      const clips = audioClipsOn(doc, trackId)!;
      const au = clips.find((c) => c.id === id)!;
      const next = slip(au);
      return next === au ? doc : withAudioClips(doc, trackId, clips.map((c) => (c.id === id ? next : c)));
    }
    const layer = (doc.layers ?? []).find((l) => l.clips.some((c) => c.id === id));
    if (!layer) return doc;
    const lc = layer.clips.find((c) => c.id === id)!;
    const next = slip(lc);
    if (next === lc) return doc;
    return { ...doc, layers: doc.layers.map((l) => (l.id === layer.id ? { ...l, clips: l.clips.map((c) => (c.id === id ? next : c)) } : l)) };
  },

  /**
   * Slide a clip (1.7.2): it moves along its track by `deltaTicks` (whole frames) and keeps its own footage.
   *
   * On V1 the previous clip's out point and the next clip's in point change by the same amount, so the sequence keeps
   * its length; a gap before either cut grows or shrinks first, where there is one. The first clip moves the gap
   * before it (one opens at the sequence start, or closes down to 0); the last clip only changes the previous one. It is clamped by the
   * neighbours' media (`sourceDurationOf`, by version id; 0 or less is no end, a still) and their one-frame minimum.
   * Transitions are clamped by `layoutVideo` as for any trim.
   *
   * On a layer or an audio track (A2–A8): the clip moves; a neighbour that touches it (within a frame) is trimmed to match, any other
   * neighbour limits the move. Unchanged when nothing moves.
   */
  slideClip(doc: SequenceDocument, id: string, deltaTicks: number, sourceDurationOf: (versionId: string) => number): SequenceDocument {
    const tpf = ticksPerFrame(doc.frameRate);
    const want = snap(deltaTicks, doc.frameRate);
    if (!want) return doc;
    const BIG = Number.MAX_SAFE_INTEGER;
    const i = doc.video.findIndex((c) => c.id === id);
    if (i >= 0) {
      const clip = doc.video[i]!;
      const prev = doc.video[i - 1];
      const next = doc.video[i + 1];
      const gapIn = gapBefore(clip, doc.frameRate);
      const gapNext = next ? gapBefore(next, doc.frameRate) : 0;
      // How far it may go right: the cut before it opens (a gap grows; or the previous clip's out point moves later,
      // within its media), the cut after it closes (a gap shrinks, then the next clip's in point moves later).
      const growBefore = gapIn > 0 || !prev ? BIG : mediaEnd(doc, prev, sourceDurationOf(prev.versionId)) - prev.sourceOutTicks;
      const shrinkAfter = next ? gapNext + clipDuration(next) - tpf : BIG;
      // How far it may go left: the cut before it closes (a gap shrinks, then the previous clip gets shorter), the cut
      // after it opens (a gap grows; or the next clip's in point moves earlier, to 0 at most).
      const shrinkBefore = gapIn + (prev ? clipDuration(prev) - tpf : 0);
      const growAfter = gapNext > 0 || !next ? BIG : next.sourceInTicks;
      const d = want > 0 ? Math.min(want, wholeFrames(Math.max(0, Math.min(growBefore, shrinkAfter)), tpf)) : -Math.min(-want, wholeFrames(Math.max(0, Math.min(shrinkBefore, growAfter)), tpf));
      if (!d) return doc;
      const video = [...doc.video];
      const setGap = (c: VideoClip, g: number): VideoClip => { const n = { ...c }; if (g > 0) n.gapBeforeTicks = g; else delete n.gapBeforeTicks; return n; };
      if (d > 0) {
        if (gapIn > 0 || !prev) video[i] = setGap(clip, gapIn + d);
        else video[i - 1] = tidyFades({ ...prev, sourceOutTicks: prev.sourceOutTicks + d }, doc.frameRate);
        if (next) {
          const take = Math.min(gapNext, d);
          video[i + 1] = tidyFades({ ...setGap(next, gapNext - take), sourceInTicks: next.sourceInTicks + d - take }, doc.frameRate);
        }
      } else {
        const e = -d;
        const take = Math.min(gapIn, e);
        if (take) video[i] = setGap(clip, gapIn - take);
        if (prev && e > take) video[i - 1] = tidyFades({ ...prev, sourceOutTicks: prev.sourceOutTicks - (e - take) }, doc.frameRate);
        if (next) video[i + 1] = gapNext > 0 ? setGap(next, gapNext + e) : tidyFades({ ...next, sourceInTicks: next.sourceInTicks - e }, doc.frameRate);
      }
      return { ...doc, video };
    }

    // A layer or an audio track: the clip moves; touching neighbours are trimmed, others limit the move.
    type TrackClip = LayerClip | AudioClip;
    const slideOn = <T extends TrackClip>(clips: T[]): T[] | null => {
      const c = clips.find((x) => x.id === id);
      if (!c) return null;
      const start = c.recordInTicks;
      const end = start + clipDuration(c);
      const others = clips.filter((x) => x.id !== id);
      const left = others.filter((x) => x.recordInTicks + clipDuration(x) <= start).sort((a, b) => (b.recordInTicks + clipDuration(b)) - (a.recordInTicks + clipDuration(a)))[0];
      const right = others.filter((x) => x.recordInTicks >= end).sort((a, b) => a.recordInTicks - b.recordInTicks)[0];
      const leftTouch = Boolean(left && start - (left.recordInTicks + clipDuration(left)) < tpf);
      const rightTouch = Boolean(right && right.recordInTicks - end < tpf);
      const rightLimit = !right ? BIG : rightTouch ? clipDuration(right) - tpf : right.recordInTicks - end;
      const leftGrow = leftTouch ? mediaEnd(doc, left!, sourceDurationOf(left!.versionId)) - left!.sourceOutTicks : BIG;
      const leftLimit = !left ? start : leftTouch ? clipDuration(left!) - tpf : start - (left!.recordInTicks + clipDuration(left!));
      const rightGrow = rightTouch ? right!.sourceInTicks : BIG;
      const d = want > 0 ? Math.min(want, wholeFrames(Math.max(0, Math.min(rightLimit, leftGrow)), tpf)) : -Math.min(-want, wholeFrames(Math.max(0, Math.min(leftLimit, rightGrow)), tpf));
      if (!d) return null;
      return clips.map((x) => {
        if (x.id === id) return { ...x, recordInTicks: x.recordInTicks + d };
        if (leftTouch && x.id === left!.id) return tidyFades({ ...x, sourceOutTicks: x.sourceOutTicks + d }, doc.frameRate);
        if (rightTouch && x.id === right!.id) return tidyFades({ ...x, recordInTicks: x.recordInTicks + d, sourceInTicks: x.sourceInTicks + d }, doc.frameRate);
        return x;
      });
    };
    // 1.7.3: any audio track (A2–A8).
    const trackId = audioTrackOf(doc, id);
    if (trackId) {
      const clips = slideOn(audioClipsOn(doc, trackId)!);
      return clips ? withAudioClips(doc, trackId, clips) : doc;
    }
    const layer = (doc.layers ?? []).find((l) => l.clips.some((c) => c.id === id));
    const clips = layer && slideOn(layer.clips);
    return clips ? { ...doc, layers: doc.layers.map((l) => (l.id === layer!.id ? { ...l, clips } : l)) } : doc;
  },

  /* ------------------------------------------------ 1.7.3: storyline editing (storyline.ts; stream A writes the bodies) */

  /**
   * Place V1 clips `ids` `deltaTicks` later (earlier when negative), snapped to the frame grid (1.7.3: a V1 body drag;
   * ⌘-drag is 'insert'). Each moved clip keeps its distance to the others; the delta is clamped so the earliest one
   * lands at 0 or later. V1 only: the editor moves the rest of a selection with moveItems.
   *  - 'overwrite': the clips are lifted (their length becomes a gap; a trailing gap is dropped), then land at their
   *    new times: gaps under them are used up; clips under them are trimmed (an edge), split (the landing range is
   *    inside one; the right half gets newId('v')) or removed (wholly covered). Nothing else moves.
   *  - 'insert': the clips are lifted with a ripple (the storyline closes up, as rippleDelete 'v1'), then put in at
   *    the drop time, splitting a clip it falls inside; everything from there on moves later by their total length.
   *    The drop time is the earliest clip's start plus the delta, against the storyline before the edit: one after
   *    the moved clips counts as that much less (it stays against the clips that do not move); one inside them is
   *    where they were (no change). A drop inside a transition's overlap is the cut there (the incoming clip follows
   *    the inserted ones whole). `opts.ripple` 'sync' also shifts the other tracks: the lift as rippleDelete 'sync'
   *    (from where the clips were) and the insert by the inserted length from the drop time, so items on the other
   *    tracks stay with the V1 clips around them.
   * Transitions: a clip after a gap opened by the edit loses its transitionIn; a moved clip keeps its own only when
   * it lands overlapping the same neighbour as before (storyline.ts fromTimeline). Clip ids are kept (but split halves).
   */
  placeVideo(doc: SequenceDocument, ids: readonly string[], deltaTicks: number, mode: PlaceMode, opts: { ripple?: RippleMode } = {}): SequenceDocument {
    return placeV1(doc, ids, deltaTicks, mode, opts.ripple ?? 'v1', new Set());
  },

  /**
   * Move a selection by `deltaTicks` (1.7.3: a drag of several items, of V1 and other tracks together). The V1 clips
   * go through placeVideo (`opts.mode`, default 'overwrite'); the others move by the same delta (clamped as one so
   * nothing goes before 0), each kept off its neighbours (freeStart on its own track; the moved items are not in
   * each other's way). Layer clips also change layer by `opts.laneDelta` (+1 = one layer up; clamped to the layers
   * there are). Gaps in `items` are ignored. With 'insert' and Sync, items in `items` are moved by the delta and are
   * not shifted again by the sync ripple.
   *
   * 1.7.4 (docs/dev/editor-1.7.4-build.md "Dragging between tracks"): a drag that moves a picture (V1 or layer clip)
   * to another video track goes through moveToVideoTrack, not here; `laneDelta` here stays the 1.7.3 layer-to-layer
   * move of unlinked layer clips. New options (stream A; a stub until then when used, i.e. non-zero or true):
   *  - `audioLaneDelta`: audio clips change audio track by this many (+1 = one down: A2 → A3), clamped to the tracks
   *    there are (A2 lowest number); a clip whose wanted track is busy over its time lands at the nearest free time
   *    there (landOnTrack), as on its own track.
   *  - `graphicLaneDelta`: graphics change graphics track by this many (+1 = one up: GFX 1 → GFX 2), clamped to the
   *    graphics tracks there are.
   *  - `newTracks`: moving past the last track creates it instead of clamping: audio below the last audio track adds
   *    audio tracks (addAudioTrack, up to A8), graphics above the top graphics track add graphics tracks (up to GFX 3),
   *    layer clips above the top layer add layers (up to V9). Beyond the limit: clamped. All created tracks are
   *    created before landing, in order (so a delta of 2 past A3 makes A4 and A5).
   */
  moveItems(doc: SequenceDocument, items: readonly ItemRef[], deltaTicks: number, opts: { mode?: PlaceMode; laneDelta?: number; ripple?: RippleMode; audioLaneDelta?: number; graphicLaneDelta?: number; newTracks?: boolean } = {}): SequenceDocument {
    const mode = opts.mode ?? 'overwrite';
    const seen = new Set<string>();
    const refs = items.filter((r) => r.kind !== 'gap' && !seen.has(itemKey(r)) && Boolean(seen.add(itemKey(r))) && itemSpan(doc, r));
    if (!refs.length) return doc;
    const d = Math.max(snap(deltaTicks, doc.frameRate), -Math.min(...refs.map((r) => itemSpan(doc, r)!.start)));
    const laneDelta = Math.round(opts.laneDelta ?? 0);
    const audioLaneDelta = Math.round(opts.audioLaneDelta ?? 0);
    const graphicLaneDelta = Math.round(opts.graphicLaneDelta ?? 0);
    const movesLane = (laneDelta !== 0 && refs.some((r) => r.kind === 'layer')) || (audioLaneDelta !== 0 && refs.some((r) => r.kind === 'audio'))
      || (graphicLaneDelta !== 0 && refs.some((r) => r.kind === 'graphic'));
    if (!d && !movesLane) return doc;
    const skip = new Set(refs.filter((r) => r.kind !== 'video').map(itemKey));
    let out = doc;
    const v1 = refs.filter((r) => r.kind === 'video').map((r) => r.id);
    if (v1.length && d) {
      out = placeV1(doc, v1, d, mode, opts.ripple ?? 'v1', skip);
      // An insert dropped inside the clips themselves moves nothing (the others stay with them).
      if (out === doc && mode === 'insert') return doc;
    }
    const ids = (kind: ItemRefKind) => new Set(refs.filter((r) => r.kind === kind).map((r) => r.id));
    // Audio: on its own track; 1.7.4 audioLaneDelta tracks down (new tracks past the last with newTracks).
    const audio = ids('audio');
    const audioLanes = audioLaneDelta && audio.size && (opts.newTracks || allAudioTracks(out).some((t, i) => t.clips.some((c) => audio.has(c.id)) && Math.max(0, Math.min(allAudioTracks(out).length - 1, i + audioLaneDelta)) !== i));
    if (audioLanes) {
      const tracks0 = allAudioTracks(out);
      const lowest = Math.max(-1, ...tracks0.map((t, i) => (t.clips.some((c) => audio.has(c.id)) ? i : -1)));
      if (opts.newTracks) for (let n = lowest + audioLaneDelta - (tracks0.length - 1); n > 0; n--) { const next = ops.addAudioTrack(out); if (next === out) break; out = next; }
      const tracks = allAudioTracks(out);
      const moving = tracks.flatMap((t, i) => t.clips.filter((c) => audio.has(c.id)).map((c) => ({ c, src: i, dest: Math.max(0, Math.min(tracks.length - 1, i + audioLaneDelta)) })));
      if (d || moving.some((m) => m.dest !== m.src)) tracks.forEach((t, i) => {
        const rest = t.clips.filter((c) => !audio.has(c.id));
        const here = moving.filter((m) => m.dest === i);
        const at = landOnTrack(spans(rest), here.map((m) => ({ id: m.c.id, want: m.c.recordInTicks + d, len: clipDuration(m.c) })));
        if (rest.length !== t.clips.length || here.length) out = withAudioClips(out, t.id, [...rest, ...here.map((m) => ({ ...m.c, recordInTicks: at.get(m.c.id)! }))]);
      });
    } else for (const t of allAudioTracks(out)) {
      const mine = t.clips.filter((c) => audio.has(c.id));
      if (!mine.length || !d) continue;
      const at = landOnTrack(spans(t.clips.filter((c) => !audio.has(c.id))), mine.map((c) => ({ id: c.id, want: c.recordInTicks + d, len: clipDuration(c) })));
      out = withAudioClips(out, t.id, t.clips.map((c) => (at.has(c.id) ? { ...c, recordInTicks: at.get(c.id)! } : c)));
    }
    // Layer clips: by the same delta, and laneDelta layers up (within the layers there are; 1.7.4 newTracks adds
    // layers above the top, up to V9).
    const layer = ids('layer');
    if (layer.size) {
      if (opts.newTracks && laneDelta > 0) {
        const top = Math.max(-1, ...(out.layers ?? []).map((l, i) => (l.clips.some((c) => layer.has(c.id)) ? i : -1)));
        for (let n = top + laneDelta - ((out.layers ?? []).length - 1); n > 0; n--) { const next = ops.addLayer(out); if (next === out) break; out = next; }
      }
      const layers = out.layers ?? [];
      const moving = (out.layers ?? []).flatMap((l, li) => l.clips.filter((c) => layer.has(c.id)).map((c) => ({ c, dest: Math.max(0, Math.min(layers.length - 1, li + laneDelta)) })));
      const next = out.layers.map((l) => ({ ...l, clips: l.clips.filter((c) => !layer.has(c.id)) }));
      next.forEach((l, li) => {
        const here = moving.filter((m) => m.dest === li);
        if (!here.length) return;
        const at = landOnTrack(spans(l.clips), here.map((m) => ({ id: m.c.id, want: m.c.recordInTicks + d, len: clipDuration(m.c) })));
        l.clips = [...l.clips, ...here.map((m) => ({ ...m.c, recordInTicks: at.get(m.c.id)! }))];
      });
      const same = (a: LayerClip[], b: LayerClip[]) => a.length === b.length && a.every((c) => b.some((x) => x.id === c.id && x.recordInTicks === c.recordInTicks));
      const layersOut = mapSame(out.layers, (l) => { const n = next[out.layers.indexOf(l)]!; return same(l.clips, n.clips) ? l : n; });
      if (layersOut !== out.layers) out = { ...out, layers: layersOut };
    }
    // Graphics: on their own track; 1.7.4 graphicLaneDelta tracks up (new tracks up to GFX 3 with newTracks).
    const graphic = ids('graphic');
    const graphicLanes = graphic.size && graphicLaneDelta && (opts.newTracks || out.graphics.some((g) => graphic.has(g.id) && Math.max(0, Math.min(graphicTrackCount(out) - 1, graphicTrackOf(g) + graphicLaneDelta)) !== graphicTrackOf(g)));
    if (graphicLanes) {
      const mine = out.graphics.filter((g) => graphic.has(g.id));
      const top = Math.max(...mine.map(graphicTrackOf));
      if (opts.newTracks) for (let n = top + graphicLaneDelta - (graphicTrackCount(out) - 1); n > 0; n--) { const next = ops.addGraphicTrack(out); if (next === out) break; out = next; }
      const count = graphicTrackCount(out);
      const dest = new Map(mine.map((g) => [g.id, Math.max(0, Math.min(count - 1, graphicTrackOf(g) + graphicLaneDelta)) as GraphicTrack]));
      const at = new Map<string, number>();
      for (let k = 0; k < MAX_GRAPHIC_TRACKS; k++) {
        const here = mine.filter((g) => dest.get(g.id) === k);
        if (!here.length) continue;
        const taken = out.graphics.filter((g) => !graphic.has(g.id) && graphicTrackOf(g) === k).map((g) => ({ start: g.recordInTicks, end: g.recordInTicks + g.durationTicks }));
        for (const [id, t] of landOnTrack(taken, here.map((g) => ({ id: g.id, want: g.recordInTicks + d, len: g.durationTicks })))) at.set(id, t);
      }
      out = { ...out, graphics: out.graphics.map((g) => (at.has(g.id) ? onGraphicTrack({ ...g, recordInTicks: at.get(g.id)! }, dest.get(g.id)!) : g)) };
    } else if (graphic.size && d) {
      const at = new Map<string, number>();
      for (let k = 0; k < MAX_GRAPHIC_TRACKS; k++) {
        const on = out.graphics.filter((g) => graphicTrackOf(g) === k);
        const mine = on.filter((g) => graphic.has(g.id));
        if (!mine.length) continue;
        const taken = on.filter((g) => !graphic.has(g.id)).map((g) => ({ start: g.recordInTicks, end: g.recordInTicks + g.durationTicks }));
        for (const [id, t] of landOnTrack(taken, mine.map((g) => ({ id: g.id, want: g.recordInTicks + d, len: g.durationTicks })))) at.set(id, t);
      }
      out = { ...out, graphics: out.graphics.map((g) => (at.has(g.id) ? { ...g, recordInTicks: at.get(g.id)! } : g)) };
    }
    // Cues, kept off the others; markers.
    const cue = ids('cue');
    if (cue.size && d && out.subtitles) {
      const cues = out.subtitles.cues;
      const at = landOnTrack(cues.filter((c) => !cue.has(c.id)).map((c) => ({ start: c.startTicks, end: c.endTicks })), cues.filter((c) => cue.has(c.id)).map((c) => ({ id: c.id, want: c.startTicks + d, len: c.endTicks - c.startTicks })));
      out = { ...out, subtitles: { ...out.subtitles, cues: cues.map((c) => (at.has(c.id) ? { ...c, startTicks: at.get(c.id)!, endTicks: at.get(c.id)! + (c.endTicks - c.startTicks) } : c)) } };
    }
    const marker = ids('marker');
    if (marker.size && d && out.markers) out = withMarkers(out, out.markers.map((m) => (marker.has(m.id) ? { ...m, atTicks: Math.max(0, m.atTicks + d) } : m)));
    return orSame(doc, tidyAudioCrossfades(doc, out));
  },

  /**
   * Delete without closing up (1.7.3 Delete). V1 clips leave a gap of their length (merged with gaps beside them; a
   * gap at the end of the storyline is dropped; the clip after a new gap loses its transitionIn). Layer, audio and
   * graphic clips, cues and markers are removed. A gap in `items` is closed (closeGap). A V1 clip's detached sound is
   * not removed unless it is in `items` (the editor expands link groups first). Link groups left with one item are
   * dissolved.
   */
  liftClips(doc: SequenceDocument, items: readonly ItemRef[]): SequenceDocument {
    let d = doc;
    // Gaps first: their ids name the V1 clip after them, and a lift merges gaps.
    for (const r of items) if (r.kind === 'gap') d = ops.closeGap(d, r.id);
    const v1 = new Set(items.filter((r) => r.kind === 'video').map((r) => r.id));
    if (v1.size) {
      const tl = toTimeline(d);
      d = withItems(d, tl.filter((it) => !v1.has(it.clip.id)), predecessors(tl));
    }
    return orSame(doc, tidyAudioCrossfades(doc, tidyLinks(removeOthers(d, items))));
  },

  /**
   * Delete and close up (1.7.3 Shift+Delete). V1 clips are removed and the storyline closes up (as removeClip: the
   * gaps before removed clips go with them, the next clip keeps its own gap; unlike removeClip, it loses a transition
   * from a removed clip, as the clip after any changed neighbour does); a gap in `items` is closed. Layer, audio and
   * graphic clips and cues close up on their own track: later
   * items on that track move earlier by the removed length (never into the item before them). Markers are removed.
   * 'sync': for each V1 range removed (an edit point P, a length D), items on every other track (layers, audio
   * tracks, graphics, cues, markers) that start at or after P move D earlier. Sync never makes an overlap or a start
   * before 0: when a track cannot move the whole D, every track (and V1) moves by what that track allows, and the rest
   * stays a gap on V1. Ranges are done from the last to the first.
   */
  rippleDelete(doc: SequenceDocument, items: readonly ItemRef[], ripple: RippleMode): SequenceDocument {
    let d = rippleOthers(doc, items);
    const v1 = new Set(items.filter((r) => r.kind === 'video' && doc.video.some((c) => c.id === r.id)).map((r) => r.id));
    if (v1.size) d = rippleV1(d, v1, ripple, new Set()).doc;
    // Gaps, the last first (a gap before a removed clip went with it).
    const gaps = gapsOf(d).filter((g) => items.some((r) => r.kind === 'gap' && r.id === g.id)).reverse();
    for (const g of gaps) d = ripple === 'sync' ? ops.closeGapSync(d, g.id) : ops.closeGap(d, g.id);
    return orSame(doc, tidyAudioCrossfades(doc, tidyLinks(d)));
  },

  /**
   * Close the gap before V1 clip `id` with Sync (1.7.3: Shift+Delete on a selected gap with Sync on): as closeGap,
   * and the other tracks shift by the gap's length from its start (the sync rule of rippleDelete).
   */
  closeGapSync(doc: SequenceDocument, id: string): SequenceDocument {
    const g = gapsOf(doc).find((x) => x.id === id);
    if (!g) return doc;
    const full = g.end - g.start;
    const by = Math.max(0, Math.min(full, syncRoom(doc, g.start, new Set())));
    if (!by) return doc;
    const d = by >= full ? ops.closeGap(doc, id) : { ...doc, video: doc.video.map((c) => (c.id === id ? { ...c, gapBeforeTicks: full - by } : c)) };
    return shiftOthers(d, g.start, -by, new Set());
  },

  /**
   * Trim a V1 clip's edge without rippling (1.7.3: the Select tool; ⌘-drag on an edge is trimClip, which ripples).
   * The clip's other edge stays where it is in the sequence. Shortening leaves a gap (before the clip for the in
   * point; before the next clip for the out point; none after the last clip). Lengthening uses up the gap on that
   * side and stops at the neighbour (and at 0, and at the source's bounds). Snapped, at least one frame long, fades
   * tidied. A transition on an edge that now has a gap is cleared.
   */
  trimClipNoRipple(doc: SequenceDocument, id: string, edge: 'in' | 'out', deltaTicks: number, sourceDurationTicks: number): SequenceDocument {
    const rate = doc.frameRate;
    const tpf = ticksPerFrame(rate);
    const d = snap(deltaTicks, rate);
    const items = toTimeline(doc);
    const i = items.findIndex((it) => it.clip.id === id);
    if (i < 0 || !d) return doc;
    const it = items[i]!;
    const c = it.clip;
    const prev = items[i - 1];
    const next = items[i + 1];
    const out = [...items];
    if (edge === 'in') {
      // Lengthening uses up the gap before it (or the space before 0), never into the clip before.
      const room = Math.max(0, it.start - (prev ? prev.end : 0));
      const sourceInTicks = Math.min(Math.max(0, c.sourceInTicks + d, c.sourceInTicks - room), c.sourceOutTicks - tpf);
      const x = sourceInTicks - c.sourceInTicks;
      if (!x) return doc;
      let clip = tidyFades({ ...c, sourceInTicks }, rate);
      const start = it.start + x;
      // Shortened under a transition from the clip before: the rest of the overlap stays a transition.
      if (prev && clip.transitionIn && prev.end > start) clip = { ...clip, transitionIn: { ...clip.transitionIn, durationTicks: prev.end - start } };
      out[i] = itemAt(clip, start);
    } else {
      const room = next ? Math.max(0, next.start - it.end) : Number.MAX_SAFE_INTEGER;
      const sourceOutTicks = Math.max(Math.min(sourceDurationTicks, c.sourceOutTicks + d, c.sourceOutTicks + room), c.sourceInTicks + tpf);
      const x = sourceOutTicks - c.sourceOutTicks;
      if (!x) return doc;
      out[i] = itemAt(tidyFades({ ...c, sourceOutTicks }, rate), it.start);
      const end = out[i]!.end;
      if (next?.clip.transitionIn && end > next.start) out[i + 1] = { ...next, clip: { ...next.clip, transitionIn: { ...next.clip.transitionIn, durationTicks: end - next.start } } };
    }
    return withItems(doc, out, predecessors(items));
  },

  /* ------------------------------------------------ 1.7.3: unlinked sound and link groups */

  /**
   * Unlink a V1 clip's sound (1.7.3): its source range, gain, mute and sound fades become an audio clip (newId('a'))
   * at the clip's record position, on the first audio track free over that range (A2, then A3…), or on a new track
   * when none is (none when there are already MAX_AUDIO_TRACKS: the document is unchanged). The clip gets
   * soundDetached. The two are not linked (⌘L links them). Unchanged for a clip already detached or not on V1.
   */
  unlink(doc: SequenceDocument, clipId: string): SequenceDocument {
    const p = layoutVideo(doc).find((x) => x.clip.id === clipId);
    if (!p || p.clip.soundDetached) return doc;
    const c = p.clip;
    const sound: AudioClip = {
      id: newId('a'), assetId: c.assetId, versionId: c.versionId, label: c.label, sourceInTicks: c.sourceInTicks, sourceOutTicks: c.sourceOutTicks,
      recordInTicks: p.recordIn, gainDb: c.gainDb, muted: c.muted, fadeInTicks: c.fadeInTicks, fadeOutTicks: c.fadeOutTicks,
    };
    // 1.7.4: the volume keys go with the sound; an A1 crossfade does not carry to another track.
    if (c.volumeKeys?.length) sound.volumeKeys = structuredClone(c.volumeKeys);
    const free = (clips: AudioClip[]) => clips.every((x) => clipDuration(x) <= 0 || x.recordInTicks >= p.recordOut || x.recordInTicks + clipDuration(x) <= p.recordIn);
    let d = doc;
    let trackId = allAudioTracks(d).find((t) => free(t.clips))?.id;
    if (!trackId) {
      d = ops.addAudioTrack(d);
      if (d === doc) return doc;
      trackId = d.audioTracks!.at(-1)!.id;
    }
    d = withAudioClips(d, trackId, [...audioClipsOn(d, trackId)!, sound]);
    return { ...d, video: d.video.map((v) => (v.id === clipId ? withoutCrossfade({ ...v, soundDetached: true as const }) : v)) };
  },

  /**
   * Put a detached sound back into its picture (1.7.3): when relinkProblem is null, the audio clip is removed and the
   * V1 clip takes its gain, mute and sound fades and loses soundDetached. Otherwise the document is unchanged (the
   * editor shows relinkProblem's sentence, "Line the sound up with its picture to link it").
   */
  relink(doc: SequenceDocument, clipId: string, audioId: string): SequenceDocument {
    if (relinkProblem(doc, clipId, audioId)) return doc;
    const trackId = audioTrackOf(doc, audioId)!;
    const clips = audioClipsOn(doc, trackId)!;
    const a = clips.find((c) => c.id === audioId)!;
    const d = withAudioClips(doc, trackId, clips.filter((c) => c.id !== audioId));
    return tidyLinks({
      ...d,
      video: d.video.map((v) => {
        if (v.id !== clipId) return v;
        const next: VideoClip = { ...v, gainDb: a.gainDb, muted: a.muted, fadeInTicks: a.fadeInTicks, fadeOutTicks: a.fadeOutTicks };
        delete next.soundDetached;
        // 1.7.4: the keys come back with the sound.
        if (a.volumeKeys?.length) next.volumeKeys = structuredClone(a.volumeKeys); else delete next.volumeKeys;
        return tidyFades(next, doc.frameRate);
      }),
    });
  },

  /**
   * Link items into one group (1.7.3 ⌘L): every linkable item (V1, layer and audio clips, graphics) in `items` gets
   * one new linkId (newId('lk')), leaving any group it was in. Fewer than two linkable items: unchanged. Groups left
   * with one item are dissolved.
   */
  link(doc: SequenceDocument, items: readonly ItemRef[]): SequenceDocument {
    const ids = new Set(items.filter(isLinkable).map((r) => r.id));
    if (ids.size < 2) return doc;
    return tidyLinks(setLinkId(doc, ids, newId('lk')));
  },

  /**
   * Unlink (1.7.3 ⌘⇧L): the linkable items in `items` leave their groups, and each V1 clip among them whose sound is
   * attached is unlinked (unlink). Groups left with one item are dissolved.
   */
  unlinkGroup(doc: SequenceDocument, items: readonly ItemRef[]): SequenceDocument {
    let d = setLinkId(doc, new Set(items.filter(isLinkable).map((r) => r.id)), null);
    for (const r of items) if (r.kind === 'video') d = ops.unlink(d, r.id);
    d = tidyLinks(d);
    return d.video === doc.video && d.audio === doc.audio && d.graphics === doc.graphics && d.layers === doc.layers && d.audioTracks === doc.audioTracks ? doc : d;
  },

  /* ------------------------------------------------ 1.7.3: audio tracks */

  /** Add an audio track after the last (A3…A8; newId('t'), label "A<n>"); unchanged at MAX_AUDIO_TRACKS. */
  addAudioTrack(doc: SequenceDocument, label?: string): SequenceDocument {
    const tracks = doc.audioTracks ?? [];
    if (tracks.length >= MAX_AUDIO_TRACKS) return doc;
    return { ...doc, audioTracks: [...tracks, { id: newId('t'), label: (label ?? `A${tracks.length + 3}`).slice(0, 60), clips: [] }] };
  },

  /**
   * Remove an audio track (A3–A8) with its clips (the editor asks first when it has any), and its mute and solo.
   * V1 clips whose detached sound was on it stay detached (silent). The list is left out when it becomes empty.
   */
  removeAudioTrack(doc: SequenceDocument, trackId: string): SequenceDocument {
    if (!(doc.audioTracks ?? []).some((t) => t.id === trackId)) return doc;
    const next: SequenceDocument = { ...doc, audioTracks: doc.audioTracks!.filter((t) => t.id !== trackId) };
    if (!next.audioTracks!.length) delete next.audioTracks;
    return tidyLinks(doc.trackAudio?.[trackId] ? ops.setTrackAudio(next, trackId, { mute: false, solo: false }) : next);
  },

  /** Rename an audio track (A3–A8). Mute and solo are setTrackAudio's. */
  updateAudioTrack(doc: SequenceDocument, trackId: string, patch: { label?: string }): SequenceDocument {
    if (!(doc.audioTracks ?? []).some((t) => t.id === trackId) || patch.label === undefined) return doc;
    return { ...doc, audioTracks: doc.audioTracks!.map((t) => (t.id === trackId ? { ...t, label: patch.label!.slice(0, 60) } : t)) };
  },

  /**
   * Trim an audio clip's in or out point on its track (1.7.3; A2–A8): trimLayerClip's rules (the out point stays when
   * trimming the in point; not into a neighbour; within the source).
   */
  trimAudio(doc: SequenceDocument, id: string, edge: 'in' | 'out', deltaTicks: number, sourceDurationTicks: number): SequenceDocument {
    const trackId = audioTrackOf(doc, id);
    if (!trackId) return doc;
    return withAudioClips(doc, trackId, trimOnTrack(audioClipsOn(doc, trackId)!, id, edge, deltaTicks, sourceDurationTicks, doc.frameRate));
  },

  /* ------------------------------------------------ 1.7.3: graphics tracks */

  /** Add a graphics track above the others (GFX 2, GFX 3): graphicTracks; unchanged at MAX_GRAPHIC_TRACKS. */
  addGraphicTrack(doc: SequenceDocument): SequenceDocument {
    const count = graphicTrackCount(doc);
    if (count >= MAX_GRAPHIC_TRACKS) return doc;
    return { ...doc, graphicTracks: (count + 1) as 2 | 3 };
  },

  /**
   * Remove a graphics track with its graphics (the editor asks first when it has any); the tracks above move down.
   * GFX 1 is never removed while it is the only track. graphicTracks is left out when one track is left.
   */
  removeGraphicTrack(doc: SequenceDocument, track: GraphicTrack): SequenceDocument {
    const count = graphicTrackCount(doc);
    if (count <= 1 || track >= count) return doc;
    const next: SequenceDocument = {
      ...doc,
      graphics: doc.graphics.filter((g) => graphicTrackOf(g) !== track).map((g) => (graphicTrackOf(g) > track ? onGraphicTrack(g, (graphicTrackOf(g) - 1) as GraphicTrack) : g)),
    };
    if (count - 1 >= 2) next.graphicTracks = (count - 1) as 2 | 3; else delete next.graphicTracks;
    return tidyLinks(next);
  },

  /**
   * Move a graphic in time and to a graphics track (1.7.3; `track` left out: its own). Graphics on one track never
   * overlap: where it would, the nearest free position on that track (freeStart). Unchanged when nothing moves.
   */
  moveGraphic(doc: SequenceDocument, id: string, recordInTicks: number, track?: GraphicTrack): SequenceDocument {
    const g = doc.graphics.find((x) => x.id === id);
    if (!g) return doc;
    const dest = track ?? graphicTrackOf(g);
    if (dest < 0 || dest >= MAX_GRAPHIC_TRACKS) return doc;
    const taken = doc.graphics.filter((x) => x.id !== id && graphicTrackOf(x) === dest).map((x) => ({ start: x.recordInTicks, end: x.recordInTicks + x.durationTicks }));
    const at = freeStart(taken, snap(Math.max(0, recordInTicks), doc.frameRate), g.durationTicks);
    if (at === null || (at === g.recordInTicks && dest === graphicTrackOf(g))) return doc;
    return { ...doc, graphics: doc.graphics.map((x) => (x.id === id ? onGraphicTrack({ ...x, recordInTicks: at }, dest) : x)) };
  },

  /* ------------------------------------------------ 1.7.3: copy and paste (stream A writes the bodies) */

  /**
   * The clipboard payload for `items` (1.7.3 ⌘C, ⌘X): see ClipboardPayload. Gaps are left out; items not in the
   * document are skipped. Null when nothing is left. Not an edit (the document is not changed); ⌘X is copyItems
   * then liftClips.
   */
  copyItems(doc: SequenceDocument, items: readonly ItemRef[]): ClipboardPayload | null {
    const tl = toTimeline(doc);
    const tracks = allAudioTracks(doc);
    const seen = new Set<string>();
    const entries: Array<{ start: number; end: number; make: (offsetTicks: number) => ClipboardItem }> = [];
    for (const r of items) {
      if (r.kind === 'gap' || seen.has(itemKey(r))) continue;
      seen.add(itemKey(r));
      const span = itemSpan(doc, r);
      if (!span) continue;
      const add = (make: (offsetTicks: number) => ClipboardItem) => entries.push({ ...span, make });
      switch (r.kind) {
        case 'video': {
          const clip = structuredClone(tl.find((x) => x.clip.id === r.id)!.clip);
          delete clip.gapBeforeTicks;
          add((offsetTicks) => ({ kind: 'video', offsetTicks, clip }));
          break;
        }
        case 'audio': {
          const track = tracks.findIndex((t) => t.clips.some((c) => c.id === r.id));
          const clip = structuredClone(tracks[track]!.clips.find((c) => c.id === r.id)!);
          add((offsetTicks) => ({ kind: 'audio', offsetTicks, track, clip }));
          break;
        }
        case 'layer': {
          const layer = doc.layers.findIndex((l) => l.clips.some((c) => c.id === r.id));
          const clip = structuredClone(doc.layers[layer]!.clips.find((c) => c.id === r.id)!);
          add((offsetTicks) => ({ kind: 'layer', offsetTicks, layer, clip }));
          break;
        }
        case 'graphic': { const graphic = structuredClone(doc.graphics.find((g) => g.id === r.id)!); add((offsetTicks) => ({ kind: 'graphic', offsetTicks, graphic })); break; }
        case 'cue': { const cue = structuredClone(doc.subtitles!.cues.find((c) => c.id === r.id)!); add((offsetTicks) => ({ kind: 'cue', offsetTicks, cue })); break; }
        case 'marker': { const marker = structuredClone(doc.markers!.find((m) => m.id === r.id)!); add((offsetTicks) => ({ kind: 'marker', offsetTicks, marker })); break; }
      }
    }
    if (!entries.length) return null;
    const t0 = Math.min(...entries.map((e) => e.start));
    return { v: 1, frameRate: { ...doc.frameRate }, durationTicks: Math.max(...entries.map((e) => e.end)) - t0, items: entries.map((e) => e.make(e.start - t0)) };
  },

  /**
   * Paste a clipboard payload at `atTicks` (1.7.3 ⌘V 'overwrite', ⌘⇧V 'insert'): each item at atTicks plus its
   * offset, snapped to the document's frame grid (a payload of another rate is snapped). Every item gets a new id
   * (newId with its kind's prefix) and every link group a new linkId.
   *  - V1 clips: as placeVideo would land them ('overwrite' over what is there; 'insert' splits and pushes later,
   *    with `opts.ripple` 'sync' shifting the other tracks by the payload's V1 length).
   *  - Layer, audio and graphic clips: on the same track (by position) when it is free over their range; otherwise
   *    the next track of that kind that is (a new one when allowed: MAX_LAYERS, MAX_AUDIO_TRACKS, MAX_GRAPHIC_TRACKS);
   *    otherwise the nearest free time on their own track (freeStart).
   *  - Cues and markers: at their times (cues are skipped when the sequence has no subtitles), new cues kept apart
   *    from the existing ones as trimCue keeps them.
   *  - A V1 clip's sound stays attached unless it was soundDetached; a detached clip whose sound was not copied
   *    keeps soundDetached (silent), as copied.
   * `pasted` lists the new items in payload order.
   */
  pasteItems(doc: SequenceDocument, payload: ClipboardPayload, atTicks: number, mode: PlaceMode, opts: { ripple?: RippleMode } = {}): PasteResult {
    const rate = doc.frameRate;
    const tpf = ticksPerFrame(rate);
    const sn = (t: number) => snap(Math.max(0, t), rate);
    const at = sn(atTicks);
    // New link ids: the copies are linked to each other as the originals were (a group of one is no group).
    const counts = new Map<string, number>();
    for (const it of payload.items) {
      const l = it.kind === 'video' || it.kind === 'audio' || it.kind === 'layer' ? it.clip.linkId : it.kind === 'graphic' ? it.graphic.linkId : undefined;
      if (l) counts.set(l, (counts.get(l) ?? 0) + 1);
    }
    const links = new Map([...counts].filter(([, n]) => n >= 2).map(([l]) => [l, newId('lk')] as const));
    const relinked = <T extends { linkId?: string }>(x: T): T => {
      const n = structuredClone(x);
      const l = x.linkId ? links.get(x.linkId) : undefined;
      if (l) n.linkId = l; else delete n.linkId;
      return n;
    };
    const fresh = <T extends VideoClip | AudioClip | LayerClip>(c: T, prefix: string): T => {
      const n = relinked(c);
      n.id = newId(prefix);
      n.sourceInTicks = snap(c.sourceInTicks, rate);
      n.sourceOutTicks = snap(c.sourceOutTicks, rate);
      return tidyFades(n, rate);
    };
    const pasted: Array<ItemRef | null> = payload.items.map(() => null);
    let d = doc;

    // V1: as placeVideo lands clips.
    const vids = payload.items.flatMap((it, k) => (it.kind === 'video' ? [{ k, offset: sn(it.offsetTicks), clip: fresh(it.clip, 'v') }] : []))
      .filter((v) => clipDuration(v.clip) > 0);
    if (vids.length) {
      for (const v of vids) { delete v.clip.gapBeforeTicks; pasted[v.k] = { kind: 'video', id: v.clip.id }; }
      const order = [...vids].sort((a, b) => a.offset - b.offset);
      const pred = new Map(order.map((v, i) => [v.clip.id, order[i - 1]?.clip.id ?? null] as const));
      const first = order[0]!.offset;
      if (mode === 'overwrite') {
        const items = toTimeline(d);
        d = overwriteV1(d, items, order.map((v) => itemAt(v.clip, at + v.offset)), new Map([...predecessors(items), ...pred]));
      } else {
        const length = Math.max(...order.map((v) => v.offset + clipDuration(v.clip))) - first;
        d = insertV1(d, order.map((v) => ({ clip: v.clip, offset: v.offset - first })), at + first, length, opts.ripple ?? 'v1', new Set(), pred);
      }
    }

    /**
     * The track a group of items copied from one track goes on: its own (by position) when free over all of them, else
     * the next one that is, else a new one (`add`; false when not allowed), else its own or the last there is (each item
     * then at the nearest free time).
     */
    const pickTrack = (src: number, count: () => number, takenOn: (i: number) => Span[], want: Span[], add: () => boolean): { index: number; free: boolean } => {
      for (let i = src; i < count(); i++) if (freeOver(takenOn(i), want)) return { index: i, free: true };
      if (add()) return { index: count() - 1, free: true };
      return { index: Math.min(src, count() - 1), free: false };
    };
    const group = <T,>(list: T[], key: (x: T) => number): T[][] => [...new Set(list.map(key))].sort((a, b) => a - b).map((g) => list.filter((x) => key(x) === g));

    // Layer clips.
    const layerItems = payload.items.flatMap((it, k) => (it.kind === 'layer' ? [{ ...it, k }] : []));
    for (const g of group(layerItems, (x) => x.layer)) {
      const clips = g.map((x) => ({ k: x.k, clip: { ...fresh(x.clip, 'p'), recordInTicks: at + sn(x.offsetTicks) } })).filter((x) => clipDuration(x.clip) > 0);
      if (!clips.length) continue;
      const want = clips.map((x) => ({ start: x.clip.recordInTicks, end: x.clip.recordInTicks + clipDuration(x.clip) }));
      const pick = pickTrack(g[0]!.layer, () => (d.layers ?? []).length, (i) => spans(d.layers[i]!.clips), want, () => { const n = ops.addLayer(d); if (n === d) return false; d = n; return true; });
      if (pick.index < 0) continue;
      const l = d.layers[pick.index]!;
      const placedAt = pick.free ? null : landOnTrack(spans(l.clips), clips.map((x) => ({ id: x.clip.id, want: x.clip.recordInTicks, len: clipDuration(x.clip) })));
      const add = clips.map((x) => (placedAt ? { ...x.clip, recordInTicks: placedAt.get(x.clip.id)! } : x.clip));
      d = { ...d, layers: d.layers.map((y, i) => (i === pick.index ? { ...y, clips: [...y.clips, ...add] } : y)) };
      for (const x of clips) pasted[x.k] = { kind: 'layer', id: x.clip.id };
    }

    // Audio clips.
    const audioItems = payload.items.flatMap((it, k) => (it.kind === 'audio' ? [{ ...it, k }] : []));
    for (const g of group(audioItems, (x) => x.track)) {
      const clips = g.map((x) => ({ k: x.k, clip: { ...fresh(x.clip, 'a'), recordInTicks: at + sn(x.offsetTicks) } })).filter((x) => clipDuration(x.clip) > 0);
      if (!clips.length) continue;
      const want = clips.map((x) => ({ start: x.clip.recordInTicks, end: x.clip.recordInTicks + clipDuration(x.clip) }));
      const pick = pickTrack(g[0]!.track, () => allAudioTracks(d).length, (i) => spans(allAudioTracks(d)[i]!.clips), want, () => { const n = ops.addAudioTrack(d); if (n === d) return false; d = n; return true; });
      const t = allAudioTracks(d)[pick.index]!;
      const placedAt = pick.free ? null : landOnTrack(spans(t.clips), clips.map((x) => ({ id: x.clip.id, want: x.clip.recordInTicks, len: clipDuration(x.clip) })));
      d = withAudioClips(d, t.id, [...t.clips, ...clips.map((x) => (placedAt ? { ...x.clip, recordInTicks: placedAt.get(x.clip.id)! } : x.clip))]);
      for (const x of clips) pasted[x.k] = { kind: 'audio', id: x.clip.id };
    }

    // Graphics: their template version and data as copied.
    const gfxItems = payload.items.flatMap((it, k) => (it.kind === 'graphic' ? [{ ...it, k }] : []));
    for (const g of group(gfxItems, (x) => graphicTrackOf(x.graphic))) {
      const list = g.map((x) => {
        const n = relinked(x.graphic);
        n.id = newId('g');
        n.recordInTicks = at + sn(x.offsetTicks);
        n.durationTicks = Math.max(tpf, snap(x.graphic.durationTicks, rate));
        return { k: x.k, g: tidyGraphic(n, rate) };
      });
      const want = list.map((x) => ({ start: x.g.recordInTicks, end: x.g.recordInTicks + x.g.durationTicks }));
      const takenOn = (i: number) => d.graphics.filter((y) => graphicTrackOf(y) === i).map((y) => ({ start: y.recordInTicks, end: y.recordInTicks + y.durationTicks }));
      let extra = 0;
      const pick = pickTrack(graphicTrackOf(g[0]!.graphic), () => graphicTrackCount(d) + extra, takenOn, want, () => { if (graphicTrackCount(d) + extra >= MAX_GRAPHIC_TRACKS) return false; extra++; return true; });
      const placedAt = pick.free ? null : landOnTrack(takenOn(pick.index), list.map((x) => ({ id: x.g.id, want: x.g.recordInTicks, len: x.g.durationTicks })));
      const add = list.map((x) => ({ k: x.k, g: onGraphicTrack(placedAt ? { ...x.g, recordInTicks: placedAt.get(x.g.id)! } : x.g, pick.index as GraphicTrack) }));
      d = { ...d, graphics: [...d.graphics, ...add.map((x) => x.g)] };
      for (const x of add) pasted[x.k] = { kind: 'graphic', id: x.g.id };
    }

    // Cues (with subtitles only), kept apart from the cues there are; markers.
    for (const [k, it] of payload.items.entries()) {
      if (it.kind !== 'cue' || !d.subtitles) continue;
      const len = Math.max(0, it.cue.endTicks - it.cue.startTicks);
      const cues = d.subtitles.cues;
      const want = at + sn(it.offsetTicks);
      const start = freeStart(cues.map((c) => ({ start: c.startTicks, end: c.endTicks })), want, len) ?? want;
      const cue: Cue = { ...structuredClone(it.cue), id: newId('c'), startTicks: start, endTicks: start + len };
      d = { ...d, subtitles: { ...d.subtitles, cues: [...cues, cue] } };
      pasted[k] = { kind: 'cue', id: cue.id };
    }
    for (const [k, it] of payload.items.entries()) {
      if (it.kind !== 'marker' || (d.markers ?? []).length >= MAX_MARKERS) continue;
      const marker: Marker = { ...structuredClone(it.marker), id: newId('k'), atTicks: at + sn(it.offsetTicks) };
      d = withMarkers(d, [...(d.markers ?? []), marker]);
      pasted[k] = { kind: 'marker', id: marker.id };
    }
    return { doc: d, pasted: pasted.filter((x): x is ItemRef => Boolean(x)) };
  },

  /* ------------------------------------------------ 1.7.4: trim tools (docs/dev/editor-1.7.4-build.md) */

  /**
   * Roll (⇧B): move the cut before `ref` (the incoming clip; 'video', 'layer', 'audio' or 'graphic') by `deltaTicks`
   * (snapped; later is positive). The clip before it on its track must end exactly where it starts (V1: the clip
   * before it, a gap between them: no roll; a transition at the cut moves with it, clamped to maxTransitionTicks), or
   * the document is unchanged. The outgoing clip's out and the incoming clip's in move together: outgoing sourceOut
   * += d; incoming sourceIn += d and, off V1, recordInTicks += d (graphics: outgoing durationTicks += d, incoming
   * recordInTicks += d and durationTicks −= d, step marks and updates kept in place). The total length and every other
   * item stay. Clamped (whole frames) so that each clip keeps at least a frame, the outgoing out stays within its
   * media (sourceDurationOf(versionId), mediaEnd) and the incoming in at 0 or later. Fades are refitted (tidyFades).
   * One clip pair only: link groups do not follow (the editor may roll a linked cut itself). Coalesce key
   * `roll:<id>` for a drag.
   */
  rollEdit(doc: SequenceDocument, ref: ItemRef, deltaTicks: number, sourceDurationOf: (versionId: string) => number): SequenceDocument {
    const rate = doc.frameRate;
    const tpf = ticksPerFrame(rate);
    const want = snap(deltaTicks, rate);
    if (!want) return doc;
    /** The roll clamped: later by at most `grow` (the outgoing's media, the incoming's length), earlier by at most `shrink`. */
    const clampRoll = (grow: number, shrink: number) => (want > 0 ? Math.min(want, wholeFrames(Math.max(0, grow), tpf)) : -Math.min(-want, wholeFrames(Math.max(0, shrink), tpf)));
    if (ref.kind === 'video') {
      const i = doc.video.findIndex((c) => c.id === ref.id);
      const clip = doc.video[i];
      const prev = doc.video[i - 1];
      if (!clip || !prev || gapBefore(clip, rate) > 0) return doc;
      const d = clampRoll(Math.min(mediaEnd(doc, prev, sourceDurationOf(prev.versionId)) - prev.sourceOutTicks, clipDuration(clip) - tpf), Math.min(clipDuration(prev) - tpf, clip.sourceInTicks));
      if (!d) return doc;
      // A multicam clip keeps to its angle's picture.
      const covers = (c: VideoClip, sIn: number, sOut: number) => {
        const g = c.multicam && (doc.multicam ?? []).find((x) => x.id === c.multicam!.groupId);
        const a = g?.angles[c.multicam!.angle];
        return !g || !a || angleCovers(g, c.multicam!.angle, a.startTicks + sIn, a.startTicks + sOut);
      };
      if (!covers(prev, prev.sourceInTicks, prev.sourceOutTicks + d) || !covers(clip, clip.sourceInTicks + d, clip.sourceOutTicks)) return doc;
      const video = [...doc.video];
      video[i - 1] = tidyFades({ ...prev, sourceOutTicks: prev.sourceOutTicks + d }, rate);
      video[i] = tidyFades({ ...clip, sourceInTicks: clip.sourceInTicks + d }, rate);
      return { ...doc, video };
    }
    if (ref.kind === 'graphic') {
      const g = doc.graphics.find((x) => x.id === ref.id);
      const prev = g && doc.graphics.find((x) => x.id !== g.id && graphicTrackOf(x) === graphicTrackOf(g) && x.durationTicks > 0 && x.recordInTicks + x.durationTicks === g.recordInTicks);
      if (!g || !prev) return doc;
      const d = clampRoll(g.durationTicks - tpf, prev.durationTicks - tpf);
      if (!d) return doc;
      return { ...doc, graphics: doc.graphics.map((x) => (x.id === prev.id ? tidyGraphic({ ...x, durationTicks: x.durationTicks + d }, rate) : x.id === g.id ? graphicTail(x, d, rate) : x)) };
    }
    if (ref.kind !== 'layer' && ref.kind !== 'audio') return doc;
    const roll = <T extends LayerClip | AudioClip>(clips: T[]): T[] | null => {
      const c = clips.find((x) => x.id === ref.id);
      const prev = c && clips.find((x) => x.id !== c.id && clipDuration(x) > 0 && x.recordInTicks + clipDuration(x) === c.recordInTicks);
      if (!c || !prev) return null;
      const d = clampRoll(Math.min(mediaEnd(doc, prev, sourceDurationOf(prev.versionId)) - prev.sourceOutTicks, clipDuration(c) - tpf), Math.min(clipDuration(prev) - tpf, c.sourceInTicks));
      if (!d) return null;
      return clips.map((x) => (x.id === prev.id ? tidyFades({ ...x, sourceOutTicks: x.sourceOutTicks + d }, rate) : x.id === c.id ? tidyFades({ ...x, sourceInTicks: x.sourceInTicks + d, recordInTicks: x.recordInTicks + d }, rate) : x));
    };
    if (ref.kind === 'audio') {
      const trackId = audioTrackOf(doc, ref.id);
      const clips = trackId ? roll(audioClipsOn(doc, trackId)!) : null;
      return clips ? withAudioClips(doc, trackId!, clips) : doc;
    }
    const layer = (doc.layers ?? []).find((l) => l.clips.some((c) => c.id === ref.id));
    const clips = layer ? roll(layer.clips) : null;
    return clips ? withLayerClips(doc, layer!.id, clips) : doc;
  },

  /**
   * Ripple trim (B, and ⌘-drag on an edge): trim the `edge` of the item `ref` on ANY track ('video', 'layer', 'audio',
   * 'graphic', 'cue') by `deltaTicks` (snapped, whole frames), and move everything after it on its track by the change
   * in length, so nothing after it opens a gap or is overwritten:
   *  - 'out': the end moves by d (sourceOutTicks += d; a graphic or cue: its length); the items on the track starting at
   *    or after the old end move by d.
   *  - 'in': the item's record start stays where it is and its source In moves by d (sourceInTicks += d; a graphic:
   *    its length −d, its step marks and updates keep their place against its content); the items after it move by −d.
   * V1 is the storyline (`trimClip` is the 'v1' form of this for one clip). Clamped like trimClip: at least a frame,
   * within the media (sourceDurationTicks; 0 or less: no limit), the source In at 0 or later; on a free track a
   * lengthened 'in' never makes later items overlap (they move with it).
   * `ripple`: 'v1' moves only the item's own track; 'sync' also shifts every other track (V1 included when the item is
   * not on V1) from the edit point P = the item's old end, by the same amount, as rippleDelete's sync rule: on each
   * track the items starting at or after P move as one, never into the item before them nor before 0; a shortening is
   * limited together (syncRoom) so that every track can follow. Link groups do not follow.
   */
  rippleTrim(doc: SequenceDocument, ref: ItemRef, edge: 'in' | 'out', deltaTicks: number, sourceDurationTicks: number, ripple: RippleMode): SequenceDocument {
    const rate = doc.frameRate;
    const tpf = ticksPerFrame(rate);
    let d = wholeFrames(snap(deltaTicks, rate), tpf);
    const span = ref.kind === 'gap' || ref.kind === 'marker' ? null : itemSpan(doc, ref);
    if (!d || !span) return doc;
    const P = span.end;
    const len = span.end - span.start;
    // The source limits of a clip (none for a graphic or cue): the In at 0 or later, the Out within the media.
    const clip = ref.kind === 'video' || ref.kind === 'layer' || ref.kind === 'audio' ? allClips(doc).find((c) => c.clip.id === ref.id)?.clip : undefined;
    if (clip) {
      const end = mediaEnd(doc, clip as VideoClip, sourceDurationTicks);
      d = edge === 'out' ? Math.min(d, wholeFrames(Math.max(0, end - clip.sourceOutTicks), tpf)) : Math.max(d, -wholeFrames(Math.max(0, clip.sourceInTicks), tpf));
    }
    // At least a frame: shortening by at most len − 1 frame.
    const shorten = (x: number) => (edge === 'out' ? -x : x);
    if (shorten(d) > len - tpf) d = edge === 'out' ? -(len - tpf) : len - tpf;
    // The change in length (Δ) everything after it follows; with Sync a shortening is limited by what every track allows.
    let delta = edge === 'out' ? d : -d;
    if (ripple === 'sync' && delta < 0) {
      const room = Math.min(syncRoom(doc, P, ownTrackKeys(doc, ref)), ref.kind === 'video' ? Number.POSITIVE_INFINITY : v1Room(doc, P));
      delta = Math.max(delta, -wholeFrames(Math.max(0, room), tpf));
      d = edge === 'out' ? delta : -delta;
    }
    if (!delta) return doc;
    let out: SequenceDocument;
    const later = <T extends { id: string; recordInTicks: number }>(xs: T[], f: (x: T) => T): T[] => xs.map((x) => (x.id === ref.id ? f(x) : x.recordInTicks >= P ? { ...x, recordInTicks: Math.max(0, x.recordInTicks + delta) } : x));
    const trimSource = <T extends AudioClip | LayerClip | VideoClip>(c: T): T => tidyFades(edge === 'out' ? { ...c, sourceOutTicks: c.sourceOutTicks + d } : { ...c, sourceInTicks: c.sourceInTicks + d }, rate);
    switch (ref.kind) {
      case 'video': out = { ...doc, video: doc.video.map((c) => (c.id === ref.id ? trimSource(c) : c)) }; break;
      case 'audio': { const t = audioTrackOf(doc, ref.id)!; out = withAudioClips(doc, t, later(audioClipsOn(doc, t)!, trimSource)); break; }
      case 'layer': { const l = doc.layers.find((x) => x.clips.some((c) => c.id === ref.id))!; out = withLayerClips(doc, l.id, later(l.clips, trimSource)); break; }
      case 'graphic': {
        const g = doc.graphics.find((x) => x.id === ref.id)!;
        const k = graphicTrackOf(g);
        const trimmed = edge === 'out' ? tidyGraphic({ ...g, durationTicks: g.durationTicks + d }, rate) : { ...graphicTail(g, d, rate), recordInTicks: g.recordInTicks };
        out = { ...doc, graphics: doc.graphics.map((x) => (x.id === g.id ? trimmed : graphicTrackOf(x) === k && x.recordInTicks >= P ? { ...x, recordInTicks: Math.max(0, x.recordInTicks + delta) } : x)) };
        break;
      }
      case 'cue': {
        const cues = doc.subtitles!.cues.map((c) => (c.id === ref.id ? { ...c, endTicks: c.endTicks + delta } : c.startTicks >= P ? { ...c, startTicks: Math.max(0, c.startTicks + delta), endTicks: Math.max(0, c.endTicks + delta) } : c));
        out = { ...doc, subtitles: { ...doc.subtitles!, cues } };
        break;
      }
      default: return doc;
    }
    if (ripple === 'sync') {
      out = shiftOthers(out, P, delta, ownTrackKeys(doc, ref));
      if (ref.kind !== 'video') out = shiftV1From(out, P, delta);
    }
    return tidyAudioCrossfades(doc, out);
  },

  /**
   * Drag pictures between video tracks (1.7.4, Mikael; plan 7b "Dragging between V1 and the video tracks"). The
   * PRIMARY picture is the first 'video' or 'layer' item in `items`; it goes to `target` ('v1', `{ layer }` or 'new':
   * a new video track above the top one); every other picture in `items` goes the same number of video tracks up or
   * down (lanes: V1 = 0, layers[i] = i + 1; below 0: V1; above the top: new tracks, up to V9 = MAX_LAYERS layers,
   * beyond that clamped to the top, with a note). All of them also move `deltaTicks` in time (snapped; clamped as one
   * at 0). The other items in `items` (audio, graphics, cues, markers) move by `deltaTicks` on their own tracks, as
   * moveItems, except the linked sound of a moved picture (below).
   *
   * V1 → a video track: the clip becomes a layer clip at its record time (+ delta; nearest free time on that layer,
   * landOnTrack), keeping its id. V1 lifts (a gap) with `mode` 'overwrite', or ripples ('insert', ⌘) with
   * `opts.ripple` (the Sync rule, rippleDelete). Kept: the source range, fit/reframe/zoom/keys and formatFraming,
   * colour, picture fades, gainDb, muted, sound fades, volumeKeys, linkId; a layer clip's defaults for opacity 1,
   * scale 1, x 0, y 0. Lost: transitionIn (and the next V1 clip's transitionIn from it), gapBeforeTicks,
   * crossfadeInTicks, multicam, soundDetached; fit 'blur' becomes 'fill'. Its sound (unless soundDetached):
   * A1 moves to the audio track as many tracks down as the picture went up (V1 → V2: A2; V1 → V3: A3; A2 = 'a2',
   * A3… = audioTracks), as an audio clip (newId('a')) with the same source range, gainDb, sound fades and volumeKeys
   * at the same record time, linked to the layer clip (its linkId, or a new one), and the layer clip is muted. When
   * that track is busy over that time, the next audio track below that is free, then a new one (addAudioTrack, up
   * to A8); with no room at all the sound stays in the layer clip (not muted) and a note says so. A clip with
   * soundDetached moves alone: its sound (linked or not, in `items` or not) keeps its track (if in `items`, it moves
   * by `deltaTicks` only). A multicam clip stays where it is, with a note.
   *
   * A video track → V1: the clip lands on V1 as placeVideo lands (`mode` 'overwrite', or 'insert' with `opts.ripple`),
   * keeping its id. Lost: opacity, scale, x, y, motion, hideIn, and a layer formatFraming entry's place/motion (its
   * framing kept); a note lists what was set and left behind ("Left on the track: opacity, size, position, motion,
   * hidden formats"). Its linked sound (an audio clip in its link group): when it lines up exactly with the landed
   * picture (same version, record start and source range: relinkProblem's rule), it is removed and becomes the V1
   * clip's own sound (its gainDb, muted, fades and volumeKeys; the link dissolves if nothing else is in it); when it
   * is offset (a J/L cut) the V1 clip gets soundDetached and the audio clip stays linked and moves by `deltaTicks`.
   * Without linked sound the layer clip's own sound (gain, muted) becomes A1.
   *
   * A video track → another video track: the clip moves layer; its linked sound moves by the same number of audio
   * tracks (down for up) when that track is free over its time (no new audio track for this); otherwise it stays on
   * its track (moving by `deltaTicks`).
   *
   * Link groups: the editor passes the whole group (linkedItems). Returns the document, the moved items' refs (kind
   * as they are now: a V1 clip moved up is { kind: 'layer', id }) and notes for the editor's toast. Unchanged (the
   * same document, moved: [], notes) when nothing can move.
   */
  moveToVideoTrack(doc: SequenceDocument, items: readonly ItemRef[], target: VideoTrackTarget, deltaTicks: number, opts: { mode?: PlaceMode; ripple?: RippleMode } = {}): TrackMoveResult {
    const rate = doc.frameRate;
    const mode = opts.mode ?? 'overwrite';
    const ripple = opts.ripple ?? 'v1';
    const notes: string[] = [];
    const note = (n: string) => { if (!notes.includes(n)) notes.push(n); };
    const seen = new Set<string>();
    const refs = items.filter((r) => r.kind !== 'gap' && !seen.has(itemKey(r)) && Boolean(seen.add(itemKey(r))) && itemSpan(doc, r));
    const pics = refs.filter((r) => r.kind === 'video' || r.kind === 'layer');
    const unchanged = (): TrackMoveResult => ({ doc, moved: [], notes });
    if (!pics.length) return unchanged();
    const layers0 = doc.layers ?? [];
    const laneOf = (r: ItemRef) => (r.kind === 'video' ? 0 : layers0.findIndex((l) => l.clips.some((c) => c.id === r.id)) + 1);
    let targetLane: number;
    if (target === 'v1') targetLane = 0;
    else if (target === 'new') targetLane = layers0.length + 1;
    else {
      const i = layers0.findIndex((l) => l.id === target.layer);
      if (i < 0) return unchanged();
      targetLane = i + 1;
    }
    const laneDelta = targetLane - laneOf(pics[0]!);
    const d = Math.max(snap(deltaTicks, rate), -Math.min(...refs.map((r) => itemSpan(doc, r)!.start)));
    const v1Start = new Map(layoutVideo(doc).map((p) => [p.clip.id, p.recordIn]));
    const videoOf = (id: string) => doc.video.find((c) => c.id === id)!;
    const layerClipOf = (id: string) => layers0.flatMap((l) => l.clips).find((c) => c.id === id)!;

    // Where each picture goes: lanes count V1 = 0, layers[i] = i + 1; above V9 is clamped; multicam stays on V1.
    const plans = pics.map((r) => {
      const from = laneOf(r);
      let to = Math.max(0, from + laneDelta);
      if (to > MAX_LAYERS) { to = MAX_LAYERS; note('A sequence has at most nine video tracks'); }
      const frozen = r.kind === 'video' && to > 0 && Boolean(videoOf(r.id).multicam);
      if (frozen) note('Multicam clips stay on V1');
      return { r, from, to: frozen ? 0 : to, frozen };
    });
    const moving = plans.filter((p) => !p.frozen);
    if (!moving.length || (!d && moving.every((p) => p.from === p.to))) return unchanged();

    // The linked sound of a layer picture that changes track (an audio clip of its link group, of its own source).
    const audioIndex = new Map(allAudioTracks(doc).flatMap((t, i) => t.clips.map((c) => [c.id, { clip: c, track: t.id, index: i }] as const)));
    const soundOf = new Map<string, { clip: AudioClip; track: string; index: number }>();
    for (const p of moving) {
      if (p.r.kind !== 'layer' || p.from === p.to) continue;
      const lc = layerClipOf(p.r.id);
      const s = lc.linkId ? [...audioIndex.values()].find((x) => x.clip.linkId === lc.linkId && x.clip.versionId === lc.versionId && ![...soundOf.values()].includes(x)) : undefined;
      if (s) soundOf.set(lc.id, s);
    }
    const handled = new Set([...soundOf.values()].map((s) => s.clip.id));
    const others = refs.filter((r) => r.kind !== 'video' && r.kind !== 'layer' && !(r.kind === 'audio' && handled.has(r.id)));
    // What the sync ripple of a V1 lift or insert leaves alone (moved here by the delta).
    const skip = new Set([...others.map(itemKey), ...[...handled].map((id) => itemKey({ kind: 'audio', id })), ...moving.filter((p) => p.r.kind === 'layer').map((p) => itemKey(p.r))]);

    let out = doc;
    // New video tracks above the top (up to V9).
    const top = Math.max(...moving.map((p) => p.to));
    while ((out.layers ?? []).length < top) { const n = ops.addLayer(out); if (n === out) break; out = n; }
    const laneIds = (out.layers ?? []).map((l) => l.id);

    // Layer pictures leave their layers (they land again below).
    const leaving = new Set(moving.filter((p) => p.r.kind === 'layer').map((p) => p.r.id));
    if (leaving.size) out = { ...out, layers: out.layers.map((l) => (l.clips.some((c) => leaving.has(c.id)) ? { ...l, clips: l.clips.filter((c) => !leaving.has(c.id)) } : l)) };

    // V1 pictures going up: V1 lifts (a gap) or, with insert, ripples (the Sync rule).
    const up = moving.filter((p) => p.r.kind === 'video' && p.to > 0);
    const upIds = new Set(up.map((p) => p.r.id));
    if (upIds.size) {
      if (mode === 'insert') out = rippleV1(out, upIds, ripple, skip).doc;
      else { const tl = toTimeline(out); out = withItems(out, tl.filter((it) => !upIds.has(it.clip.id)), predecessors(tl)); }
    }
    // V1 pictures staying on V1 move in time.
    const stay = moving.filter((p) => p.r.kind === 'video' && p.to === 0).map((p) => p.r.id);
    if (stay.length && d) out = placeV1(out, stay, d, mode, ripple, skip);

    // Pictures coming down to V1: as a V1 drag lands them; what V1 has no place for is left behind (a note).
    const down = moving.filter((p) => p.r.kind === 'layer' && p.to === 0).map((p) => {
      const lc = layerClipOf(p.r.id);
      const { clip, left } = toVideoClip(lc);
      return { clip, start: lc.recordInTicks + d, left };
    }).sort((a, b) => a.start - b.start);
    const rejoined = new Set<string>();
    if (down.length) {
      const leftBehind = ['opacity', 'size', 'position', 'motion', 'hidden formats'].filter((k) => down.some((x) => x.left.includes(k)));
      if (leftBehind.length) note(`Left on the track: ${leftBehind.join(', ')}`);
      if (mode === 'overwrite') {
        const tl = toTimeline(out);
        out = overwriteV1(out, tl, down.map((x) => itemAt(x.clip, x.start)), predecessors(tl));
      } else {
        const first = down[0]!.start;
        const length = Math.max(...down.map((x) => x.start + clipDuration(x.clip))) - first;
        out = insertV1(out, down.map((x) => ({ clip: x.clip, offset: x.start - first })), first, length, ripple, skip, new Map());
      }
      // Linked sound that lines up exactly with the landed picture becomes its own sound again; offset sound stays
      // linked on its track and the picture's sound is detached.
      const landed = new Map(toTimeline(out).map((it) => [it.clip.id, it.start]));
      const patch = new Map<string, (c: VideoClip) => VideoClip>();
      for (const x of down) {
        const s = soundOf.get(x.clip.id);
        if (!s) continue;
        const a = s.clip;
        if (landed.get(x.clip.id) === a.recordInTicks + d && a.versionId === x.clip.versionId && a.sourceInTicks === x.clip.sourceInTicks && a.sourceOutTicks === x.clip.sourceOutTicks) {
          rejoined.add(a.id);
          patch.set(x.clip.id, (c) => {
            const next: VideoClip = { ...c, gainDb: a.gainDb, muted: a.muted, fadeInTicks: a.fadeInTicks, fadeOutTicks: a.fadeOutTicks };
            if (a.volumeKeys?.length) next.volumeKeys = structuredClone(a.volumeKeys); else delete next.volumeKeys;
            return tidyFades(next, rate);
          });
        } else patch.set(x.clip.id, (c) => ({ ...c, soundDetached: true as const }));
      }
      if (patch.size) out = { ...out, video: out.video.map((c) => patch.get(c.id)?.(c) ?? c) };
      if (rejoined.size) out = removeOthers(out, [...rejoined].map((id) => ({ kind: 'audio' as const, id })));
    }

    // Pictures landing on video tracks (from V1 or another track), at their time plus the delta (the nearest free time there).
    const created: ItemRef[] = [];
    const arrivals = moving.filter((p) => p.to > 0).map((p) => {
      if (p.r.kind === 'video') {
        const v = videoOf(p.r.id);
        const lc = toLayerClip(v, v1Start.get(v.id)! + d);
        if (v.soundDetached) lc.muted = true; // its sound is on an audio track already
        return { p, clip: lc, fromV1: !v.soundDetached ? v : null };
      }
      const lc = layerClipOf(p.r.id);
      return { p, clip: { ...lc, recordInTicks: lc.recordInTicks + d }, fromV1: null };
    });
    for (let lane = 1; lane <= laneIds.length; lane++) {
      const here = arrivals.filter((x) => x.p.to === lane);
      if (!here.length) continue;
      const lid = laneIds[lane - 1]!;
      const clips = out.layers.find((l) => l.id === lid)!.clips;
      const at = landOnTrack(spans(clips), here.map((x) => ({ id: x.clip.id, want: x.clip.recordInTicks, len: clipDuration(x.clip) })));
      for (const x of here) x.clip = { ...x.clip, recordInTicks: at.get(x.clip.id)! };
      out = withLayerClips(out, lid, [...clips, ...here.map((x) => x.clip)]);
    }
    // A V1 picture's sound (A1) goes as many audio tracks down as it went up: that track, the next free one below, a
    // new one (up to A8), or, with no room, it stays in the clip.
    for (const x of arrivals) {
      if (!x.fromV1) continue;
      const r = soundToAudioTrack(out, laneIds[x.p.to - 1]!, x.clip.id, x.fromV1);
      if (!r.sound) { note(NO_AUDIO_TRACK); continue; }
      out = r.doc;
      created.push(r.sound);
    }
    // The linked sound of a picture moving between video tracks: as many audio tracks down as it went up when that
    // track is free there, else on its own track; and (any picture) by the delta in time.
    for (const [picId, s] of soundOf) {
      if (rejoined.has(s.clip.id)) continue;
      const plan = moving.find((p) => p.r.id === picId)!;
      const len = clipDuration(s.clip);
      const want = s.clip.recordInTicks + d;
      const tracks = allAudioTracks(out);
      const cur = tracks.findIndex((t) => t.clips.some((c) => c.id === s.clip.id));
      if (cur < 0) continue;
      const rest = (i: number) => tracks[i]!.clips.filter((c) => c.id !== s.clip.id);
      let dest = cur;
      const wantIdx = cur + (plan.to - plan.from);
      if (plan.to > 0 && wantIdx !== cur && wantIdx >= 0 && wantIdx < tracks.length && freeOver(spans(rest(wantIdx)), [{ start: want, end: want + len }])) dest = wantIdx;
      const at = dest === cur ? landOnTrack(spans(rest(cur)), [{ id: s.clip.id, want, len }]).get(s.clip.id)! : want;
      const moved = { ...tracks[cur]!.clips.find((c) => c.id === s.clip.id)!, recordInTicks: at };
      if (dest === cur) out = withAudioClips(out, tracks[cur]!.id, tracks[cur]!.clips.map((c) => (c.id === s.clip.id ? moved : c)));
      else {
        out = withAudioClips(out, tracks[cur]!.id, rest(cur));
        out = withAudioClips(out, tracks[dest]!.id, [...allAudioTracks(out)[dest]!.clips, moved]);
      }
    }
    // Everything else in `items` moves by the delta on its own track.
    if (others.length && d) out = ops.moveItems(out, others, d);
    out = tidyAudioCrossfades(doc, tidyLinks(out));
    if (sameTracks(doc, out)) return unchanged();
    const kindNow = (r: ItemRef): ItemRef | null => {
      if (r.kind !== 'video' && r.kind !== 'layer') return rejoined.has(r.id) ? null : r;
      const p = plans.find((x) => x.r.id === r.id && x.r.kind === r.kind)!;
      if (p.frozen) return null;
      return { kind: p.to === 0 ? 'video' : 'layer', id: r.id };
    };
    const moved = [...refs.map(kindNow).filter((r): r is ItemRef => Boolean(r)), ...created];
    for (const s of soundOf.values()) if (!rejoined.has(s.clip.id) && !moved.some((r) => r.kind === 'audio' && r.id === s.clip.id)) moved.push({ kind: 'audio', id: s.clip.id });
    return { doc: out, moved, notes };
  },

  /** ⌘K and Blade ⇧-click: split every track (V1, the layers, A2–A8, GFX 1–3) at `atTicks`; splitAt with every track. */
  splitAll(doc: SequenceDocument, atTicks: number): SequenceDocument {
    return ops.splitAt(doc, atTicks, { tracks: allTrackRefs(doc) });
  },

  /* ------------------------------------------------ 1.7.4: three-point editing */

  /**
   * Overwrite (.): put `src` (its sourceIn/Out are the resolved three-point range: three-point.ts resolveThreePoint)
   * at `recordInTicks` (snapped) on the targeted tracks, replacing what is there over [recordIn, recordIn + length):
   *  - targets.video 'v1': a V1 clip there (as placeVideo's overwrite lands: clips under it trimmed, split or removed,
   *    gaps used up; past the end a gap opens before it); `{ layer }`: a layer clip on that layer, the layer's clips
   *    under it trimmed, split or removed. New clips as insertClip / addLayerClip make them (defaultFit, gainDb 0).
   *  - targets.audio 'with-picture': the picture's own sound (A1 on V1; 1.8.0, on a layer: that layer's audio track, V2 → A2,
   *    made up to it, as `{ track }` below, unless the source is known to be silent); `{ track }`: the
   *    picture lands muted (muted: true) and its sound is an audio clip (gainDb 0) at the same record time on that
   *    track (overwriting there), linked to the picture with a new linkId; null: the picture lands muted.
   *  - targets.video null with `{ track }`: a sound-only edit on that track; with 'with-picture' or null: no edit.
   * Only the targeted tracks change (overwrite never moves anything). An unknown layer or track: no edit (the editor
   * resolves targets first: resolveTargets). Returns the document and the new items (the editor selects them);
   * `{ doc, placed: [] }` with the same document when nothing was placed.
   */
  overwriteEdit(doc: SequenceDocument, src: SourceRange, recordInTicks: number, targets: EditTargets): EditResult {
    return threePointEdit(doc, src, recordInTicks, targets, 'overwrite', 'v1');
  },

  /**
   * Insert (,): as overwriteEdit, but what is at and after `recordInTicks` on the targeted tracks moves later by the
   * edit's length (a clip it lands inside is split there; V1 as insertV1 does, a transition at the point goes with its
   * neighbour). `ripple` 'v1': only the targeted tracks move; 'sync' (the Sync toggle): every other track moves from
   * recordInTicks too (shiftOthers), so everything stays in sync with the picture.
   */
  insertEdit(doc: SequenceDocument, src: SourceRange, recordInTicks: number, targets: EditTargets, opts: { ripple?: RippleMode } = {}): EditResult {
    return threePointEdit(doc, src, recordInTicks, targets, 'insert', opts.ripple ?? 'v1');
  },

  /**
   * Set or clear the sequence In and Out (I, O, ⌥X): a number sets the mark (snapped, at 0 or later), null clears it,
   * left out keeps it. When the new mark would cross the other one (In at or after Out), the other one is cleared.
   * The marks are left out when neither is set; the same document when nothing changes.
   */
  setMarks(doc: SequenceDocument, patch: { inTicks?: number | null; outTicks?: number | null }): SequenceDocument {
    const cur = doc.marks ?? {};
    const fix = (v: number | null | undefined, old: number | undefined) => (v === undefined ? old : v === null ? undefined : snap(Math.max(0, v), doc.frameRate));
    let inTicks = fix(patch.inTicks, cur.inTicks);
    let outTicks = fix(patch.outTicks, cur.outTicks);
    if (inTicks !== undefined && outTicks !== undefined && inTicks >= outTicks) {
      if (patch.inTicks !== undefined && patch.inTicks !== null) outTicks = undefined; else inTicks = undefined;
    }
    if (inTicks === cur.inTicks && outTicks === cur.outTicks) return doc;
    const next = { ...doc };
    const marks: SequenceMarks = {};
    if (inTicks !== undefined) marks.inTicks = inTicks;
    if (outTicks !== undefined) marks.outTicks = outTicks;
    if (marks.inTicks !== undefined || marks.outTicks !== undefined) next.marks = marks; else delete next.marks;
    return next;
  },

  /* ------------------------------------------------ 1.7.4: audio levels */

  /**
   * Add a volume key to a clip (V1, layer or audio; ⌘-click on the level line): at `key.atTicks` in source time
   * (snapped, clamped into the clip's sourceIn…sourceOut) with `key.db` (clamped to MIN_DB…MAX_DB, rounded to 0.1 dB).
   * A key at the same time is replaced. A clip with MAX_VOLUME_KEYS keys, or an unknown clip: unchanged. The keys are
   * an offset on top of gainDb (audio-levels.ts volumeAt): to add a key on the line where it is, pass
   * volumeAt(clip, t) − clip.gainDb.
   */
  addVolumeKey(doc: SequenceDocument, clipId: string, key: VolumeKey): SequenceDocument {
    return mapClip(doc, clipId, (c) => {
      const at = Math.min(c.sourceOutTicks, Math.max(c.sourceInTicks, snap(key.atTicks, doc.frameRate)));
      const keys = (c.volumeKeys ?? []).filter((k) => k.atTicks !== at);
      if (keys.length >= MAX_VOLUME_KEYS) return c;
      return withVolumeKeys(c, [...keys, { atTicks: at, db: tidyDb(key.db) }]);
    });
  },

  /**
   * Move a volume key (drag): a new time (source ticks, snapped; kept strictly between its neighbours' times and
   * inside the clip's source range) and/or a new level (clamped, 0.1 dB). An unknown clip or index: unchanged.
   * Coalesce key `vkey:<clipId>:<index>` for a drag.
   */
  moveVolumeKey(doc: SequenceDocument, clipId: string, index: number, patch: { atTicks?: number; db?: number }): SequenceDocument {
    const tpf = ticksPerFrame(doc.frameRate);
    return mapClip(doc, clipId, (c) => {
      const keys = c.volumeKeys ?? [];
      const k = keys[index];
      if (!k) return c;
      const lo = index > 0 ? keys[index - 1]!.atTicks + tpf : c.sourceInTicks;
      const hi = index < keys.length - 1 ? keys[index + 1]!.atTicks - tpf : c.sourceOutTicks;
      const at = patch.atTicks === undefined ? k.atTicks : Math.min(Math.max(lo, hi), Math.max(lo, snap(patch.atTicks, doc.frameRate)));
      const db = patch.db === undefined ? k.db : tidyDb(patch.db);
      if (at === k.atTicks && db === k.db) return c;
      return withVolumeKeys(c, keys.map((x, i) => (i === index ? { atTicks: at, db } : x)));
    });
  },

  /** Remove a volume key (Delete on a selected key); the list is left out when it empties. */
  removeVolumeKey(doc: SequenceDocument, clipId: string, index: number): SequenceDocument {
    return mapClip(doc, clipId, (c) => (c.volumeKeys?.[index] ? withVolumeKeys(c, c.volumeKeys.filter((_, i) => i !== index)) : c));
  },

  /**
   * Set a clip's level (dragging the whole level line, or the Inspector's gain): its gainDb (clamped, 0.1 dB). The
   * keys are an offset on top, so the whole line shifts with them. Coalesce key `level:<clipId>`.
   */
  setClipLevel(doc: SequenceDocument, clipId: string, db: number): SequenceDocument {
    return mapClip(doc, clipId, (c) => { const g = tidyDb(db); return g === c.gainDb ? c : { ...c, gainDb: g }; });
  },

  /**
   * Crossfade (⇧⌘D, the context menu) into the clip `ref` ('audio', or 'video' for its A1) from the clip before it:
   * `ticks` (snapped to whole frames, clamped to crossfadeLimits().maxTicks); 0 or less removes it. Setting one also
   * sets the incoming clip's sound fadeInTicks and the outgoing clip's fadeOutTicks to 0 (the crossfade is the fade).
   * Where crossfadeLimits has a problem (no touching clip before, a transition, unlinked sound, no handle), unchanged:
   * the editor shows the problem. Coalesce key `xfade:<id>` for a drag of its handle.
   *
   * 1.8.0: `align` sets crossfadeAlign too ('end' leaves it out) and the limits are that alignment's (crossfadeLimits,
   * with `durations` for the tail handle). Left out: an existing crossfade keeps its alignment; a new one is centred,
   * or where the media does not allow that gets the alignment that fits (bestCrossfadeAlign; the editor tells which),
   * when `durations` are given; without them (API callers) it ends at the cut, as in 1.7.4. Removing clears both.
   */
  setCrossfade(doc: SequenceDocument, ref: ItemRef, ticks: number, align?: CrossfadeAlign, durations?: SourceDurations): SequenceDocument {
    const set = (c: VideoClip | AudioClip, v: number | null, a: CrossfadeAlign = 'end'): AnyClip => {
      const keepAlign = v === null ? !('crossfadeAlign' in c) : crossfadeAlignOf(c) === a;
      if ((c.crossfadeInTicks ?? null) === v && keepAlign && (v === null || !c.fadeInTicks)) return c;
      const next = { ...c };
      if (v) { next.crossfadeInTicks = v; next.fadeInTicks = 0; } else delete next.crossfadeInTicks;
      if (v && a !== 'end') next.crossfadeAlign = a; else delete next.crossfadeAlign;
      return next;
    };
    const tpf = ticksPerFrame(doc.frameRate);
    const want = Math.floor(Math.max(0, ticks) / tpf + 1e-9) * tpf;
    if (want <= 0) return mapClip(doc, ref.id, (c) => ('crossfadeInTicks' in c || 'crossfadeAlign' in c ? set(c as VideoClip | AudioClip, null) : c));
    // 1.8.0: the alignment asked for; else the crossfade's own; else, for a new one, centred where the media allows
    // (bestCrossfadeAlign) when the source lengths are known, and at the cut (1.7.4) when they are not.
    const own = (doc.video.find((c) => c.id === ref.id) ?? allAudioTracks(doc).flatMap((t) => t.clips).find((c) => c.id === ref.id));
    const a = align ?? (own?.crossfadeInTicks ? crossfadeAlignOf(own) : durations ? bestCrossfadeAlign(doc, ref, want, durations) ?? 'center' : 'end');
    const lim = crossfadeLimits(doc, ref, { align: a, durations });
    if (lim.problem || !lim.cut) return doc;
    const v = Math.min(want, lim.maxTicks);
    const outId = lim.cut.outgoing.id;
    let next = mapClip(doc, ref.id, (c) => set(c as VideoClip | AudioClip, v, a));
    next = mapClip(next, outId, (c) => (c.fadeOutTicks ? { ...c, fadeOutTicks: 0 } : c));
    return next;
  },

  /**
   * A sound track's volume (dB; 'a1', 'a2', a layer id or an audio track id), clamped to MIN_DB…MAX_DB and rounded
   * to 0.1 dB; 0 leaves it out (and an entry with nothing set, and an empty map). Mute and solo stay.
   */
  setTrackGain(doc: SequenceDocument, track: string, db: number): SequenceDocument {
    const cur = doc.trackAudio?.[track]?.gainDb ?? 0;
    const next = tidyDb(db);
    if (next === cur) return doc;
    return ops.setTrackAudio(doc, track, { gainDb: next });
  },
};

// 1.8.0: every edit but setTransition clears the borrow records it made stale (tidyTransitionBorrows), whatever it
// returns the document in (the document itself, or { doc } of a paste, a three-point edit or a track move).
for (const name of Object.keys(ops) as Array<keyof typeof ops>) {
  if (name === 'setTransition') continue;
  const op = ops[name] as (doc: SequenceDocument, ...args: unknown[]) => unknown;
  (ops as Record<string, unknown>)[name] = (doc: SequenceDocument, ...args: unknown[]) => {
    const out = op(doc, ...args) as { video?: unknown; doc?: SequenceDocument } | null;
    if (!out || typeof out !== 'object' || !doc?.video) return out;
    if (Array.isArray(out.video)) return tidyTransitionBorrows(doc, out as SequenceDocument);
    if (out.doc && Array.isArray(out.doc.video)) { const d = tidyTransitionBorrows(doc, out.doc); return d === out.doc ? out : { ...out, doc: d }; }
    return out;
  };
}

/* ---------------------------------------------------------------- 1.7.4: shared types and helpers */

/**
 * What splitAt splits (1.7.4): 'v1' (the storyline clip under the point, as before), the items listed, or every item
 * on the tracks listed.
 */
export type SplitScope = 'v1' | { items: readonly ItemRef[] } | { tracks: readonly TrackRef[] };

/** What overwriteEdit / insertEdit return: the document and the items they placed (the editor selects them). */
export interface EditResult { doc: SequenceDocument; placed: ItemRef[] }

/** Where moveToVideoTrack puts the primary picture (1.7.4): V1, a layer (V2…), or a new video track above the top. */
export type VideoTrackTarget = 'v1' | { layer: string } | 'new';

/**
 * What moveToVideoTrack returns: the document, the moved items as they are now (a V1 clip moved up is a 'layer'
 * item with the same id; created sound clips are included as 'audio' items) and notes for one toast (each a
 * sentence; empty when there is nothing to say), e.g. "Multicam clips stay on V1", "There is no free audio track for
 * the sound: it stays in the clip", "Left on the track: opacity, size, position", "A sequence has at most nine video
 * tracks".
 */
export interface TrackMoveResult { doc: SequenceDocument; moved: ItemRef[]; notes: string[] }

/**
 * Select forward (A; 1.7.4): every item from `atTicks` to the end, the items whose end is after `atTicks` (so the one
 * under the point too; markers at or after it). With `track`, that track only ('v1': V1 clips; a layer, an audio track
 * or a graphics track); without, every track, cues and markers included. Gaps are never selected. In allItemRefs'
 * order. Link groups are not added: the editor expands with linkedItems (Option skips that), as for a click.
 */
export function selectForward(doc: SequenceDocument, atTicks: number, track?: TrackRef): ItemRef[] {
  const after = (span: Span | null, kind: ItemRefKind) => Boolean(span) && (kind === 'marker' ? span!.start >= atTicks : span!.end > atTicks);
  const onTrack = (r: ItemRef): boolean => {
    if (!track) return true;
    switch (track.kind) {
      case 'v1': return r.kind === 'video';
      case 'layer': return r.kind === 'layer' && Boolean(doc.layers?.find((l) => l.id === track.id)?.clips.some((c) => c.id === r.id));
      case 'audio': return r.kind === 'audio' && audioTrackOf(doc, r.id) === track.id;
      case 'graphic': return r.kind === 'graphic' && graphicTrackOf(doc.graphics.find((g) => g.id === r.id)!) === track.track;
    }
  };
  return allItemRefs(doc).filter((r) => onTrack(r) && after(itemSpan(doc, r), r.kind));
}

/**
 * The part of the sequence from span.start to span.end as a sequence of its own (1.7.4 "In to Out" for the export,
 * the EDL and Send to edit; three-point.ts markRange gives the span). Everything is moved span.start earlier; what
 * lies across an end is cut there (V1 with cutOut: the parts outside dropped, a gap at the start kept as
 * gapBeforeTicks; layer and audio clips trimmed in source time; graphics shortened, their step marks and updates
 * shifted and those outside dropped, updates before the start merged into the data; cues cut to the range); what lies
 * wholly outside is dropped, and so are markers outside. A crossfade or transition whose cut is at span.start is
 * dropped. `marks` is left out of the result. The same document for a span of the whole sequence.
 */
export function cropToRange(doc: SequenceDocument, span: Span): SequenceDocument {
  const rate = doc.frameRate;
  const a = snap(Math.max(0, span.start), rate);
  const b = snap(span.end, rate);
  if (a <= 0 && b >= sequenceDuration(doc)) return doc;
  if (b <= a) {
    const empty: SequenceDocument = { ...doc, video: [], audio: [], graphics: [], layers: (doc.layers ?? []).map((l) => ({ ...l, clips: [] })) };
    if (doc.subtitles) empty.subtitles = { ...doc.subtitles, cues: [] };
    if (doc.audioTracks) empty.audioTracks = doc.audioTracks.map((t) => ({ ...t, clips: [] }));
    delete empty.markers;
    delete empty.marks;
    return empty;
  }
  // V1: the part of each item inside [a, b), moved a earlier (a gap at the start stays as gapBeforeTicks).
  const items = toTimeline(doc);
  const inside = items.flatMap((it) => {
    if (it.end <= a || it.start >= b) return [];
    let parts = [it];
    if (it.start < a) parts = parts.flatMap((p) => cutOut(p, p.start, a, rate));
    if (it.end > b) parts = parts.flatMap((p) => cutOut(p, b, p.end, rate));
    return parts.map((p) => {
      // Its cut at the start is gone: no transition or crossfade from before the range.
      const clip = it.start <= a ? withoutCrossfade({ ...p.clip, transitionIn: null }) : p.clip;
      return itemAt(clip, p.start - a);
    });
  });
  let out = withItems(doc, inside, predecessors(items));
  // Free tracks: trimmed in source time.
  const crop = <T extends AudioClip | LayerClip>(clips: T[]): T[] => clips.flatMap((c) => {
    const s = c.recordInTicks, e = s + clipDuration(c);
    if (e <= a || s >= b) return [];
    let next: T = c;
    if (s < a) next = withoutCrossfade({ ...next, sourceInTicks: next.sourceInTicks + (a - s), recordInTicks: a });
    if (e > b) next = { ...next, sourceOutTicks: next.sourceOutTicks - (e - b) };
    if (s === a) next = withoutCrossfade(next);
    return [tidyFades({ ...next, recordInTicks: next.recordInTicks - a }, rate)];
  });
  out = { ...out, audio: crop(out.audio), layers: (out.layers ?? []).map((l) => ({ ...l, clips: crop(l.clips) })) };
  if (out.audioTracks) out.audioTracks = out.audioTracks.map((t) => ({ ...t, clips: crop(t.clips) }));
  out.graphics = out.graphics.flatMap((g) => {
    const s = g.recordInTicks, e = s + g.durationTicks;
    if (e <= a || s >= b) return [];
    let next = s < a ? graphicTail(g, a - s, rate) : g;
    if (e > b) next = tidyGraphic({ ...next, durationTicks: b - next.recordInTicks }, rate);
    return [{ ...next, recordInTicks: next.recordInTicks - a }];
  });
  if (out.subtitles) {
    out.subtitles = { ...out.subtitles, cues: out.subtitles.cues.filter((c) => c.endTicks > a && c.startTicks < b).map((c) => ({ ...c, startTicks: Math.max(a, c.startTicks) - a, endTicks: Math.min(b, c.endTicks) - a })) };
  }
  if (doc.markers) out = withMarkers(out, doc.markers.filter((m) => m.atTicks >= a && m.atTicks < b).map((m) => ({ ...m, atTicks: m.atTicks - a })));
  delete out.marks;
  return tidyLinks(out);
}

/**
 * overwriteEdit and insertEdit (1.7.4 three-point editing): the new clips on the targeted tracks, what is there cleared
 * (overwrite) or opened (insert), and with 'sync' every other track moved from the edit point.
 */
function threePointEdit(doc: SequenceDocument, src: SourceRange, recordInTicks: number, targets: EditTargets, mode: PlaceMode, ripple: RippleMode): EditResult {
  const rate = doc.frameRate;
  const none: EditResult = { doc, placed: [] };
  const sIn = snap(src.sourceInTicks, rate);
  const sOut = snap(src.sourceOutTicks, rate);
  const L = sOut - sIn;
  if (L <= 0) return none;
  const video = targets.video;
  const layer = video && video !== 'v1' ? (doc.layers ?? []).find((l) => l.id === video.layer) : undefined;
  if (video && video !== 'v1' && !layer) return none;
  let track = targets.audio && targets.audio !== 'with-picture' ? targets.audio.track : null;
  if (track && !audioClipsOn(doc, track)) return none;
  if (!video && !track) return none;
  // 1.8.0: on a video track, sound "with the picture" is that track's audio track (V2 → A2, V3 → A3; made up to it,
  // up to A8), so the clip's sound is a linked audio clip there and the clip is muted. A silent source has none; with
  // no such track possible the sound stays in the clip.
  if (layer && targets.audio === 'with-picture' && src.hasAudio !== false) {
    const want = Math.min((doc.layers ?? []).indexOf(layer), MAX_AUDIO_TRACKS);
    let dd = doc;
    while (allAudioTracks(dd).length <= want) { const n = ops.addAudioTrack(dd); if (n === dd) break; dd = n; }
    const t = allAudioTracks(dd)[want];
    if (t) { doc = dd; track = t.id; targets = { ...targets, audio: { track: t.id } }; }
  }
  let R = snap(Math.max(0, recordInTicks), rate);
  // An insert inside a transition's overlap on V1 is the cut there (as insertV1 puts it), for every targeted track.
  if (mode === 'insert' && video === 'v1') {
    const sorted = sortItems(toTimeline(doc));
    for (let k = 1; k < sorted.length; k++) if (R >= sorted[k]!.start && R < sorted[k - 1]!.end) R = sorted[k - 1]!.end;
  }
  const base = {
    assetId: src.assetId, versionId: src.versionId, label: src.label, sourceInTicks: sIn, sourceOutTicks: sOut,
    gainDb: 0, muted: Boolean(video) && targets.audio !== 'with-picture', fadeInTicks: 0, fadeOutTicks: 0,
  };
  const fill = defaultFit(src, doc) === 'fill';
  const linkId = video && track ? newId('lk') : undefined;
  const freeOp = <T extends AudioClip | LayerClip>(clips: T[], prefix: string): T[] => (mode === 'insert' ? openFree(clips, R, L, prefix, rate) : clearFree(clips, R, R + L, prefix, rate));
  let d = doc;
  const placed: ItemRef[] = [];
  if (layer) d = withLayerClips(d, layer.id, freeOp(layer.clips, 'p'));
  if (track) d = withAudioClips(d, track, freeOp(audioClipsOn(d, track)!, 'a'));
  if (video === 'v1') {
    const clip: VideoClip = { id: newId('v'), ...base, transitionIn: null, multicam: null };
    if (fill) clip.fit = 'fill';
    if (linkId) clip.linkId = linkId;
    if (mode === 'overwrite') {
      const items = toTimeline(d);
      d = overwriteV1(d, items, [itemAt(clip, R)], predecessors(items));
    } else d = insertV1(d, [{ clip, offset: 0 }], R, L, 'v1', new Set(), new Map());
    placed.push({ kind: 'video', id: clip.id });
  } else if (layer) {
    const clip: LayerClip = { id: newId('p'), ...base, recordInTicks: R, opacity: 1, scale: 1, x: 0, y: 0 };
    if (fill) clip.fit = 'fill';
    if (linkId) clip.linkId = linkId;
    d = withLayerClips(d, layer.id, [...d.layers.find((l) => l.id === layer.id)!.clips, clip]);
    placed.push({ kind: 'layer', id: clip.id });
  }
  if (track) {
    const sound: AudioClip = { id: newId('a'), ...base, muted: false, recordInTicks: R };
    if (linkId) sound.linkId = linkId;
    d = withAudioClips(d, track, [...audioClipsOn(d, track)!, sound]);
    placed.push({ kind: 'audio', id: sound.id });
  }
  if (mode === 'insert' && ripple === 'sync') {
    const skip = new Set(placed.flatMap((r) => [...ownTrackKeys(d, r)]));
    d = shiftOthers(d, R, L, skip);
    if (video !== 'v1') d = shiftV1From(d, R, L);
  }
  return { doc: tidyAudioCrossfades(doc, d), placed };
}

/** A V1, layer or audio clip by id, changed by `f` (the same document when f returns it unchanged or no clip has the id). */
type AnyClip = VideoClip | LayerClip | AudioClip;
function mapClip(doc: SequenceDocument, id: string, f: (c: AnyClip) => AnyClip): SequenceDocument {
  // f keeps the clip's kind (it only patches fields every clip kind has, or crossfadeInTicks on V1 and audio clips).
  const fix = <T extends AnyClip>(xs: T[]): T[] => (xs.some((c) => c.id === id) ? mapSame(xs, (c) => (c.id === id ? (f(c) as T) : c)) : xs);
  const next: SequenceDocument = { ...doc, video: fix(doc.video), audio: fix(doc.audio) };
  if (doc.layers) next.layers = mapSame(doc.layers, (l) => { const clips = fix(l.clips); return clips === l.clips ? l : { ...l, clips }; });
  if (doc.audioTracks) next.audioTracks = mapSame(doc.audioTracks, (t) => { const clips = fix(t.clips); return clips === t.clips ? t : { ...t, clips }; });
  return orSame(doc, next);
}

/** A level as kept: clamped to MIN_DB…MAX_DB, 0.1 dB steps (0 for anything not finite). */
const tidyDb = (db: number) => (Number.isFinite(db) ? Math.round(Math.min(MAX_DB, Math.max(MIN_DB, db)) * 10) / 10 : 0);

/** A clip with these volume keys, sorted by time; left out when none. */
function withVolumeKeys<T extends VideoClip | LayerClip | AudioClip>(c: T, keys: VolumeKey[]): T {
  const next = { ...c };
  const sorted = [...keys].sort((a, b) => a.atTicks - b.atTicks).slice(0, MAX_VOLUME_KEYS);
  if (sorted.length) next.volumeKeys = sorted; else delete next.volumeKeys;
  return next;
}

/* ---------------------------------------------------------------- 1.7.4: stream A internals */

/** A clip without crossfadeInTicks (1.8.0: or crossfadeAlign; the same object when it has neither). */
function withoutCrossfade<T extends object>(c: T): T {
  if (!('crossfadeInTicks' in c) && !('crossfadeAlign' in c)) return c;
  const next = { ...c } as T & { crossfadeInTicks?: number; crossfadeAlign?: string };
  delete next.crossfadeInTicks; delete next.crossfadeAlign;
  return next;
}

/* ---------------------------------------------------------------- 1.8.0: the sound of clips on video tracks (stream C) */

/** The note when a video-track clip's sound finds no audio track (moveToVideoTrack, moveSoundToTrack). */
export const NO_AUDIO_TRACK = 'There is no free audio track for the sound: it stays in the clip';

/**
 * A layer clip's sound put on an audio track (1.7.4's V1 → V2 rule, shared in 1.8.0): as many audio tracks down as the
 * layer is above V1 (V2 → A2, V3 → A3), else the next free one below, else a new one (addAudioTrack, up to A8), as an
 * audio clip (newId('a')) with `from`'s source range, gainDb, muted, sound fades and volume keys (default: the layer
 * clip's own) at the clip's record time, linked to it (its linkId, or a new one); the layer clip is muted. With no room
 * anywhere: `sound` null and the document unchanged.
 */
function soundToAudioTrack(doc: SequenceDocument, layerId: string, clipId: string, from?: VideoClip | LayerClip): { doc: SequenceDocument; sound: ItemRef | null } {
  const li = (doc.layers ?? []).findIndex((l) => l.id === layerId);
  const lc = doc.layers?.[li]?.clips.find((c) => c.id === clipId);
  if (!lc) return { doc, sound: null };
  const v = from ?? lc;
  const span = { start: lc.recordInTicks, end: lc.recordInTicks + clipDuration(lc) };
  const wanted = Math.min(li, MAX_AUDIO_TRACKS);
  let out = doc;
  let tracks = allAudioTracks(out);
  let idx = -1;
  for (let i = wanted; i < tracks.length; i++) if (freeOver(spans(tracks[i]!.clips), [span])) { idx = i; break; }
  while (idx < 0) {
    const n = ops.addAudioTrack(out);
    if (n === out) break;
    out = n;
    tracks = allAudioTracks(out);
    if (tracks.length - 1 >= wanted) idx = tracks.length - 1;
  }
  if (idx < 0) return { doc, sound: null };
  const linkId = v.linkId ?? lc.linkId ?? newId('lk');
  const sound: AudioClip = {
    id: newId('a'), assetId: v.assetId, versionId: v.versionId, label: v.label, sourceInTicks: v.sourceInTicks, sourceOutTicks: v.sourceOutTicks,
    recordInTicks: span.start, gainDb: v.gainDb, muted: v.muted, fadeInTicks: v.fadeInTicks, fadeOutTicks: v.fadeOutTicks, linkId,
  };
  if (v.volumeKeys?.length) sound.volumeKeys = structuredClone(v.volumeKeys);
  const t = tracks[idx]!;
  out = withAudioClips(out, t.id, [...t.clips, sound]);
  out = withLayerClips(out, layerId, out.layers.find((l) => l.id === layerId)!.clips.map((c) => (c.id === clipId ? { ...c, muted: true, linkId } : c)));
  return { doc: out, sound: { kind: 'audio', id: sound.id } };
}

/* ---------------------------------------------------------------- 1.8.0: transitions that borrow media (stream C) */

/** A still's source window moved `by` ticks later (setTransition): its In and Out and every key with it, so nothing shows differently. */
function shiftSource(c: VideoClip, by: number): VideoClip {
  const next: VideoClip = { ...c, sourceInTicks: c.sourceInTicks + by, sourceOutTicks: c.sourceOutTicks + by };
  if (c.keys) next.keys = shiftFramingKeys(c.keys, by);
  if (c.volumeKeys) next.volumeKeys = c.volumeKeys.map((k) => ({ ...k, atTicks: k.atTicks + by }));
  if (c.formatFraming) next.formatFraming = Object.fromEntries(Object.entries(c.formatFraming).map(([k, f]) => [k, f.keys ? { ...f, keys: shiftFramingKeys(f.keys, by) } : f]));
  return next;
}

/** What a transition borrowed (1.8.0): its length, the head and tail it took from the clips' media, and what still overlaps. */
export interface TransitionBorrow { durationTicks: number; headTicks: number; tailTicks: number; overlapTicks: number; recorded: boolean }

/** The borrow of the transition into storyline clip `id` as layoutVideo applies it (null when there is none). */
export function transitionBorrow(doc: SequenceDocument, id: string): TransitionBorrow | null {
  const p = layoutVideo(doc).find((x) => x.clip.id === id);
  const t = p?.clip.transitionIn;
  if (!p?.transition || !t) return null;
  const head = Math.min(t.headTicks ?? 0, p.transition.ticks);
  const tail = Math.min(t.tailTicks ?? 0, p.transition.ticks - head);
  return { durationTicks: p.transition.ticks, headTicks: head, tailTicks: tail, overlapTicks: p.transition.ticks - head - tail, recorded: Boolean(t.headTicks || t.tailTicks) };
}

/**
 * Borrow records an edit made stale (1.8.0): a transition keeps headTicks/tailTicks only while the incoming clip's In
 * and the clip before it (its source and Out) are as they were. A trim, roll, slip or slide at that cut, or a move of
 * either clip, clears the record: the frames then belong to the edit, and removing the transition removes only the
 * transition. New clips (a paste) do not keep one. `after` itself when nothing changes.
 */
function tidyTransitionBorrows(before: SequenceDocument, after: SequenceDocument): SequenceDocument {
  if (before === after || !after.video.some((c) => c.transitionIn?.headTicks || c.transitionIn?.tailTicks)) return after;
  const was = new Map(before.video.map((c, i) => [c.id, { inTicks: c.sourceInTicks, prev: before.video[i - 1] }]));
  let changed = false;
  const video = after.video.map((c, i) => {
    const t = c.transitionIn;
    if (!t || (!t.headTicks && !t.tailTicks)) return c;
    const w = was.get(c.id);
    const prev = after.video[i - 1];
    if (w?.prev && prev && w.inTicks === c.sourceInTicks && w.prev.versionId === prev.versionId && w.prev.sourceOutTicks === prev.sourceOutTicks
      && w.prev.multicam?.angle === prev.multicam?.angle) return c;
    changed = true;
    const { headTicks: _h, tailTicks: _t, ...rest } = t;
    void _h; void _t;
    return { ...c, transitionIn: rest };
  });
  return changed ? { ...after, video } : after;
}

/** The id of the audio clip ending exactly where `c` starts in `clips` (its crossfade partner), or null. */
const touchingBefore = (clips: readonly AudioClip[], c: AudioClip): string | null =>
  clips.find((x) => x.id !== c.id && clipDuration(x) > 0 && x.recordInTicks + clipDuration(x) === c.recordInTicks)?.id ?? null;

/**
 * Audio crossfades whose cut changed (1.7.4): an audio clip that existed in `before` with crossfadeInTicks loses it
 * when another clip now touches it before (not the one that did, or on another track), so a dormant crossfade never
 * wakes against a different clip; with nothing touching it, it stays dormant. New clips (a paste) keep theirs.
 * `after` itself when nothing changes.
 */
function tidyAudioCrossfades(before: SequenceDocument, after: SequenceDocument): SequenceDocument {
  if (before === after || !allAudioTracks(after).some((t) => t.clips.some((c) => c.crossfadeInTicks))) return after;
  const was = new Map<string, { track: string; pred: string | null }>();
  for (const t of allAudioTracks(before)) for (const c of t.clips) if (c.crossfadeInTicks) was.set(c.id, { track: t.id, pred: touchingBefore(t.clips, c) });
  let out = after;
  for (const t of allAudioTracks(after)) {
    const stale = new Set(t.clips.filter((c) => {
      const w = c.crossfadeInTicks ? was.get(c.id) : undefined;
      const now = touchingBefore(t.clips, c);
      return Boolean(w) && now !== null && (w!.track !== t.id || w!.pred !== now);
    }).map((c) => c.id));
    if (stale.size) out = withAudioClips(out, t.id, t.clips.map((c) => (stale.has(c.id) ? withoutCrossfade(c) : c)));
  }
  return out;
}

/**
 * The part of a graphic from `cut` ticks after its start (1.7.4: split, roll, ripple trim, crop): it starts `cut`
 * later and is that much shorter; step marks and updates keep their place against its content (shifted by −cut);
 * updates at or before the cut are merged into its data, so it shows what was showing. A negative cut extends it
 * earlier (everything shifts later). Tidied (tidyGraphic).
 */
function graphicTail(g: GraphicInstance, cut: number, rate: Rational): GraphicInstance {
  const next: GraphicInstance = { ...g, recordInTicks: g.recordInTicks + cut, durationTicks: g.durationTicks - cut };
  if (g.stepMarks) next.stepMarks = g.stepMarks.map((t) => t - cut).filter((t) => t > 0);
  if (g.updates) {
    const before = cut > 0 ? g.updates.filter((u) => u.atTicks <= cut) : [];
    if (before.length) next.data = Object.assign({}, g.data, ...before.map((u) => u.data));
    next.updates = g.updates.filter((u) => !before.includes(u)).map((u) => ({ ...u, atTicks: u.atTicks - cut }));
  }
  return tidyGraphic(next, rate);
}

/**
 * The parts of a clip on a freely placed track (a layer, an audio track) outside [s, e): itself, its head (the same
 * id, trimmed at its out point), its tail (trimmed at its in point; a new id with `prefix` when the head is kept too)
 * or both; s = e splits it. The fades at a cut are cleared, as cutOut's; a tail loses its crossfade.
 */
function cutFree<T extends AudioClip | LayerClip>(c: T, s: number, e: number, prefix: string, rate: Rational): T[] {
  const start = c.recordInTicks;
  const end = start + clipDuration(c);
  if (e <= start || s >= end || (s === e && (s === start || s === end))) return [c];
  const head = s > start;
  const tail = e < end;
  const out: T[] = [];
  if (head) {
    const left = { ...c, sourceOutTicks: c.sourceOutTicks - (end - s) };
    if (tail) { left.fadeOutTicks = 0; delete left.videoFadeOutTicks; }
    out.push(tidyFades(left, rate));
  }
  if (tail) {
    const right = head ? { ...structuredClone(c), id: newId(prefix), fadeInTicks: 0 } : { ...c };
    if (head) delete right.videoFadeInTicks;
    delete (right as { crossfadeInTicks?: number }).crossfadeInTicks; delete (right as { crossfadeAlign?: string }).crossfadeAlign;
    right.sourceInTicks = c.sourceInTicks + (e - start);
    right.recordInTicks = e;
    out.push(tidyFades(right, rate));
  }
  return out;
}

/** Clips on a free track with [s, e) cleared (an overwrite), each cut by cutFree; the same array when none is touched. */
function clearFree<T extends AudioClip | LayerClip>(clips: T[], s: number, e: number, prefix: string, rate: Rational): T[] {
  const out = clips.flatMap((c) => cutFree(c, s, e, prefix, rate));
  return out.length === clips.length && out.every((c, i) => c === clips[i]) ? clips : out;
}

/** Clips on a free track opened at T by `len` (an insert): a clip T falls inside is split there, and what starts at or after T moves later. */
function openFree<T extends AudioClip | LayerClip>(clips: T[], T: number, len: number, prefix: string, rate: Rational): T[] {
  if (len <= 0) return clips;
  return clearFree(clips, T, T, prefix, rate).map((c) => (c.recordInTicks >= T ? { ...c, recordInTicks: c.recordInTicks + len } : c));
}

/** The document with a layer's clips replaced. */
const withLayerClips = (doc: SequenceDocument, layerId: string, clips: LayerClip[]): SequenceDocument =>
  ({ ...doc, layers: (doc.layers ?? []).map((l) => (l.id === layerId ? { ...l, clips } : l)) });

/** The V1 clips starting at or after P moved by `delta` (the sync rule for V1 when the edit is on another track). */
function shiftV1From(doc: SequenceDocument, P: number, delta: number): SequenceDocument {
  if (!delta) return doc;
  const items = toTimeline(doc);
  if (!items.some((it) => it.start >= P)) return doc;
  return withItems(doc, items.map((it) => (it.start >= P ? itemAt(it.clip, Math.max(0, it.start + delta)) : it)), predecessors(items));
}

/** How far the V1 clips starting at or after P can move earlier together (the sync rule's room on V1). */
function v1Room(doc: SequenceDocument, P: number): number {
  const items = toTimeline(doc);
  const moving = items.filter((it) => it.start >= P);
  if (!moving.length) return Number.POSITIVE_INFINITY;
  const first = Math.min(...moving.map((it) => it.start));
  const before = items.filter((it) => it.start < P);
  if (before.some((it) => it.end > first)) return 0;
  return first - Math.max(0, ...before.map((it) => it.end));
}

/** The item keys of every item on the track holding `ref` (the sync rule leaves its own track to the op). */
function ownTrackKeys(doc: SequenceDocument, ref: ItemRef): Set<string> {
  const keys = (kind: ItemRefKind, xs: Array<{ id: string }>) => new Set(xs.map((x) => itemKey({ kind, id: x.id })));
  switch (ref.kind) {
    case 'audio': { const t = audioTrackOf(doc, ref.id); return keys('audio', t ? audioClipsOn(doc, t)! : []); }
    case 'layer': return keys('layer', (doc.layers ?? []).find((l) => l.clips.some((c) => c.id === ref.id))?.clips ?? []);
    case 'graphic': { const g = doc.graphics.find((x) => x.id === ref.id); return keys('graphic', g ? doc.graphics.filter((x) => graphicTrackOf(x) === graphicTrackOf(g)) : []); }
    case 'cue': return keys('cue', doc.subtitles?.cues ?? []);
    default: return new Set();
  }
}

/** A storyline clip as a layer clip (moveToVideoTrack, V1 → a video track): the storyline-only fields go, Blur becomes Fill. */
function toLayerClip(v: VideoClip, recordInTicks: number): LayerClip {
  const { transitionIn: _t, multicam: _m, gapBeforeTicks: _g, crossfadeInTicks: _x, crossfadeAlign: _xa, soundDetached: _s, formatFraming, ...rest } = v;
  void _t; void _m; void _g; void _x; void _xa; void _s;
  const c = { ...rest, recordInTicks, opacity: 1, scale: 1, x: 0, y: 0 } as LayerClip;
  if (v.fit === 'blur') c.fit = 'fill';
  if (formatFraming) c.formatFraming = Object.fromEntries(Object.entries(formatFraming).map(([k, f]) => [k, f.fit === 'blur' ? { ...f, fit: 'fill' as const } : f])) as LayerClip['formatFraming'];
  return c;
}

/** A layer clip as a storyline clip (moveToVideoTrack, a video track → V1), and what was set of what it leaves behind (the note). */
function toVideoClip(lc: LayerClip): { clip: VideoClip; left: string[] } {
  const { recordInTicks: _r, opacity, scale, x, y, motion, hideIn, formatFraming, ...rest } = lc;
  void _r;
  const clip = { ...rest, transitionIn: null, multicam: null } as VideoClip;
  const left: string[] = [];
  const ff = Object.values(formatFraming ?? {});
  if (opacity !== 1) left.push('opacity');
  if (scale !== 1 || ff.some((f) => f.place?.scale !== undefined)) left.push('size');
  if (x !== 0 || y !== 0 || ff.some((f) => f.place?.x !== undefined || f.place?.y !== undefined)) left.push('position');
  if (motion?.keys.length || ff.some((f) => f.motion?.keys.length)) left.push('motion');
  if (hideIn?.length) left.push('hidden formats');
  if (formatFraming) {
    const kept = Object.entries(formatFraming).flatMap(([k, f]) => {
      const { place: _p, motion: _mo, ...framing } = f;
      void _p; void _mo;
      return Object.keys(framing).length ? [[k, framing] as const] : [];
    });
    if (kept.length) clip.formatFraming = Object.fromEntries(kept) as VideoClip['formatFraming'];
  }
  return { clip, left };
}

/* ---------------------------------------------------------------- validation for export */

export interface SequenceProblem {
  severity: 'error' | 'warning';
  message: string;
  clipId?: string;
}

export function checkSequence(doc: SequenceDocument): SequenceProblem[] {
  const problems: SequenceProblem[] = [];
  if (!doc.video.length) problems.push({ severity: 'error', message: 'Add at least one clip to the storyline' });
  const tpf = ticksPerFrame(doc.frameRate);
  if (!Number.isInteger(tpf)) problems.push({ severity: 'warning', message: 'Non-standard frame rate: positions are rounded to the nearest tick' });
  for (const { clip: c } of allClips(doc)) {
    if (clipDuration(c) <= 0) problems.push({ severity: 'error', message: `“${c.label}” has no duration`, clipId: c.id });
  }
  // Clips on A2 or a layer that overlap (0.96.0 edits never make them; older documents or the API can have them).
  const label = (id: string) => allClips(doc).find((c) => c.clip.id === id)?.clip.label ?? id;
  // 1.7.3: every audio track (A2–A8), and every graphics track (GFX 1–3).
  for (const t of allAudioTracks(doc)) {
    for (const [a, b] of overlappingClips(t.clips)) problems.push({ severity: 'warning', message: `“${label(a)}” and “${label(b)}” overlap on A${t.number}: both are heard`, clipId: b });
  }
  for (let track = 0; track < MAX_GRAPHIC_TRACKS; track++) {
    const on = doc.graphics.filter((g) => graphicTrackOf(g) === track).map((g) => ({ id: g.id, recordInTicks: g.recordInTicks, sourceInTicks: 0, sourceOutTicks: g.durationTicks }));
    for (const [, b] of overlappingClips(on)) problems.push({ severity: 'warning', message: `Two graphics overlap on GFX ${track + 1}: the later one is drawn on top`, clipId: b });
  }
  (doc.layers ?? []).forEach((l, k) => {
    for (const [a, b] of overlappingClips(l.clips)) problems.push({ severity: 'warning', message: `“${label(a)}” and “${label(b)}” overlap on V${k + 2}: the later one is drawn on top`, clipId: b });
  });
  const total = sequenceDuration(doc);
  // 1.7.4: marks never move with edits, so an Out can end up past the end (In to Out then stops at the end).
  if (doc.marks?.outTicks !== undefined && doc.marks.outTicks > total) problems.push({ severity: 'warning', message: 'The sequence Out is after the end' });
  if (doc.marks?.inTicks !== undefined && doc.marks.inTicks >= total && doc.video.length) problems.push({ severity: 'warning', message: 'The sequence In is at or after the end' });
  if (total > TICKS_PER_SECOND * 60 * 30) problems.push({ severity: 'warning', message: 'Sequences longer than 30 minutes export slowly in this release' });
  return problems;
}

/**
 * Step marks and updates of a graphic kept valid after an edit: snapped to the frame grid, inside the clip, step
 * marks unique and ascending, updates by time. An empty list is left out, so documents that never had one stay as
 * they were (byte for byte when saved again).
 */
function tidyGraphic(g: GraphicInstance, rate: SequenceDocument['frameRate']): GraphicInstance {
  const out: GraphicInstance = { ...g };
  if ('stepMarks' in out) {
    const marks = [...new Set((out.stepMarks ?? []).map((t) => snap(Math.max(0, t), rate)))].filter((t) => t > 0 && t < out.durationTicks).sort((a, b) => a - b);
    if (marks.length) out.stepMarks = marks; else delete out.stepMarks;
  }
  if ('updates' in out) {
    const updates = (out.updates ?? []).map((u) => ({ ...u, atTicks: snap(Math.max(0, u.atTicks), rate) })).filter((u) => u.atTicks < out.durationTicks).sort((a, b) => a.atTicks - b.atTicks);
    if (updates.length) out.updates = updates; else delete out.updates;
  }
  return out;
}
